import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import versionService from '../services/versionService';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { useAuth, readJwtPayload } from '../contexts/AuthContext';
import api, { membersAPI, dashboardAPI, teamsAPI, divisionsAPI, matchesAPI, playerAPI } from '../services/api';
import PlayerHomeBannerStack from '../components/PlayerHomeBannerStack';
import { computeAvailabilityReminder } from '../utils/playerAvailabilityReminder';
import leaderboardAPI from '../services/leaderboardApi';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import ScrollMemoryWrapper from '../components/ScrollMemoryWrapper';
import MatchChatModal from '../components/MatchChatModal';
import InvitePlayerModal from '../components/playerInvites/InvitePlayerModal';
import MatchScheduleModal from '../components/MatchScheduleModal';
import MatchScheduleButton from '../components/MatchScheduleButton';
import MatchChatButton from '../components/MatchChatButton';
import MatchScoreButton from '../components/MatchScoreButton';
import MatchScoreModal from '../components/MatchScoreModal';
import TeamLogoModal from '../components/TeamLogoModal';
import MatchDetailsModal from '../components/MatchDetailsModal';
import HomeAwayInfoModal from '../components/HomeAwayInfoModal';
import DisputeScoreModal from '../components/DisputeScoreModal';
import CourtBooking from '../components/CourtBooking';
import matchChatAPI from '../services/matchChatApi';
import { syncPlayerNotificationsAfterMatchChat } from '../utils/playerNotificationQueries';
import matchSchedulingAPI from '../services/matchSchedulingApi';
import scoreAPI from '../services/scoreApi';
import PhoneNumberModal from '../components/PhoneNumberModal';
import TeamMemberInfoModal from '../components/TeamMemberInfoModal';
import OpponentTeamSheetModal from '../components/OpponentTeamSheetModal';
import ResultsLocationModal from '../components/ResultsLocationModal';
import { publicAssetUrl } from '../utils/publicAssetUrl';
import { usePhoneNumberStatus } from '../hooks/usePhoneNumberStatus';
import { getLocalStorageItem } from '../utils/safeStorage';
import { getPromotionRelegationIconsForLeaderboardRow } from '../utils/promotionRelegationEmojis';
import { isForfeitTeam } from '../utils/promotionRelegationSuggestions';
import PasswordInput from '../components/PasswordInput';

const DESKTOP_RESULTS_PER_PAGE = 20;
const MOBILE_RESULTS_PER_PAGE = 15;

/** YYYY-MM-DD in Zurich for a season boundary (DB stores UTC instants; avoid raw ISO date slice). */
function toSwissYmd(value) {
  if (value == null) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    const m = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : null;
  }
  return d.toLocaleDateString('en-CA', { timeZone: 'Europe/Zurich' });
}

/** Calendar-inclusive season window in Europe/Zurich (matches server is_active_by_date). */
function isSeasonPlayWindowSwiss(season, now = new Date()) {
  if (!season?.start_date || !season?.end_date) return false;
  const swissDay = now.toLocaleDateString('en-CA', { timeZone: 'Europe/Zurich' });
  const start = toSwissYmd(season.start_date);
  const end = toSwissYmd(season.end_date);
  if (!start || !end) return false;
  return swissDay >= start && swissDay <= end;
}

function isActiveByDateFlag(season) {
  if (!season || season.is_active_by_date == null) return false;
  const v = season.is_active_by_date;
  return v === true || v === 'true' || v === 't' || v === 1;
}

/** Completed / active / calendar-in-window / past kickoff show standings. */
function seasonAllowsStandingsUI(season, now = new Date()) {
  if (!season) return false;
  if (season.status === 'completed') return true;
  if (season.status === 'active') return true;
  if (isActiveByDateFlag(season)) return true;
  if (isSeasonPlayWindowSwiss(season, now)) return true;
  // DB often stays "registration" after go-live — once wall-clock is past start instant, show rankings
  if (season.start_date && ['registration', 'upcoming'].includes(season.status)) {
    const startMs = new Date(season.start_date).getTime();
    if (!Number.isNaN(startMs) && now.getTime() >= startMs) return true;
  }
  return false;
}

const DashboardContainer = styled.div`
  min-height: ${(p) => (p.$embedded ? 'auto' : '100vh')};
  width: ${(p) => (p.$embedded ? '100%' : 'auto')};
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  display: flex;
  position: relative;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    overflow-x: hidden;
  }
  
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(4, 54, 99, 0.02) 0%, transparent 50%, rgba(213, 43, 30, 0.02) 100%);
    pointer-events: none;
    z-index: 0;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SeasonEndedBanner = styled.div`
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 2px solid #f59e0b;
  border-radius: 12px;
  padding: 1.5rem;
  margin: 1.5rem 2rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.2);
  
  @media (max-width: 768px) {
    margin: 1rem;
    padding: 1rem;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
`;

const SeasonEndedIcon = styled.div`
  font-size: 2.5rem;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SeasonEndedContent = styled.div`
  flex: 1;
`;

const SeasonEndedTitle = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: #92400e;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const SeasonEndedMessage = styled.div`
  font-size: 1rem;
  color: #78350f;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const Sidebar = styled.aside`
  width: 250px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border-right: 2px solid #043663;
  padding: 2rem 0;
  position: fixed;
  height: 100vh;
  overflow-y: auto;
  z-index: 1;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const SidebarHeader = styled.div`
  padding: 0 2rem 2rem 2rem;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 2rem;
`;

const TeamLogoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const TeamLogoContainer = styled.div`
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #043663 0%, #043663 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 3px solid #e5e7eb;
  
  &:hover {
    transform: scale(1.05);
    border-color: #043663;
  }
`;

const TeamLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const TeamLogoPlaceholder = styled.div`
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  text-transform: uppercase;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
`;

const PlusIcon = styled.div`
  font-size: 1rem;
  opacity: 0.8;
`;

/** Single hidden file input in main content (not inside display:none sidebar) so mobile browsers accept programmatic .click() for gallery upload */
const VisuallyHiddenFileInput = styled.input.attrs({ type: 'file' })`
  position: fixed;
  left: 0;
  top: 0;
  width: 0.01px;
  height: 0.01px;
  opacity: 0;
  overflow: hidden;
  z-index: -1;
`;

const LogoUploadModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const LogoUploadModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const LogoUploadModalTitle = styled.h3`
  margin: 0;
  color: #1f2937;
  font-size: 1.25rem;
  font-weight: 600;
`;

const LogoUploadModalButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.variant === 'primary' ? '#043663' : '#f3f4f6'};
  color: ${props => props.variant === 'primary' ? 'white' : '#1f2937'};
  
  &:hover {
    background: ${props => props.variant === 'primary' ? '#032a4f' : '#e5e7eb'};
  }
`;

const LogoUploadModalClose = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: #f3f4f6;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  color: #6b7280;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e5e7eb;
    color: #1f2937;
  }
`;

const AppTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  flex: 1;
  text-align: center;
`;

const MobileTeamHeader = styled.div`
  display: none;
  align-items: center;
  gap: 1rem;
  padding-bottom: 1rem;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileTeamName = styled.h1`
  font-size: 1.2rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  flex: 1;
  text-align: left;
  line-height: 1.3;
  word-break: break-word;
`;

const Navigation = styled.nav`
  padding: 0 1rem;
`;

const NavItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: none;
  background: ${props => props.$active ? '#f3f4f6' : 'transparent'};
  color: ${props => props.$active ? '#043663' : '#6b7280'};
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 0.5rem;
  text-align: left;
  
  &:hover {
    background: #f3f4f6;
    color: #043663;
  }
`;

const NavIcon = styled.span`
  font-size: 1.1rem;
`;

const LogoutButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: none;
  background: transparent;
  color: #6b7280;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 2rem;
  text-align: left;
  
  &:hover {
    background: #fef2f2;
    color: #dc2626;
  }
`;

/** Embedded player tournaments: back to team/campaign flow (sidebar — hidden on narrow view; see TopBar mobile back) */
const EmbeddedBackSidebar = styled.button`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  width: calc(100% - 2rem);
  margin: 0 1rem 1rem 1rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  color: #0f172a;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  &:hover {
    background: #e2e8f0;
  }
  @media (max-width: 768px) {
    display: none;
  }
`;

const EmbeddedBackMobile = styled.button`
  display: none;
  align-items: center;
  gap: 0.35rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  color: #0f172a;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #e2e8f0;
  }
  @media (max-width: 768px) {
    display: flex;
  }
`;

const SidebarFooter = styled.div`
  margin-top: auto;
  padding: 1rem;
`;

const VersionInfo = styled.div`
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
`;

const UpdateButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #dc2626;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 0.75rem;
  text-align: center;
  
  &:hover {
    background: #fee2e2;
    color: #991b1b;
  }
`;

const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const SettingsSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SettingsSectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SettingsForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FormLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`;

const FormInput = styled.input`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #1f2937;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const FormButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #043663;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;
  
  &:hover {
    background: #052d4f;
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const HelpText = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: ${(p) => (p.$compactEmbedMain ? 0 : '250px')};
  width: ${(p) => (p.$compactEmbedMain ? '100%' : 'auto')};
  padding: 1.5rem;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    padding: 0.75rem;
    padding-bottom: 120px; /* Space for mobile action bar */
    overflow-x: hidden;
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const AppLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LogoIcon = styled.img`
  height: 36px;
  width: 36px;
  border-radius: 8px;
  object-fit: contain;
`;

const LogoText = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
  color: #1f2937;
`;

const TopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const TopBarBtn = styled.button`
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-size: 0.875rem;
  border: 1px solid #e5e7eb;
  background: white;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f9fafb;
    color: #043663;
  }
  
  &.primary {
    background: #1f2937;
    color: white;
    border-color: #1f2937;
    
    &:hover {
      background: #374151;
    }
  }
`;

const MobileActionBar = styled.div`
  display: none;
  position: fixed;
  bottom: 12px;
  left: 12px;
  right: 12px;
  z-index: 40;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileActionContainer = styled.div`
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(229, 231, 235, 0.7);
  padding: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 380px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
`;

const EmblaViewport = styled.div`
  overflow: hidden;
  flex: 1;
  touch-action: pan-y pinch-zoom;
  cursor: grab;
  
  &:active {
    cursor: grabbing;
  }
`;

const EmblaContainer = styled.div`
  display: flex;
  gap: 4px;
  will-change: transform;
`;

const EmblaSlide = styled.div`
  flex: 0 0 70px;
  min-width: 0;
`;

const CarouselArrow = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.8);
  color: #6b7280;
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: ${props => props.$disabled ? 0.2 : 0.7};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  
  &:hover {
    background: rgba(255, 255, 255, 0.95);
    transform: ${props => props.$disabled ? 'none' : 'scale(1.1)'};
    opacity: ${props => props.$disabled ? 0.2 : 1};
    color: ${props => props.$disabled ? '#6b7280' : '#043663'};
  }
  
  &:disabled {
    cursor: not-allowed;
  }
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const MobileActionBtn = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 4px;
  border-radius: 16px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 68px;
  flex-shrink: 0;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  &:hover {
    background: rgba(243, 244, 246, 0.8);
    color: #043663;
    transform: translateY(-1px);
  }
  
  &.active {
    background: rgba(30, 64, 175, 0.1);
    color: #043663;
  }
`;

const MobileActionIcon = styled.span`
  font-size: 20px;
`;

const MobileActionLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
`;



const StatsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const StatsTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const StatsControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const SeasonDropdown = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SeasonSelect = styled.select`
  background: rgba(255, 255, 255, 0.7);
  color: #6b7280;
  border: 1px solid rgba(229, 231, 235, 0.7);
  border-radius: 8px;
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  font-size: 0.875rem;
  cursor: pointer;
  appearance: none;
  backdrop-filter: blur(8px);
  
  &:focus {
    outline: none;
    border-color: #043663;
    color: #043663;
  }
`;

const DropdownIcon = styled.span`
  position: absolute;
  right: 0.75rem;
  color: #9ca3af;
  pointer-events: none;
  font-size: 0.75rem;
`;

const StatsCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
`;

const StatsCard = styled.div`
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(229, 231, 235, 0.7);
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(8px);
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const StatsCardIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${props => props.$accent ? '#d52b1e' : '#043663'} 0%, ${props => props.$accent ? '#e6392a' : '#0a4d7a'} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: white;
  flex-shrink: 0;
`;

const StatsCardContent = styled.div`
  flex: 1;
`;

const StatsCardValue = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
`;

const StatsCardLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
`;

const StatsCardSubtext = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    margin-bottom: 1rem;
    justify-items: center;
    width: 100%;
    max-width: 100%;
  }
`;

const ChartCard = styled.div`
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(229, 231, 235, 0.7);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(8px);
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    margin: 0 auto;
    width: 95%;
    max-width: 95%;
    border-radius: 10px;
  }
`;

const ChartTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 1rem 0;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin: 0 0 0.5rem 0;
  }
`;

const ChartContainer = styled.div`
  background: #f9fafb;
  border: 1px solid rgba(229, 231, 235, 0.7);
  border-radius: 12px;
  min-height: 200px;
  padding: 1rem;
  overflow-x: auto;
  
  @media (max-width: 768px) {
    padding: 0.3rem;
    min-height: 100px;
    overflow-x: auto;
    width: 100%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    border-radius: 6px;
  }
`;

const LeaderboardTableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  width: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  
  @media (max-width: 768px) {
    margin: 0 auto;
    padding: 0;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const LeaderboardTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  min-width: 0;
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
    min-width: 350px;
  }
`;

const LeaderboardHeadCell = styled.th`
  text-align: left;
  padding: 0.5rem;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
  
  &.center {
    text-align: center;
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.3rem;
    font-size: 0.75rem;
  }
`;

const LeaderboardRow = styled.tr`
  background: ${props => props.$highlight ? 'rgba(4, 54, 99, 0.08)' : 'transparent'};
`;

const ForfeitBadge = styled.span`
  display: inline-block;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #9f1239;
  background: #ffe4e6;
  vertical-align: middle;
  text-decoration: none;
  white-space: nowrap;
`;

const LeaderboardCell = styled.td`
  padding: 0.5rem;
  border-bottom: 1px solid #f3f4f6;
  color: #1f2937;
  
  &.center {
    text-align: center;
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.3rem;
    font-size: 0.75rem;
  }
`;

const TeamLogoCell = styled.td`
  padding: 0.5rem;
  border-bottom: 1px solid #f3f4f6;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.3rem;
    gap: 0.4rem;
  }
`;

const TeamLogoSmall = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #043663 0%, #043663 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
  }
`;

const TeamLogoImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const TeamLogoInitials = styled.div`
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  
  @media (max-width: 768px) {
    font-size: 0.6rem;
  }
`;

const TeamNameCell = styled.div`
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 0.65rem;
    max-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// Results section styled components
const ResultsSearchContainer = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const ResultsSearchInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.95rem;
  background: rgba(255, 255, 255, 0.9);
  color: #1f2937;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.1);
  }
  
  &::placeholder {
    color: #9ca3af;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 0.65rem 0.85rem;
    font-size: 0.9rem;
  }
`;

const ResultsFilterSelect = styled.select`
  min-width: 160px;
  padding: 0.65rem 1rem;
  border-radius: 8px;
  border: 2px solid #e5e7eb;
  background: rgba(255, 255, 255, 0.9);
  color: #1f2937;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.1);
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ResultsCount = styled.div`
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #043663 0%, #0a4d7a 100%);
  color: white;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    width: 100%;
    text-align: center;
    padding: 0.65rem;
    font-size: 0.85rem;
  }
`;

const ResultsTableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  width: 100%;
  margin: 0 auto;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    margin: 0 auto;
    padding: 0;
    width: 100%;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
  }
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #043663;
    border-radius: 4px;
  }
  
  /* Prevent horizontal scrolling from long text */
  table {
    table-layout: fixed;
    width: 100%;
  }
`;

const ResultsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  overflow: hidden;
  min-width: 700px;
  table-layout: fixed;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    min-width: 650px;
  }
`;

const ResultsHeadCell = styled.th`
  text-align: left;
  padding: 1rem 0.75rem;
  color: white;
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
  background: linear-gradient(135deg, #043663 0%, #0a4d7a 100%);
  font-weight: 600;
  font-size: 0.9rem;
  position: sticky;
  top: 0;
  z-index: 10;
  
  &:first-child {
    border-top-left-radius: 12px;
  }
  
  &:last-child {
    border-top-right-radius: 12px;
  }
  
  &.center {
    text-align: center;
  }
  
  &.date-col {
    background: linear-gradient(135deg, #043663 0%, #0a4d7a 100%);
    width: 12%;
    min-width: 100px;
  }
  
  &.time-col {
    background: linear-gradient(135deg, #0a4d7a 0%, #0f5a8a 100%);
    width: 8%;
    min-width: 70px;
  }
  
  &.location-col {
    background: linear-gradient(135deg, #0f5a8a 0%, #155a9a 100%);
    width: 14%;
    min-width: 140px;
    max-width: 180px;
    
    @media (max-width: 768px) {
      width: 12%;
      min-width: 110px;
      max-width: 140px;
    }
  }
  
  &.team-a-col {
    background: linear-gradient(135deg, #0f5a8a 0%, #155a9a 100%);
    width: 22%;
    min-width: 150px;
  }
  
  &.team-b-col {
    background: linear-gradient(135deg, #155a9a 0%, #1b5aaa 100%);
    width: 22%;
    min-width: 150px;
  }
  
  &.scores-col {
    background: linear-gradient(135deg, #2160ba 0%, #265fc1 100%);
    width: 12%;
    min-width: 100px;
  }
  
  &.result-col {
    background: linear-gradient(135deg, #1b5aaa 0%, #2160ba 100%);
    width: 10%;
    min-width: 80px;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.75rem;
  }
`;

const ResultsRow = styled.tr`
  background: ${props => props.$highlight ? 'rgba(4, 54, 99, 0.12)' : '#ffffff'};
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.$highlight ? 'rgba(4, 54, 99, 0.18)' : 'rgba(4, 54, 99, 0.08)'};
    transform: scale(1.01);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  
  &:nth-child(even) {
    background: ${props => props.$highlight ? 'rgba(4, 54, 99, 0.12)' : '#f8fafc'};
    
    &:hover {
      background: ${props => props.$highlight ? 'rgba(4, 54, 99, 0.18)' : 'rgba(4, 54, 99, 0.12)'};
    }
  }
`;

const ResultsCell = styled.td`
  padding: 1rem 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  color: #1f2937;
  font-weight: 500;
  vertical-align: middle;
  
  &.center {
    text-align: center;
  }
  
  &.date-cell {
    color: #043663;
    font-weight: 600;
    width: 12%;
    min-width: 100px;
  }
  
  &.time-cell {
    color: #0a4d7a;
    font-weight: 600;
    width: 8%;
    min-width: 70px;
  }
  
  &.location-cell {
    color: #0f5a8a;
    font-weight: 500;
    font-size: 0.85rem;
    width: 14%;
    min-width: 140px;
    max-width: 180px;
    padding-right: 0.75rem;
    padding-left: 0.75rem;

    @media (max-width: 768px) {
      width: 12%;
      min-width: 110px;
      max-width: 140px;
      padding-right: 0.5rem;
      padding-left: 0.5rem;
    }
  }
  
  &.team-a-cell {
    color: #155a9a;
    font-weight: 600;
  }
  
  &.team-b-cell {
    color: #1b5aaa;
    font-weight: 600;
  }

  &.scores-cell {
    color: #0f4c8a;
    font-weight: 600;
    width: 12%;
    min-width: 100px;
    text-align: center;
  }
  
  &.result-cell {
    color: #0f4c8a;
    font-weight: 600;
    width: 10%;
    min-width: 80px;
    text-align: center;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.75rem;
  }
`;

const ResultsLocationTrigger = styled.button`
  display: block;
  width: 100%;
  max-width: 100%;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  font-weight: inherit;
  font-size: inherit;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid #043663;
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const ResultScore = styled.span`
  font-weight: 700;
  color: #043663;
  font-size: 1rem;
  padding: 0.25rem 0.5rem;
  background: linear-gradient(135deg, rgba(4, 54, 99, 0.1) 0%, rgba(10, 77, 122, 0.1) 100%);
  border-radius: 6px;
  display: inline-block;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    padding: 0.2rem 0.4rem;
  }
`;

const ResultsTeamCell = styled.td`
  padding: 1rem 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  color: #1f2937;
  font-weight: 500;
  vertical-align: middle;
  
  &.team-a-cell {
    color: #155a9a;
    font-weight: 600;
    width: 22%;
    min-width: 150px;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }
  
  &.team-b-cell {
    color: #1b5aaa;
    font-weight: 600;
    width: 22%;
    min-width: 150px;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.25rem;
    font-size: 0.75rem;
    
    &.team-a-cell,
    &.team-b-cell {
      padding-left: 0.25rem;
    }
  }
`;

const ResultsTeamCellContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    gap: 0.4rem;
  }
`;

const ResultsTeamLogo = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #043663 0%, #043663 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
  }
`;

const ResultsTeamLogoImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ResultsTeamLogoInitials = styled.div`
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  
  @media (max-width: 768px) {
    font-size: 0.6rem;
  }
`;

const ResultsTeamName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  
  @media (max-width: 768px) {
    max-width: 120px;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
  font-size: 0.9rem;
  margin-bottom: 4.5rem;
`;

const PaginationInfo = styled.span`
  color: #1f2937;
  font-weight: 600;
`;

const PaginationButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const PaginationButton = styled.button`
  border: 1px solid #043663;
  background: #043663;
  color: #ffffff;
  padding: 0.35rem 0.9rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #0f4c8a;
    border-color: #0f4c8a;
  }

  &:disabled {
    background: #e5e7eb;
    border-color: #e5e7eb;
    color: #94a3b8;
    cursor: not-allowed;
  }
`;

const PerformanceChartContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
  height: 150px;
  padding: 1rem 0.5rem 0 0.5rem;
  
  @media (max-width: 768px) {
    height: 80px;
    padding: 0.3rem 0.1rem 0 0.1rem;
    gap: 0.1rem;
  }
`;

const WinLossChartContainer = styled.div`
  padding: 1rem;
  
  @media (max-width: 768px) {
    padding: 0.3rem;
  }
`;

const WinLossStatsContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    gap: 0.2rem;
    flex-wrap: wrap;
    margin-bottom: 0.3rem;
  }
`;

const WinLossStatItem = styled.div`
  text-align: center;
  
  @media (max-width: 768px) {
    flex: 1;
    min-width: 60px;
  }
`;

const LeaderboardTabsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  
  @media (max-width: 768px) {
    gap: 0.2rem;
    margin-bottom: 0.3rem;
  }
`;

const LeaderboardTab = styled.button`
  padding: 0.4rem 0.75rem;
  border-radius: 9999px;
  border: 1px solid #e5e7eb;
  background: ${props => props.$active ? '#043663' : 'white'};
  color: ${props => props.$active ? 'white' : '#374151'};
  font-size: 0.8rem;
  cursor: pointer;
  
  @media (max-width: 768px) {
    padding: 0.2rem 0.4rem;
    font-size: 0.65rem;
  }
`;

// Leaderboard accordion container
const DivisionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

// Leaderboard accordion styled components
const LeaderboardAccordion = styled.div`
  margin-bottom: 1.5rem;
`;

const CantonRow = styled.div`
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  overflow: hidden;
  transition: all 0.3s ease;
`;

const CantonHeader = styled.button`
  width: 100%;
  padding: 1rem 1.5rem;
  background: ${props => props.$isOpen ? 'linear-gradient(135deg, #043663 0%, #0a4d7a 100%)' : 'rgba(255, 255, 255, 0.9)'};
  color: ${props => props.$isOpen ? 'white' : '#1f2937'};
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$isOpen ? 'linear-gradient(135deg, #043663 0%, #0a4d7a 100%)' : 'rgba(4, 54, 99, 0.1)'};
    color: ${props => props.$isOpen ? 'white' : '#043663'};
  }
`;

const CantonName = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CantonCount = styled.span`
  background: ${props => props.$isOpen ? 'rgba(255, 255, 255, 0.2)' : 'rgba(4, 54, 99, 0.1)'};
  color: ${props => props.$isOpen ? 'white' : '#043663'};
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
`;

const ExpandIcon = styled.div`
  font-size: 1.2rem;
  transition: transform 0.3s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const CantonContent = styled.div`
  /* Viewport-capped so opening several division boards stays reachable via scroll */
  max-height: ${(props) => (props.$isOpen ? 'min(70vh, 900px)' : '0')};
  overflow-x: hidden;
  overflow-y: ${(props) => (props.$isOpen ? 'auto' : 'hidden')};
  -webkit-overflow-scrolling: touch;
  transition: max-height 0.3s ease;
  background: rgba(255, 255, 255, 0.95);
`;

const DivisionRow = styled.div`
  border-bottom: 1px solid #e5e7eb;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DivisionHeader = styled.button`
  width: 100%;
  padding: 0.75rem 1.5rem;
  background: ${props => props.$isOpen ? 'linear-gradient(135deg, #d52b1e 0%, #f56565 100%)' : 'rgba(255, 255, 255, 0.9)'};
  color: ${props => props.$isOpen ? 'white' : '#1f2937'};
  border: none;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$isOpen ? 'linear-gradient(135deg, #d52b1e 0%, #f56565 100%)' : 'rgba(213, 43, 30, 0.1)'};
    color: ${props => props.$isOpen ? 'white' : '#d52b1e'};
  }
`;

const DivisionName = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DivisionCount = styled.span`
  background: ${props => props.$isOpen ? 'rgba(255, 255, 255, 0.2)' : 'rgba(213, 43, 30, 0.1)'};
  color: ${props => props.$isOpen ? 'white' : '#d52b1e'};
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const DivisionContent = styled.div`
  /* No hard clip — parent CantonContent scrolls when many boards are open */
  max-height: ${(props) => (props.$isOpen ? 'none' : '0')};
  overflow: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transition: max-height 0.3s ease;
  background: rgba(255, 255, 255, 0.98);
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  color: #6b7280;
  font-size: 0.9rem;
`;

const EmptyMessage = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  color: #6b7280;
  font-size: 0.9rem;
  font-style: italic;
`;

const SeasonInfoBanner = styled.div`
  background-color: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  color: #92400e;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::before {
    content: 'ℹ️';
    font-size: 1rem;
  }
`;

// Search components
const SearchSection = styled.div`
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(229, 231, 235, 0.7);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
  margin-bottom: 1rem;

  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.1);
  }
`;

const SearchResultsContainer = styled.div`
  margin-top: 1rem;
`;

const SearchResultItem = styled.div`
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #043663;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(4, 54, 99, 0.1);
  }
`;

const SearchResultTitle = styled.div`
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
`;

const SearchResultSubtitle = styled.div`
  font-size: 0.9rem;
  color: #6b7280;
`;

const SimpleChart = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
  height: 200px;
  gap: 0.5rem;
`;

const ChartBar = styled.div`
  flex: 1;
  background: linear-gradient(135deg, #043663 0%, #043663 100%);
  border-radius: 4px;
  display: flex;
  align-items: end;
  justify-content: center;
  color: white;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem;
  min-height: 20px;
`;

const RadarChart = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  position: relative;
`;

const RadarCircle = styled.div`
  width: 200px;
  height: 200px;
  border: 2px solid #e5e7eb;
  border-radius: 50%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    position: absolute;
    width: 150px;
    height: 150px;
    border: 1px solid #d1d5db;
    border-radius: 50%;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 100px;
    height: 100px;
    border: 1px solid #d1d5db;
    border-radius: 50%;
  }
`;

const RadarLabel = styled.div`
  position: absolute;
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
`;

const RadarChartCard = styled.div`
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(229, 231, 235, 0.7);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(8px);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  
  @media (min-width: 768px) {
    margin-bottom: 1rem;
  }

  @media (max-width: 767px) {
    ${(p) =>
      p.$teamRoster
        ? `
      flex-direction: column;
      align-items: stretch;
      gap: 0.65rem;

      & > div:last-child {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 0.45rem;
        width: 100%;
      }
    `
        : ''}
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const SectionAction = styled.div``;

const TeamRosterHint = styled.span`
  font-size: 0.85rem;
  color: #6b7280;
  max-width: 420px;
  text-align: right;
  line-height: 1.4;

  @media (max-width: 767px) {
    max-width: none;
    text-align: left;
  }
`;

const SyncCalendarBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 12px;
  border: 1px solid rgba(229, 231, 235, 0.7);
  background: rgba(255, 255, 255, 0.7);
  color: #6b7280;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(8px);
  
  &:hover {
    background: #f9fafb;
    color: #043663;
  }
`;

const ScheduleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ScheduleCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-radius: 16px;
  border: 1px solid rgba(229, 231, 235, 0.7);
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(8px);
  transition: all 0.2s ease;
  gap: 1rem;
    
  &:hover {
    background: rgba(249, 250, 251, 0.8);
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 1rem;
  }
`;

const ScheduleDate = styled.div`
  min-width: 110px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  border-radius: 12px;
  border: 1px solid rgba(229, 231, 235, 0.7);
  background: rgba(255, 255, 255, 0.5);
  padding: 0.75rem 0.5rem;
  margin-right: 1rem;

  @media (max-width: 768px) {
    min-width: 100%;
    margin-right: 0;
    padding: 0.5rem;
  }
`;

const ScheduleDay = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
`;

const ScheduleDateNum = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
`;

const ScheduleInfo = styled.div`
  flex: 1;
  min-width: 0;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ScheduleOpponent = styled.div`
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 0.25rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    font-weight: 600;
  }
`;

const ScheduleVenue = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const ScheduleActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const CalendarBtn = styled.button`
  display: none;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 12px;
  border: 1px solid rgba(229, 231, 235, 0.7);
  background: rgba(255, 255, 255, 0.7);
  color: #6b7280;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(8px);
    
    &:hover {
    background: #f9fafb;
    color: #043663;
  }
  
  @media (min-width: 768px) {
    display: inline-flex;
  }
`;

const PlayerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 0.75rem;
  
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 639px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const PlayerCard = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 16px;
  border: 1px solid rgba(229, 231, 235, 0.7);
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(8px);
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(249, 250, 251, 0.8);
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }

  @media (max-width: 767px) {
    flex-wrap: wrap;
    align-items: flex-start;
  }
`;

const PlayerAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #043663 0%, #043663 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid #e5e7eb;
  
  &:hover {
    transform: scale(1.05);
    border-color: #043663;
  }
`;

const PlayerAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlayerAvatarInitials = styled.div`
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.1rem;
`;

const PlayerPlusIcon = styled.div`
  font-size: 0.7rem;
  opacity: 0.8;
`;

const ProfileUploadInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
`;

const PlayerInfo = styled.div`
  flex: 1;
  min-width: 0;

  @media (max-width: 767px) {
    flex: 1 1 100%;
  }
`;

const PlayerName = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  color: #1f2937;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 767px) {
    flex-wrap: wrap;
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
  }
`;

const PlayerPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  
  ${props => props.$type === 'captain' && `
    background: rgba(59, 130, 246, 0.1);
    color: #043663;
    border: 1px solid rgba(59, 130, 246, 0.2);
  `}

  ${props => props.$type === 'member' && `
    background: rgba(124, 58, 237, 0.08);
    color: #5b21b6;
    border: 1px solid rgba(124, 58, 237, 0.2);
  `}

  ${props => props.$type === 'complete' && `
    background: rgba(16, 185, 129, 0.1);
    color: #059669;
    border: 1px solid rgba(16, 185, 129, 0.2);
  `}
  
  ${props => props.$type === 'inactive' && `
    background: rgba(107, 114, 128, 0.1);
    color: #6b7280;
    border: 1px solid rgba(107, 114, 128, 0.2);
  `}
`;

const PlayerDetails = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const PlayerMetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;

  @media (max-width: 767px) {
    white-space: normal;
    overflow-wrap: anywhere;
  }
`;

const PlayerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #6b7280;

  @media (max-width: 767px) {
    width: 100%;
    flex-wrap: wrap;
    justify-content: flex-start;
  }
`;

const PlayerIcon = styled.span`
  font-size: 1rem;
  cursor: pointer;
  transition: color 0.2s ease;
  
  &:hover {
    color: #043663;
  }
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const HeaderTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
`;


const CurrentStandingCard = styled.div`
  background: linear-gradient(135deg, #043663 0%, #043663 100%);
  color: white;
  padding: 1.5rem;
  border-radius: 24px;
  margin-bottom: 1.5rem;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    margin-bottom: 1rem;
  }
`;

const StandingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
`;

const StandingTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const StandingRank = styled.div`
  text-align: right;
`;

const RankNumber = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  line-height: 1;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const Division = styled.div`
  font-size: 1rem;
  opacity: 0.9;
  margin-top: 0.25rem;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const StandingStats = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.15);
  padding: 1rem;
  border-radius: 16px;
  text-align: center;
  backdrop-filter: blur(8px);

  @media (max-width: 768px) {
    padding: 0.75rem;
  }
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  opacity: 0.9;
`;

const TeamStatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const TeamStatCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(12px);

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const TeamStatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #043663 0%, #043663 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: white;
  flex-shrink: 0;
`;

const TeamStatContent = styled.div`
  flex: 1;
`;

const TeamStatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.25rem;
`;

const TeamStatLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
`;

const TeamStatSubtext = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
`;

const MatchesSection = styled.section`
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

/** Title inline in overview matches row (with season selector on the right). */
const MatchesHeading = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const SchedulingInboxSection = styled.div`
  margin: -0.5rem 0 1rem 0;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  border: 1px solid rgba(250, 204, 21, 0.5);
  background: rgba(254, 252, 232, 0.9);
`;

const SchedulingInboxHeading = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  color: #854d0e;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const SchedulingInboxRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.45rem 0;
  border-top: 1px solid rgba(234, 179, 8, 0.25);
  &:first-of-type {
    border-top: none;
    padding-top: 0;
  }
`;

const SchedulingInboxNames = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #422006;
  flex: 1;
  min-width: 140px;
`;

const SchedulingInboxBadge = styled.span`
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #1e40af;
  background: #dbeafe;
  padding: 0.15rem 0.35rem;
  border-radius: 4px;
  margin-right: 0.35rem;
`;

const SchedulingInboxBtn = styled.button`
  padding: 0.35rem 0.65rem;
  border-radius: 8px;
  border: none;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  background: linear-gradient(135deg, #043663 0%, #d52b1e 100%);
  color: white;
`;

const SchedulingMatchBadge = styled.span`
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #9a3412;
  background: #ffedd5;
  padding: 0.12rem 0.35rem;
  border-radius: 4px;
  margin-right: 0.35rem;
`;

const MatchesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

// Styled component for match window groups (pairs of 2 matches)
const MatchWindowGroup = styled.div`
  border: 2px solid rgba(59, 130, 246, 0.3);
  border-radius: 16px;
  padding: 0.5rem;
  margin-bottom: 1rem;
  background: rgba(239, 246, 255, 0.3);
  backdrop-filter: blur(4px);
  
  @media (max-width: 768px) {
    padding: 0.4rem;
    margin-bottom: 0.75rem;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const MatchWindowLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: #3b82f6;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
  padding: 0.25rem 0.5rem;
  background: rgba(59, 130, 246, 0.1);
  border-radius: 8px;
  display: inline-block;
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
    margin-bottom: 0.4rem;
  }
`;

const MatchCard = styled.div`
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(229, 231, 235, 0.7);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;
  backdrop-filter: blur(8px);
  gap: 1rem;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:hover {
    background: rgba(249, 250, 251, 0.8);
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 1rem;
    gap: 0.75rem;
  }
`;

const MatchInfo = styled.div`
  flex: 1;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const MatchDate = styled.div`
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
  font-size: 0.875rem;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const MatchHomeAwayRow = styled.div`
  margin: 0.35rem 0 0.25rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
`;

const OpponentMatchTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  margin-top: 0.35rem;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  text-align: left;
  font: inherit;
  max-width: 100%;

  &:disabled {
    cursor: default;
    opacity: 0.65;
  }

  .opp-line {
    font-size: 0.95rem;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.3;
  }

  &:hover:not(:disabled) .opp-line {
    color: #043663;
    text-decoration: underline;
  }

  @media (max-width: 768px) {
    .opp-line {
      font-size: 0.9rem;
    }
  }
`;

const OpponentMiniLogo = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid #e5e7eb;
  flex-shrink: 0;
`;

const OpponentMiniLogoPh = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #e5e7eb;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 800;
  flex-shrink: 0;
`;

const ChartOpponentHit = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: none;
  background: none;
  padding: 0.25rem 0 0;
  margin: 0;
  width: 100%;
  cursor: pointer;
  font: inherit;

  &:disabled {
    cursor: default;
    opacity: 0.75;
  }

  &:not(:disabled):hover .chart-opp-line {
    color: #043663;
    text-decoration: underline;
  }
`;

const ChartOppLogo = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  object-fit: cover;
  border: 1px solid #e5e7eb;
`;

const MatchLocation = styled.div`
  font-size: 0.875rem;
  color: #9ca3af;
  margin-top: 0.25rem;
`;

const MatchScore = styled.div`
  font-weight: 600;
  color: ${props => props.$isWin ? '#16a34a' : props.$isLoss ? '#dc2626' : '#6b7280'};
  margin-top: 0.25rem;
`;

const MatchActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const MatchStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
    gap: 0.75rem;
  }
`;

const DisputedBanner = styled.div`
  margin-top: 0.75rem;
  background: rgba(251, 191, 36, 0.14);
  border: 1px solid rgba(251, 191, 36, 0.45);
  border-radius: 12px;
  padding: 0.75rem 0.9rem;
  color: #92400e;
  font-size: 0.78rem;
  line-height: 1.5;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const DisputedBannerAction = styled.button`
  align-self: flex-start;
  border: none;
  border-radius: 9999px;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.4rem 0.85rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(234, 88, 12, 0.25);
  transition: transform 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 8px 22px rgba(234, 88, 12, 0.3);
  }
`;

const StatusBadge = styled.span`
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  
  ${props => props.$status === 'pending-score' && `
    background-color: rgba(4, 54, 99, 0.1);
    color: #043663;
    border: 1px solid rgba(4, 54, 99, 0.2);
  `}
  
  ${props => props.$status === 'completed' && `
    background-color: #dcfce7;
    color: #166534;
    border: 1px solid #bbf7d0;
  `}
  
  ${props => props.$status === 'upcoming' && `
    background-color: #dbeafe;
    color: #043663;
    border: 1px solid #bfdbfe;
  `}
  
  ${props => props.$status === 'disputed' && `
    background-color: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
  `}
`;

const StatusArrow = styled.span`
  color: #9ca3af;
  font-size: 0.875rem;
  font-weight: 600;
`;

const MatchDetailsButton = styled.button`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  
  &:hover {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.6rem;
    font-size: 0.7rem;
  }
`;

const HomeAwayButton = styled.button`
  background: ${props => props.$isUnset
    ? 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
    : props.$isHome
      ? 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)'
      : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.15);
  }
  &:active {
    transform: translateY(0);
  }
  @media (max-width: 768px) {
    padding: 0.4rem 0.6rem;
    font-size: 0.7rem;
  }
`;

const ActivitySection = styled.section`
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const PendingVerificationsSection = styled.section`
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const PendingVerificationsTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 1rem 0;
`;

const PendingVerificationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const VerificationCard = styled.div`
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(229, 231, 235, 0.7);
  border-radius: 16px;
  padding: 1.25rem;
  backdrop-filter: blur(8px);
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background: rgba(255, 255, 255, 0.9);
    border-color: #043663;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(4, 54, 99, 0.1);
  }
`;

const VerificationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
`;

const VerificationOpponent = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
`;

const VerificationDate = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const VerificationScore = styled.div`
  font-size: 0.875rem;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const VerificationAction = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const VerifyButton = styled.button`
  background: linear-gradient(135deg, #043663 0%, #d52b1e 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(4, 54, 99, 0.3);
  }
`;

const DisputeButton = styled.button`
  background: transparent;
  color: #d52b1e;
  border: 2px solid #d52b1e;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #d52b1e;
    color: white;
    transform: translateY(-1px);
  }
`;

const ActivityTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 1rem 0;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ActivityItem = styled.div`
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(229, 231, 235, 0.7);
  border-radius: 16px;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.2s ease;
  backdrop-filter: blur(8px);
  
  &:hover {
    background: rgba(249, 250, 251, 0.8);
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
`;

const ActivityIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #16a34a;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  flex-shrink: 0;
`;

const ActivityContent = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ActivityText = styled.span`
  color: #1f2937;
  font-size: 0.95rem;
`;

const ActivityTime = styled.span`
  font-size: 0.875rem;
  color: #9ca3af;
`;

const AlertBanner = styled.div`
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border: 1px solid #3b82f6;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AlertIcon = styled.div`
  color: #3b82f6;
  font-size: 1.5rem;
`;

const AlertContent = styled.div`
  flex: 1;
`;

const AlertTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e40af;
  margin: 0 0 0.5rem 0;
`;

const AlertText = styled.p`
  color: #1e40af;
  margin: 0;
  font-size: 0.95rem;
`;

const AlertButton = styled(Link)`
  background: #3b82f6;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.2s ease;
  
  &:hover {
    background: #2563eb;
    transform: translateY(-1px);
  }
`;

const MidSeasonMessage = styled.div`
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #d97706;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 0.75rem;
  }
`;

const MidSeasonIcon = styled.div`
  color: #d97706;
  font-size: 2rem;
  flex-shrink: 0;
`;

const MidSeasonContent = styled.div`
  flex: 1;
`;

const MidSeasonTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #92400e;
  margin: 0 0 0.5rem 0;
`;

const MidSeasonText = styled.p`
  color: #92400e;
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.5;
`;

// Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
const getOrdinalSuffix = (num) => {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
};

const PaymentAlert = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background: ${props => props.$success ? '#10b981' : 'linear-gradient(135deg, #043663 0%, #0a4d7a 100%)'};
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

// Helper function to get team initials
const getTeamInitials = (teamName) => {
  if (!teamName) return 'TM';
  return teamName
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

// Helper function to format scores with bold winning numbers and dull losing numbers
const formatScoresWithBold = (scoresString) => {
  // Return empty string for matches without scores (upcoming matches)
  if (!scoresString || scoresString.trim() === '' || scoresString === '-') return '';
  
  const sets = scoresString.split(';').map(s => s.trim()).filter(Boolean);
  
  if (sets.length === 0) return '';
  
  // Filter out sets where both scores are 0 (set wasn't played, especially for 3rd set tiebreaker)
  const validSets = sets.filter(set => {
    const parts = set.split('-').map(s => s.trim());
    if (parts.length !== 2) return true; // Keep if format is invalid (let it display as-is)
    
    const score1 = parseInt(parts[0], 10);
    const score2 = parseInt(parts[1], 10);
    
    if (isNaN(score1) || isNaN(score2)) return true; // Keep if parsing fails
    
    // Filter out "0-0" sets (not played)
    return !(score1 === 0 && score2 === 0);
  });
  
  if (validSets.length === 0) return '';
  
  return validSets.map((set, index) => {
    const parts = set.split('-').map(s => s.trim());
    if (parts.length !== 2) return set;
    
    const score1 = parseInt(parts[0], 10);
    const score2 = parseInt(parts[1], 10);
    
    if (isNaN(score1) || isNaN(score2)) return set;
    
    const isFirstWinner = score1 > score2;
    
    return (
      <React.Fragment key={index}>
        {index > 0 && ' ; '}
        {isFirstWinner ? (
          <>
            <strong>{score1}</strong>-<span style={{ opacity: 0.5, color: '#6b7280' }}>{score2}</span>
          </>
        ) : score2 > score1 ? (
          <>
            <span style={{ opacity: 0.5, color: '#6b7280' }}>{score1}</span>-<strong>{score2}</strong>
          </>
        ) : (
          <>
            {score1}-{score2}
          </>
        )}
      </React.Fragment>
    );
  });
};

const VALID_SECTIONS = ['overview', 'stats', 'schedule', 'results', 'divisions', 'reserve-court', 'team', 'settings'];

const DashboardPage = (props) => {
  const {
    embedded = false,
    embeddedTeamId = null,
    embeddedSeasonId = null,
    embeddedSection = 'overview',
    /** When true (with embedded), show team nav sidebar + offset main content like standalone dashboard */
    embeddedShowTeamSidebar = false,
    /** If set, section clicks update parent URL instead of navigating to /dashboard */
    onEmbeddedSectionNavigate = null,
    /** When embedded from player tournaments, scope season dropdown to this campaign only */
    embeddedCampaignId = null,
    /** Completed campaign: read-only (hide settings, no roster edits) */
    embeddedCampaignViewOnly = false,
    /** When embedded, navigate back to tournament flow (e.g. team picker) without re-entering from menu */
    onEmbeddedBack = null,
  } = props;
  const { t, i18n } = useTranslation();
  const localeTag = i18n.language?.startsWith('fr') ? 'fr-CH' : 'en-GB';
  const campaignViewOnly = embedded && embeddedCampaignViewOnly;
  const showTeamSidebar = !embedded || embeddedShowTeamSidebar;
  const compactEmbedMain = embedded && !embeddedShowTeamSidebar;
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const refreshScoreRelatedQueries = () => {
    queryClient.invalidateQueries('dashboardOverview');
    queryClient.invalidateQueries('dashboardOverviewMatches');
    queryClient.invalidateQueries('dashboardSchedule');
    queryClient.invalidateQueries('matchResults');
  };
  const urlTeamId = searchParams.get('team_id');
  const urlSeasonId = searchParams.get('season_id');
  // JWT must match API behavior: /api/members requires team_id query for player tokens; stale React user can be wrong after token swap.
  const isPlayerView = readJwtPayload(getLocalStorageItem('token'))?.type === 'player';
  const effectiveTeamId = embedded
    ? (embeddedTeamId || null)
    : (isPlayerView ? urlTeamId || null : (user?.teamId || user?.id));
  const effectiveSeasonId = embedded ? (embeddedSeasonId || null) : (urlSeasonId || null);
  const viewerPlayerIdForChat = useMemo(() => {
    if (!isPlayerView) return null;
    const p = readJwtPayload(getLocalStorageItem('token'));
    return p?.playerId || null;
  }, [isPlayerView, user?.id]);

  const { data: availabilityFortnightAxios } = useQuery(
    ['playerAvailabilityFortnight'],
    () => playerAPI.getAvailabilityFortnight(),
    {
      enabled: Boolean(isPlayerView && viewerPlayerIdForChat),
      staleTime: 60 * 1000,
      refetchOnWindowFocus: true,
    }
  );

  const availabilityReminder = useMemo(() => {
    const raw = availabilityFortnightAxios;
    if (!raw || raw.success === false || raw.enabled === false) return null;
    const { show, variant } = computeAvailabilityReminder(raw.days);
    if (!show || !variant) return null;
    return { variant };
  }, [availabilityFortnightAxios]);

  const [activeSection, setActiveSection] = useState(() => {
    if (embedded && embeddedSection && VALID_SECTIONS.includes(embeddedSection))
      return embeddedSection;
    const urlParams = new URLSearchParams(window.location.search);
    const section = urlParams.get('section');
    return section && VALID_SECTIONS.includes(section) ? section : 'overview';
  });

  useEffect(() => {
    if (campaignViewOnly && activeSection === 'settings') {
      setActiveSection('overview');
    }
  }, [campaignViewOnly, activeSection]);

  const [paymentStatus, setPaymentStatus] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  
  const [selectedSkillRange, setSelectedSkillRange] = useState('');
  
  // Edit member form state
  const [showEditForm, setShowEditForm] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [invitePlayerModalOpen, setInvitePlayerModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removePassword, setRemovePassword] = useState('');
  const [removeReason, setRemoveReason] = useState('');
  
  // Chat modal state
  const [chatModalOpen, setChatModalOpen] = useState(false);
  // Home/Away info modal state
  const [homeAwayModalOpen, setHomeAwayModalOpen] = useState(false);
  const [homeAwayModalType, setHomeAwayModalType] = useState('home'); // 'home' | 'away'
  const [selectedChatMatch, setSelectedChatMatch] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [chatTeamIdOverride, setChatTeamIdOverride] = useState(null);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [selectedScheduleMatch, setSelectedScheduleMatch] = useState(null);
  const [scheduleTeamIdOverride, setScheduleTeamIdOverride] = useState(null);
  const [opponentSheetOpen, setOpponentSheetOpen] = useState(false);
  const [opponentSheetMatch, setOpponentSheetMatch] = useState(null);

  // Team logo modal state
  const [teamLogoModalOpen, setTeamLogoModalOpen] = useState(false);
  const [resultsLocationModalOpen, setResultsLocationModalOpen] = useState(false);
  const [resultsLocationText, setResultsLocationText] = useState('');
  const [selectedTeamLogo, setSelectedTeamLogo] = useState(null);
  const [selectedTeamName, setSelectedTeamName] = useState(null);
  const [selectedTeamInitials, setSelectedTeamInitials] = useState(null);

  // Logo upload options modal state
  const [logoUploadModalOpen, setLogoUploadModalOpen] = useState(false);

  // Version display and update prompt state
  const [versionText, setVersionText] = useState('');
  const [hasPendingUpdate, setHasPendingUpdate] = useState(false);

  // Settings form state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneNumberLoading, setPhoneNumberLoading] = useState(false);
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');

  // When logged in as player, /dashboard requires team_id and optionally season_id (from player tournaments page) — skip when embedded (player menu provides context)
  useEffect(() => {
    if (embedded) return;
    if (user && isPlayerView && !urlTeamId) {
      navigate('/player/tournaments', { replace: true });
    }
  }, [embedded, user, isPlayerView, urlTeamId, navigate]);

  useEffect(() => {
    // Seed initial version if available
    const initial = versionService.getCurrentVersion?.();
    if (initial?.version) {
      setVersionText(initial.version);
    }

    // Listen for update notifications (from SSE or polling)
    const onUpdate = (hasUpdate) => {
      if (hasUpdate) {
        setHasPendingUpdate(true);
      } else {
        setHasPendingUpdate(false);
      }
      const v = versionService.getCurrentVersion?.();
      if (v?.version) setVersionText(v.version);
    };
    versionService.addListener(onUpdate);
    return () => versionService.removeListener(onUpdate);
  }, []);

  const [editSkillRange, setEditSkillRange] = useState('');
  const maxDob = useMemo(() => new Date().toISOString().split('T')[0], []);
  
  // Score modal state
  const [scoreModalOpen, setScoreModalOpen] = useState(false);
  
  const [selectedScoreMatch, setSelectedScoreMatch] = useState(null);

  // Match details modal state
  const [matchDetailsModalOpen, setMatchDetailsModalOpen] = useState(false);
  const [selectedDetailsMatch, setSelectedDetailsMatch] = useState(null);
  const [matchDetailsLoading, setMatchDetailsLoading] = useState(false);
  const [disputeModalOpen, setDisputeModalOpen] = useState(false);
  const [selectedDisputeMatch, setSelectedDisputeMatch] = useState(null);
  const [disputeLoading, setDisputeLoading] = useState(false);

  // Resubmit modal state
  const [resubmitModalOpen, setResubmitModalOpen] = useState(false);
  const [selectedResubmitMatch, setSelectedResubmitMatch] = useState(null);

  // Season start date - October 27, 2025
  const SEASON_START_DATE = new Date('2025-10-27');
  // Team member editing is now always available
  const isSeasonStarted = new Date() >= SEASON_START_DATE;
  // Team member editing end date - January 30, 2026
  const MEMBER_EDITING_END_DATE = new Date('2026-01-30');
  
  // Check if team member editing is allowed (before end date)
  const canEditMembers = () => {
    return new Date() < MEMBER_EDITING_END_DATE;
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };
  
  // Chat functions (player JWT requires team_id on match-chat routes)
  const fetchUnreadCounts = useCallback(async () => {
    if (isPlayerView && !effectiveTeamId) return;
    try {
      const response = await matchChatAPI.getUnreadCounts(effectiveTeamId || undefined);
      if (response.success) {
        setUnreadCounts(response.byMatch || {});
      }
    } catch (error) {
      // Silently handle error
    }
  }, [isPlayerView, effectiveTeamId]);
  
  const handleOpenChat = (match) => {
    setChatTeamIdOverride(null);
    setSelectedChatMatch(match);
    setChatModalOpen(true);
  };

  const openOpponentTeamSheet = (match) => {
    if (!match?.id || !effectiveTeamId || match?.opponentId == null) return;
    setOpponentSheetMatch(match);
    setOpponentSheetOpen(true);
  };

  const closeOpponentTeamSheet = () => {
    setOpponentSheetOpen(false);
    setOpponentSheetMatch(null);
  };

  const handleOpenSchedulingInbox = (row) => {
    const myTeam = String(row.my_team_id);
    const opp =
      String(row.team1_id) === myTeam
        ? { id: row.team2_id, name: row.team2_name }
        : { id: row.team1_id, name: row.team1_name };
    setSelectedScheduleMatch({
      id: row.league_match_id,
      opponent: opp.name,
      opponentId: opp.id,
    });
    setScheduleTeamIdOverride(row.my_team_id);
    setScheduleModalOpen(true);
  };

  const handleOpenSchedule = (match) => {
    setScheduleTeamIdOverride(null);
    setSelectedScheduleMatch(match);
    setScheduleModalOpen(true);
  };

  const handleCloseSchedule = () => {
    setScheduleModalOpen(false);
    setSelectedScheduleMatch(null);
    setScheduleTeamIdOverride(null);
    queryClient.invalidateQueries(['matchSchedulingInbox']);
  };
  
  const handleCloseChat = () => {
    setChatModalOpen(false);
    setSelectedChatMatch(null);
    setChatTeamIdOverride(null);
    queryClient.invalidateQueries(['matchSchedulingInbox']);
    syncPlayerNotificationsAfterMatchChat(queryClient);
    fetchUnreadCounts();
  };

  // Score modal state
  const [opponentMembers, setOpponentMembers] = useState([]);
  const [loadingOpponentMembers, setLoadingOpponentMembers] = useState(false);

  // Score modal handlers
  const handleOpenScore = async (match) => {
    // Determine modal state based on match status and pending verifications
    let modalState = 'submit';
    let existingScores = null;
    let onVerifyScore = null;
    let onDisputeScore = null;
    let onResubmitScore = null;

    // Check if this match has a pending verification
    const pendingVerification = dashboard?.pendingScoreVerifications?.find(
      v => v.matchId === match.id
    );

    if (pendingVerification) {
      // Parse the score string correctly
      const [team1Score, team2Score] = pendingVerification.score.split(' vs ');
      const team1Sets = team1Score.split('-');
      const team2Sets = team2Score.split('-');
      
      existingScores = {
        team1_set1: team1Sets[0] || '',
        team1_set2: team1Sets[1] || '',
        team1_set3: team1Sets[2] || '',
        team2_set1: team2Sets[0] || '',
        team2_set2: team2Sets[1] || '',
        team2_set3: team2Sets[2] || ''
      };
      
      // Check if current team is the one who submitted the score
      // Ensure both are strings for comparison
      const currentTeamId = String(dashboard?.team?.id);
      const submittedById = String(pendingVerification.submittedBy);
      const isSubmitter = submittedById === currentTeamId;
      
      if (isSubmitter) {
        // Team who submitted - show in view mode (read-only)
        modalState = 'view';
        onVerifyScore = null;
        onDisputeScore = null;
      } else {
        // Opponent team - show verification mode
        modalState = 'verify';
        onVerifyScore = async () => {
          const isVerifyAlreadyDone = (err) =>
            String(err?.message || '')
              .toLowerCase()
              .includes('already processed');
          try {
            await scoreAPI.verifyScore(pendingVerification.id, 'confirm');
            toast.success(t('teamDashboardPage.toastScoreConfirmed'));
          } catch (error) {
            if (isVerifyAlreadyDone(error)) {
              toast.success(t('teamDashboardPage.toastScoreAlreadyConfirmed'));
            } else {
              toast.error(error.message || t('teamDashboardPage.toastVerifyScoreFail'));
              throw error;
            }
          }
          refreshScoreRelatedQueries();
        };
        
        onDisputeScore = async () => {
          setSelectedDisputeMatch({
            ...match,
            pendingVerificationId: pendingVerification.id,
            score: pendingVerification.score,
            dateDisplay: pendingVerification.date || match.dateDisplay || match.date,
          });
          setDisputeModalOpen(true);
        };
      }
    } else if (match.status === 'disputed') {
      // Match is disputed - check if current team is the original submitter
      const currentTeamId = String(dashboard?.team?.id);
      const submittedById = String(match.submitted_by_team_id || '');
      const isOriginalSubmitter = submittedById === currentTeamId;
      
      // Get the disputed scores from the match data
      existingScores = match.scoreDetails || {
        team1_set1: match.team1_score_set1 || '',
        team1_set2: match.team1_score_set2 || '',
        team1_set3: match.team1_score_set3 || '',
        team2_set1: match.team2_score_set1 || '',
        team2_set2: match.team2_score_set2 || '',
        team2_set3: match.team2_score_set3 || ''
      };
      
      // Handle disputed match scores
      if (isOriginalSubmitter) {
        // Original submitter sees disputed scores and can resubmit
        modalState = 'disputed_resubmit';
        onVerifyScore = null;
        onDisputeScore = null;
        onResubmitScore = () => {
          handleOpenResubmitModal(match);
        };
      } else {
        // Opponent team - show in view mode only
        modalState = 'view';
        onVerifyScore = null;
        onDisputeScore = null;
        onResubmitScore = null;
      }
    } else if (match.status === 'completed' || match.status === 'verified') {
      modalState = 'view';
      // Use the score details from the dashboard data
      existingScores = match.scoreDetails || {
        team1_set1: '',
        team1_set2: '',
        team1_set3: '',
        team2_set1: '',
        team2_set2: '',
        team2_set3: ''
      };
    }

    // Determine if this is an opponent view (scores should be swapped)
    // IMPORTANT: isOpponentView should be based on fixture position (team1_id vs team2_id), NOT submitter
    // The modal displays: top = "Your Team", bottom = "Opponent"
    // DB scores are in fixture order: team1_score_set* = fixture team1_id, team2_score_set* = fixture team2_id
    // If viewer is fixture team2, we need to swap for display (so their scores appear at top)
    let isOpponentView = false;
    if (pendingVerification || match.status === 'completed' || match.status === 'verified' || match.status === 'disputed') {
      // Check if current team is fixture team2 (opponent position)
      // If they are team2, swap scores so their scores appear at top in modal
      const currentTeamId = String(dashboard?.team?.id);
      // Prefer fixture ids from pendingVerification if present; fallback to match
      const fixtureTeam1Id = String(
        (pendingVerification && (pendingVerification.team1_id || pendingVerification.team1Id))
        || match.team1_id || match.team1Id || ''
      );
      isOpponentView = fixtureTeam1Id !== currentTeamId; // If not team1, then team2 → swap for display
    }

    setSelectedScoreMatch({
      ...match,
      modalState,
      existingScores,
      onVerifyScore,
      onDisputeScore,
      onResubmitScore,
      isOpponentView
    });
    setScoreModalOpen(true);
    setLoadingOpponentMembers(true);
    
    try {
      // Player JWT: /scores/* requires team_id (query); same context as chat + dashboard APIs
      const teamIdForScores = effectiveTeamId || dashboard?.team?.id;
      const response = await scoreAPI.getOpponentTeamMembers(match.id, teamIdForScores || undefined);
      if (response.success) {
        setOpponentMembers(response.data);
      } else {
        setOpponentMembers([]);
      }
    } catch (error) {
      setOpponentMembers([]);
    } finally {
      setLoadingOpponentMembers(false);
    }
  };

  const handleCloseScore = () => {
    setScoreModalOpen(false);
    setSelectedScoreMatch(null);
    setOpponentMembers([]);
    setLoadingOpponentMembers(false);
  };

  // Handle match details modal
  const handleOpenMatchDetails = (match) => {
    setSelectedDetailsMatch(match);
    setMatchDetailsModalOpen(true);
  };

  const handleCloseMatchDetails = () => {
    setMatchDetailsModalOpen(false);
    setSelectedDetailsMatch(null);
  };

  // Handle dispute score submission
  const handleDisputeSubmission = async (disputeMessage) => {
    if (!selectedDisputeMatch) return;

    const isDisputeAlreadyDone = (err) => {
      const msg = String(err?.message || '').toLowerCase();
      return msg.includes('already processed') || msg.includes('not found');
    };

    setDisputeLoading(true);
    try {
      const scoreConfirmationId =
        selectedDisputeMatch.pendingVerificationId ||
        dashboard?.pendingScoreVerifications?.find(
          (v) => String(v.matchId) === String(selectedDisputeMatch.id)
        )?.id;

      if (!scoreConfirmationId) {
        throw new Error('No pending verification found for this match');
      }

      await scoreAPI.verifyScore(scoreConfirmationId, 'dispute', disputeMessage);

      toast.success(t('teamDashboardPage.toastScoreDisputed'));
      refreshScoreRelatedQueries();
      setDisputeModalOpen(false);
      setSelectedDisputeMatch(null);
    } catch (error) {
      if (isDisputeAlreadyDone(error)) {
        toast.success(t('teamDashboardPage.toastScoreDisputed'));
        refreshScoreRelatedQueries();
        setDisputeModalOpen(false);
        setSelectedDisputeMatch(null);
      } else {
        toast.error(error.message || t('teamDashboardPage.toastDisputeScoreFail'));
      }
    } finally {
      setDisputeLoading(false);
    }
  };

  const handleCloseDisputeModal = () => {
    setDisputeModalOpen(false);
    setSelectedDisputeMatch(null);
  };

  // Handle opening resubmit modal
  const handleOpenResubmitModal = (match) => {
    setSelectedResubmitMatch(match);
    setResubmitModalOpen(true);
  };

  const handleCloseResubmitModal = () => {
    setResubmitModalOpen(false);
    setSelectedResubmitMatch(null);
  };

  // Handle match details submission
  const handleMatchDetailsSubmission = async (formData) => {
    if (!selectedDetailsMatch) return;

    
    setMatchDetailsLoading(true);
    try {
      // Use service method instead of direct fetch
      const result = await matchesAPI.updateMatchDetails(
        selectedDetailsMatch.id,
        formData,
        effectiveTeamId || undefined
      );
      
      if (result.success) {
        toast.success(t('teamDashboardPage.toastMatchDetailsOk'));
        
        // Update the selected match with the new data immediately
        if (selectedDetailsMatch) {
          setSelectedDetailsMatch(prev => ({
            ...prev,
            courtDetails: {
              court_name: formData.court_name,
              court_location: formData.court_location,
              match_time: formData.match_time,
              match_date: formData.match_date,
              details_submitted_at: new Date().toISOString()
            },
            match_notes: formData.match_notes || prev.match_notes || ''
            // Don't overwrite venue with court_name - they are separate fields
          }));
        }
        
        // Refresh dashboard data in background
        queryClient.invalidateQueries('dashboardOverview');
        queryClient.invalidateQueries('matchResults'); // Refresh results section
        handleCloseMatchDetails();
      } else {
        toast.error(result.message || t('teamDashboardPage.toastMatchDetailsFail'));
      }
    } catch (error) {
      toast.error(t('teamDashboardPage.toastMatchDetailsFail'));
    } finally {
      setMatchDetailsLoading(false);
    }
  };

  const handleScoreSubmission = async (scores) => {
    const teamIdForScores = effectiveTeamId || dashboard?.team?.id;
    const isAlreadySubmittedError = (err) => {
      const msg = String(err?.message || '').toLowerCase();
      const apiErr = String(err?.response?.data?.error || '').toLowerCase();
      return msg.includes('already submitted') || apiErr.includes('already submitted');
    };
    try {
      const result = await scoreAPI.submitScore(
        selectedScoreMatch.id,
        scores,
        teamIdForScores || undefined
      );
      toast.success(t('teamDashboardPage.toastScoreSubmitted'));
      
      // Refresh dashboard data to update button states
      queryClient.invalidateQueries('dashboardOverview');
      queryClient.invalidateQueries('matchResults'); // Refresh results section
      
      handleCloseScore();
      return result;
    } catch (error) {
      if (isAlreadySubmittedError(error)) {
        toast.success(t('teamDashboardPage.toastScoreAlreadyRecorded'));
        queryClient.invalidateQueries('dashboardOverview');
        queryClient.invalidateQueries('matchResults');
        handleCloseScore();
        return null;
      }
      toast.error(error.message || t('teamDashboardPage.toastSubmitScoreFail'));
      throw error;
    }
  };


  // Form handling
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues
  } = useForm();

  // Update member mutation
  const updateMemberMutation = useMutation(
    ({ memberId, data, seasonId, teamId }) => {
      return membersAPI.updateMember(memberId, data, seasonId, teamId);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('teamMembers');
        toast.success(t('teamDashboardPage.toastMemberUpdated'));
        setShowEditForm(false);
        setEditingMember(null);
        resetEditForm();
      },
      onError: (error) => {
        // Show detailed validation errors if available
        if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
          const errorMessages = error.response.data.errors.join(', ');
          toast.error(t('teamDashboardPage.validationErrorPrefix', { details: errorMessages }));
        } else {
          const errorMessage =
            error.response?.data?.message ||
            error.message ||
            t('teamDashboardPage.validationFailUpdateMember');
          toast.error(errorMessage);
        }
      }
    }
  );

  const addMemberMutation = useMutation(({ data, seasonId }) => membersAPI.addMember(data, seasonId), {
    onSuccess: () => {
      queryClient.invalidateQueries('teamMembers');
      queryClient.invalidateQueries('dashboardOverview');
      toast.success(t('teamDashboardPage.toastMemberAdded'));
      setShowAddForm(false);
      reset();
      setSelectedSkillRange('');
    },
    onError: (error) => {
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const errorMessages = error.response.data.errors.join(', ');
        toast.error(t('teamDashboardPage.validationErrorPrefix', { details: errorMessages }));
      } else {
        const errorMessage =
          error.response?.data?.message || error.message || t('teamDashboardPage.validationFailAddMember');
        toast.error(errorMessage);
      }
    },
  });

  const removeMemberMutation = useMutation(
    ({ memberId, body, teamId }) => membersAPI.removeMember(memberId, body, teamId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('teamMembers');
        queryClient.invalidateQueries('dashboardOverview');
        toast.success(t('teamDashboardPage.toastMemberRemoved'));
        setRemoveModalOpen(false);
        setRemoveTarget(null);
        setRemovePassword('');
        setRemoveReason('');
      },
      onError: (error) => {
        const errorMessage =
          error.response?.data?.message || error.message || 'Failed to remove team member';
        toast.error(errorMessage);
      },
    }
  );

  const onSubmit = (data) => {
    const memberData = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      homeClub: data.homeClub || '',
      instagramHandle: data.instagramHandle || '',
      phoneNumber: data.phoneNumber && typeof data.phoneNumber === 'string' ? data.phoneNumber.trim() : data.phoneNumber,
      sex: data.sex && typeof data.sex === 'string' ? data.sex.trim() : data.sex,
      dateOfBirth: data.dateOfBirth && typeof data.dateOfBirth === 'string' ? data.dateOfBirth.trim() : data.dateOfBirth,
    };

    // Incomplete roster (0–1 players): must be able to set skill on new members even when
    // season skill-edit window is closed. Full team uses season policy only.
    const rosterCountForAdd = membersData?.data?.members?.length ?? 0;
    const canSetSkillOnNewMember = rosterCountForAdd < 2;

    if (!canSetSkillOnNewMember && !canEditSkillLevel()) {
      toast.error(skillLevelEdit?.reason || t('teamDashboardPage.toastSkillClosed'));
      return;
    }

    if (canSetSkillOnNewMember || canEditSkillLevel()) {
      if (!selectedSkillRange) {
        toast.error(t('teamDashboardPage.toastSelectSkillRange'));
        return;
      }

      let skillLevel;
      if (data.exactSkillLevel && data.exactSkillLevel.trim() !== '') {
        const exactValue = parseFloat(data.exactSkillLevel);
        const [min, max] = selectedSkillRange.split('-').map(parseFloat);

        if (exactValue < min || exactValue > max) {
          toast.error(t('teamDashboardPage.toastExactSkillBetween', { min, max }));
          return;
        }

        skillLevel = exactValue;
      } else {
        const [min, max] = selectedSkillRange.split('-').map(parseFloat);
        skillLevel = (min + max) / 2;
      }

      memberData.skillLevel = skillLevel;
    } else {
      memberData.skillLevel = null;
    }
    addMemberMutation.mutate({
      data: memberData,
      seasonId: operationSeasonId || undefined
    });
  };

  const resetForm = () => {
    reset();
    setSelectedSkillRange('');
  };

  const resetEditForm = () => {
    setEditSkillRange('');
  };

  const onEditSubmit = (data) => {
    if (!editingMember) {
      toast.error(t('teamDashboardPage.toastNoMemberSelected'));
      return;
    }

    if (isPlayerView && editingMember.is_self) {
      let newSkillLevel = null;
      if (editSkillRange && data.exactSkillLevel && data.exactSkillLevel.trim() !== '') {
        const exactValue = parseFloat(data.exactSkillLevel);
        const [min, max] = editSkillRange.split('-').map(parseFloat);
        if (!isNaN(exactValue) && exactValue >= min && exactValue <= max) {
          newSkillLevel = exactValue;
        }
      } else if (editSkillRange) {
        const [min, max] = editSkillRange.split('-').map(parseFloat);
        newSkillLevel = (min + max) / 2;
      }

      const existingEffective =
        editingMember.roster_skill_level != null && editingMember.roster_skill_level !== ''
          ? parseFloat(editingMember.roster_skill_level)
          : editingMember.profile_skill_level != null && editingMember.profile_skill_level !== ''
            ? parseFloat(editingMember.profile_skill_level)
            : editingMember.skill_level != null && editingMember.skill_level !== ''
              ? parseFloat(editingMember.skill_level)
              : null;

      const isSkillLevelChanging =
        newSkillLevel !== null &&
        (existingEffective === null ||
          Number.isNaN(existingEffective) ||
          Math.abs(newSkillLevel - existingEffective) > 0.01);

      if (!canEditSkillLevel()) {
        if (isSkillLevelChanging) {
          toast.error(skillLevelEdit?.reason || t('teamDashboardPage.toastSkillClosed'));
        }
        return;
      }

      if (!editSkillRange || newSkillLevel == null) {
        toast.error(t('teamDashboardPage.toastSelectRosterSkillRange'));
        return;
      }

      updateMemberMutation.mutate({
        memberId: editingMember.id,
        data: { rosterSkillLevel: newSkillLevel },
        seasonId: operationSeasonId || undefined,
        teamId: effectiveTeamId || undefined,
      });
      return;
    }

    // Build member data object (team-login legacy roster)
    const memberData = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      homeClub: data.homeClub || '',
      instagramHandle: data.instagramHandle || '',
    };
    
    // Handle phone number
    const phoneFromForm = data.phoneNumber && typeof data.phoneNumber === 'string' && data.phoneNumber.trim();
    const phoneFromMember = editingMember.phone_number || '';
    memberData.phoneNumber = phoneFromForm ? phoneFromForm : phoneFromMember;
    
    // Handle sex
    const sexFromForm = data.sex && typeof data.sex === 'string' && data.sex.trim();
    const sexFromMember = editingMember.sex || '';
    memberData.sex = sexFromForm ? sexFromForm : sexFromMember;
    
    // Handle date of birth
    const dobFromForm = data.dateOfBirth && typeof data.dateOfBirth === 'string' && data.dateOfBirth.trim();
    let dobFromMember = null;
    if (editingMember.date_of_birth) {
      if (typeof editingMember.date_of_birth === 'string') {
        dobFromMember = editingMember.date_of_birth.slice(0, 10);
      } else if (editingMember.date_of_birth instanceof Date) {
        dobFromMember = editingMember.date_of_birth.toISOString().split('T')[0];
      }
    }
    memberData.dateOfBirth = dobFromForm ? dobFromForm : (dobFromMember || '');
    
    memberData.position = editingMember.position || 'member';

    // CRITICAL: Check if skill level is locked AND user is trying to CHANGE it
    // We need to check if the skill level is actually changing, not just if a range is selected
    
    // Calculate what the new skill level would be
    let newSkillLevel = null;
    if (editSkillRange && data.exactSkillLevel && data.exactSkillLevel.trim() !== '') {
      const exactValue = parseFloat(data.exactSkillLevel);
      const [min, max] = editSkillRange.split('-').map(parseFloat);
      if (!isNaN(exactValue) && exactValue >= min && exactValue <= max) {
        newSkillLevel = exactValue;
      }
    } else if (editSkillRange) {
      const [min, max] = editSkillRange.split('-').map(parseFloat);
      newSkillLevel = (min + max) / 2;
    }
    
    // Check if skill level is actually changing
    const existingSkillLevel = editingMember.skill_level !== null && editingMember.skill_level !== undefined 
      ? parseFloat(editingMember.skill_level) 
      : null;
    const isSkillLevelChanging = newSkillLevel !== null && 
                                  existingSkillLevel !== null &&
                                  Math.abs(newSkillLevel - existingSkillLevel) > 0.01; // Account for floating point precision
    
    if (!canEditSkillLevel() && isSkillLevelChanging) {
      // User is trying to CHANGE skill level when locked
      toast.error(skillLevelEdit?.reason || t('teamDashboardPage.toastSkillClosed'));
      return;
    }
    
    // Only update skill level if editing is allowed
    if (canEditSkillLevel()) {
      // If skill range is selected, use it; otherwise preserve existing skill level
      if (editSkillRange) {
        // Use the exact skill level value if available, otherwise use the middle of the selected range
        let skillLevel;
        if (data.exactSkillLevel && data.exactSkillLevel.trim() !== '') {
          const exactValue = parseFloat(data.exactSkillLevel);
          const [min, max] = editSkillRange.split('-').map(parseFloat);
          
          // Validate that the exact value is within the selected range
          if (exactValue < min || exactValue > max) {
            toast.error(t('teamDashboardPage.toastExactSkillBetween', { min, max }));
            return;
          }
          
          skillLevel = exactValue;
        } else {
          // Use the middle of the selected range as fallback
          const [min, max] = editSkillRange.split('-').map(parseFloat);
          skillLevel = (min + max) / 2;
        }
        
        memberData.skillLevel = skillLevel;
      } else {
        // No range selected - preserve existing skill level (allows old values to remain)
        memberData.skillLevel = editingMember.skill_level || null;
      }
    } else {
      // Skill level editing is locked - explicitly preserve existing skill level
      // Send the existing value so backend knows we're not trying to change it
      memberData.skillLevel = editingMember.skill_level !== null && editingMember.skill_level !== undefined 
        ? editingMember.skill_level 
        : null;
    }
    
    try {
      updateMemberMutation.mutate({
        memberId: editingMember.id,
        data: memberData,
        seasonId: operationSeasonId || undefined,
        teamId: effectiveTeamId || undefined,
      });
    } catch (error) {
      toast.error(t('teamDashboardPage.toastUpdateFail'));
    }
  };

  const handleEditMember = (member) => {
    // Team editing is always available
    
    setEditingMember(member);
    
    // Reset form first
    reset();
    
    // Set the skill range from team roster override, else profile, else effective display
    const skillLevel =
      member.roster_skill_level ??
      member.profile_skill_level ??
      member.skill_level;
    if (skillLevel && !isNaN(skillLevel)) {
      // New ranges
      if (skillLevel >= 1.0 && skillLevel <= 2.5) {
        setEditSkillRange('1.0-2.5');
      } else if (skillLevel >= 3.0 && skillLevel <= 3.5) {
        setEditSkillRange('3.0-3.5');
      } else if (skillLevel >= 4.0 && skillLevel <= 4.5) {
        setEditSkillRange('4.0-4.5');
      } else if (skillLevel >= 5.0 && skillLevel <= 5.5) {
        setEditSkillRange('5.0-5.5');
      } else if (skillLevel >= 6.0 && skillLevel <= 6.5) {
        setEditSkillRange('6.0-6.5');
      } else if (skillLevel >= 7.0 && skillLevel <= 7.5) {
        setEditSkillRange('7.0-7.5');
      } else if (skillLevel >= 8.0 && skillLevel <= 10.0) {
        setEditSkillRange('8.0-10.0');
      } else {
        // Old skill levels that fall outside new ranges (e.g., 4.7, 5.8, 6.9)
        // Map to closest range for display, but preserve original value
        if (skillLevel >= 2.6 && skillLevel <= 2.9) {
          setEditSkillRange('1.0-2.5'); // Map to closest range
        } else if (skillLevel >= 3.6 && skillLevel <= 3.9) {
          setEditSkillRange('3.0-3.5'); // Map to closest range
        } else if (skillLevel >= 4.6 && skillLevel <= 4.9) {
          setEditSkillRange('4.0-4.5'); // Map to closest range
        } else if (skillLevel >= 5.6 && skillLevel <= 5.9) {
          setEditSkillRange('5.0-5.5'); // Map to closest range
        } else if (skillLevel >= 6.6 && skillLevel <= 6.9) {
          setEditSkillRange('6.0-6.5'); // Map to closest range
        } else if (skillLevel >= 7.6 && skillLevel <= 7.9) {
          setEditSkillRange('7.0-7.5'); // Map to closest range
        } else {
          setEditSkillRange(''); // Unknown range, preserve existing value
        }
      }
    } else {
      setEditSkillRange(''); // No skill level
    }
    
    // Set default values in the form
    setValue('firstName', member.first_name || '');
    setValue('lastName', member.last_name || '');
    setValue('email', member.email || '');
    setValue('homeClub', member.home_club || '');
    setValue('instagramHandle', member.instagram_handle || '');
    setValue('phoneNumber', member.phone_number || '');
    setValue('sex', member.sex || '');
    setValue('dateOfBirth', member.date_of_birth ? member.date_of_birth.slice(0, 10) : '');
    const rosterOrDisplay =
      member.roster_skill_level != null && member.roster_skill_level !== ''
        ? member.roster_skill_level
        : member.skill_level;
    setValue(
      'exactSkillLevel',
      rosterOrDisplay != null && rosterOrDisplay !== '' && typeof rosterOrDisplay === 'number'
        ? rosterOrDisplay.toFixed(1)
        : rosterOrDisplay || ''
    );
    
    setShowEditForm(true);
  };


  // Handle payment success/cancellation from URL params
  useEffect(() => {
    const payment = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (payment === 'success' && sessionId) {
      setPaymentStatus('success');
      setShowAlert(true);
      const p = new URLSearchParams(searchParams);
      p.delete('payment');
      p.delete('session_id');
      navigate(`/dashboard?${p.toString()}`, { replace: true });
    } else if (payment === 'cancelled') {
      setPaymentStatus('cancelled');
      setShowAlert(true);
      const p = new URLSearchParams(searchParams);
      p.delete('payment');
      p.delete('session_id');
      navigate(`/dashboard?${p.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);

  // When embedded, keep activeSection in sync with embeddedSection
  useEffect(() => {
    if (embedded && embeddedSection && VALID_SECTIONS.includes(embeddedSection) && embeddedSection !== activeSection) {
      setActiveSection(embeddedSection);
    }
  }, [embedded, embeddedSection, activeSection]);

  // Handle section parameter from URL changes (e.g., browser back/forward or direct URL) — skip when embedded
  useEffect(() => {
    if (embedded) return;
    const section = searchParams.get('section');
    if (section && VALID_SECTIONS.includes(section) && section !== activeSection) {
      const timer = setTimeout(() => setActiveSection(section), 0);
      return () => clearTimeout(timer);
    }
  }, [embedded, searchParams, activeSection]);

  // Fetch team members (pass team_id when viewing as player)
  const { data: membersData, isLoading: membersLoading, refetch: refetchMembers } = useQuery(
    ['teamMembers', effectiveTeamId, effectiveSeasonId || 'auto'],
    ({ signal }) =>
      membersAPI.getMembers(effectiveTeamId || undefined, effectiveSeasonId || undefined, {
        signal,
      }),
    {
      enabled: !!user && (!isPlayerView || !!effectiveTeamId),
      retry: false,
      refetchOnWindowFocus: true,
      staleTime: 30000,
      cacheTime: 60000
    }
  );

  const { data: schedulingInboxData } = useQuery(
    ['matchSchedulingInbox', user?.id],
    ({ signal }) => matchSchedulingAPI.getInbox({ signal }),
    {
      enabled: Boolean(user && isPlayerView && !embedded),
      staleTime: 60000,
      refetchOnWindowFocus: true,
    }
  );

  const schedulingNeedsMap = useMemo(() => {
    const m = new Map();
    for (const row of schedulingInboxData?.matches || []) {
      m.set(String(row.league_match_id), row);
    }
    return m;
  }, [schedulingInboxData]);

  // Get skill level lock status from members data (must be after membersData query)
  const skillLevelLocked = membersData?.data?.skillLevelLocked || false;
  const skillLevelEdit = membersData?.data?.skillLevelEdit || { allowed: !skillLevelLocked, reason: '' };
  const operationSeasonId = skillLevelEdit?.seasonId || effectiveSeasonId || '';
  const viewerIsCaptain = membersData?.data?.viewerIsCaptain || false;
  
  // Skill level editing is season-scoped. Lock-status is still returned for visibility/debugging.
  const canEditSkillLevel = () => {
    return skillLevelEdit?.allowed !== false;
  };

  // Track if we've done the initial redirect (only redirect once on first load) — skip when embedded
  const hasInitialRedirectedRef = React.useRef(false);

  // When embedded, sync season selectors to embeddedSeasonId so all tabs use the same season
  useEffect(() => {
    if (embedded && embeddedSeasonId) {
      setSelectedResultsSeason(embeddedSeasonId);
      setSelectedStatsSeason(embeddedSeasonId);
      setSelectedRankingsSeason(embeddedSeasonId);
      setSelectedScheduleSeason(embeddedSeasonId);
    }
  }, [embedded, embeddedSeasonId]);
  
  // Redirect to team section if members < 2 ONLY on initial page load — skip when embedded
  useEffect(() => {
    if (embedded) return;
    if (!membersLoading && membersData?.data?.members && !hasInitialRedirectedRef.current) {
      const members = membersData.data.members || [];
      const currentSection = searchParams.get('section') || 'overview';
      if (members.length < 2 && (currentSection === 'overview' || !searchParams.get('section'))) {
        const params = new URLSearchParams(searchParams);
        params.set('section', 'team');
        navigate(`/dashboard?${params.toString()}`, { replace: true });
        setActiveSection('team');
        hasInitialRedirectedRef.current = true;
      } else {
        hasInitialRedirectedRef.current = true;
      }
    }
  }, [embedded, membersData, membersLoading, navigate, searchParams]);

  // Season selection state (declared early to use in useQuery hooks). When embedded, seed from embeddedSeasonId.
  const [selectedResultsSeason, setSelectedResultsSeason] = useState(() => (embedded && embeddedSeasonId) ? embeddedSeasonId : '');
  const [selectedStatsSeason, setSelectedStatsSeason] = useState(() => (embedded && embeddedSeasonId) ? embeddedSeasonId : '');
  const [selectedRankingsSeason, setSelectedRankingsSeason] = useState(() => (embedded && embeddedSeasonId) ? embeddedSeasonId : '');
  const [selectedScheduleSeason, setSelectedScheduleSeason] = useState(() => (embedded && embeddedSeasonId) ? embeddedSeasonId : '');
  const [selectedOverviewMatchesSeason, setSelectedOverviewMatchesSeason] = useState(() =>
    embedded && embeddedSeasonId ? embeddedSeasonId : ''
  );

  // Fetch dashboard overview data (pass team_id when viewing as player)
  const { data: dashboardData, isLoading: dashboardLoading, error: dashboardError } = useQuery(
    ['dashboardOverview', selectedStatsSeason, effectiveTeamId],
    ({ signal }) =>
      dashboardAPI.getOverview(selectedStatsSeason || undefined, effectiveTeamId || undefined, {
        signal,
      }),
    {
      enabled: !!user && (!isPlayerView || !!effectiveTeamId),
      retry: false,
      refetchOnWindowFocus: true,
      staleTime: 30000,
      cacheTime: 60000,
      onSuccess: () => {
        // Data received successfully
      },
      onError: () => {
        // Error handled by query
      }
    }
  );

  // Fetch schedule data separately (for schedule section)
  const { data: scheduleDashboardData, isLoading: scheduleDashboardLoading } = useQuery(
    ['dashboardSchedule', selectedScheduleSeason, effectiveTeamId],
    ({ signal }) =>
      dashboardAPI.getOverview(selectedScheduleSeason || undefined, effectiveTeamId || undefined, {
        signal,
      }),
    {
      enabled: !!user && !!selectedScheduleSeason && (!isPlayerView || !!effectiveTeamId),
      retry: false,
      refetchOnWindowFocus: true,
      staleTime: 30000,
      cacheTime: 60000,
      onSuccess: () => {
        // Data received successfully
      },
      onError: () => {
        // Error handled by query
      }
    }
  );
  
  const scheduleDashboard = scheduleDashboardData?.data;

  const needsSeparateOverviewMatches =
    !!user &&
    !!selectedOverviewMatchesSeason &&
    selectedOverviewMatchesSeason !== selectedStatsSeason &&
    (!isPlayerView || !!effectiveTeamId);

  const { data: overviewMatchesOnlyData, isLoading: overviewMatchesOnlyLoading } = useQuery(
    ['dashboardOverviewMatches', selectedOverviewMatchesSeason, effectiveTeamId],
    ({ signal }) =>
      dashboardAPI.getOverview(selectedOverviewMatchesSeason || undefined, effectiveTeamId || undefined, {
        signal,
      }),
    {
      enabled: needsSeparateOverviewMatches,
      retry: false,
      refetchOnWindowFocus: true,
      staleTime: 30000,
      cacheTime: 60000,
    }
  );

  const overviewMatchesDashboard = needsSeparateOverviewMatches
    ? overviewMatchesOnlyData?.data
    : dashboardData?.data;
  const overviewMatchesListLoading = needsSeparateOverviewMatches ? overviewMatchesOnlyLoading : dashboardLoading;

  // Fetch seasons for season selector (pass team_id when player; campaign_id when embedded from tournaments)
  const { data: seasonsData } = useQuery(
    ['dashboardSeasons', effectiveTeamId, embedded ? embeddedCampaignId : null],
    ({ signal }) =>
      dashboardAPI.getSeasons(
        effectiveTeamId || undefined,
        embedded && embeddedCampaignId ? embeddedCampaignId : undefined,
        { signal }
      ),
    {
      enabled: !!user && (!isPlayerView || !!effectiveTeamId),
      retry: false,
      staleTime: 300000, // Cache for 5 minutes
      cacheTime: 600000,
      onSuccess: (data) => {
          if (data?.success && data?.data && data.data.length > 0) {
            const fromUrl = effectiveSeasonId && data.data.some((s) => s.id === effectiveSeasonId) ? effectiveSeasonId : null;
            const activeCandidates = data.data.filter((s) => s.is_active_by_date);
            const preferredActive =
              activeCandidates.length > 0
                ? [...activeCandidates].sort(
                    (a, b) =>
                      new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
                  )[0]
                : null;
            const defaultSeason = fromUrl
              ? { id: fromUrl }
              : preferredActive
                ? { id: preferredActive.id }
                : { id: data.data[0].id };
            if (
              !selectedResultsSeason &&
              !selectedStatsSeason &&
              !selectedRankingsSeason &&
              !selectedScheduleSeason &&
              !selectedOverviewMatchesSeason
            ) {
              setSelectedResultsSeason(defaultSeason.id);
              setSelectedStatsSeason(defaultSeason.id);
              setSelectedRankingsSeason(defaultSeason.id);
              setSelectedScheduleSeason(defaultSeason.id);
              setSelectedOverviewMatchesSeason(defaultSeason.id);
            } else {
              if (!selectedResultsSeason) {
                setSelectedResultsSeason(defaultSeason.id);
              }
              if (!selectedStatsSeason) {
                setSelectedStatsSeason(defaultSeason.id);
              }
              if (!selectedRankingsSeason) {
                setSelectedRankingsSeason(defaultSeason.id);
              }
              if (!selectedScheduleSeason) {
                setSelectedScheduleSeason(defaultSeason.id);
              }
              if (!selectedOverviewMatchesSeason) {
                setSelectedOverviewMatchesSeason(defaultSeason.id);
              }
            }
          }
      }
    }
  );

  // Fetch match results (pass team_id when player)
  const { data: resultsData, isLoading: resultsLoading, error: resultsError } = useQuery(
    ['matchResults', selectedResultsSeason, effectiveTeamId],
    ({ signal }) =>
      dashboardAPI.getResults(selectedResultsSeason || undefined, effectiveTeamId || undefined, {
        signal,
      }),
    {
      enabled: !!user && activeSection === 'results' && (!isPlayerView || !!effectiveTeamId),
      retry: false,
      refetchOnWindowFocus: true,
      staleTime: 30000,
      cacheTime: 60000
    }
  );

  const results = resultsData?.data || [];
  const filterOptions = resultsData?.filters || { divisions: [], cantons: [] };

  const orderedResults = React.useMemo(() => {
    return [...results].sort((a, b) => {
      // Sort by match_date and match_time (upcoming matches first, then past matches)
      // Priority: matchDateTime (combines date and time) > verified_at > submitted_at
      const getSortTime = (match) => {
        if (match.matchDateTime !== null && match.matchDateTime !== undefined) {
          return match.matchDateTime;
        }
        if (match.verified_at) {
          return new Date(match.verified_at).getTime();
        }
        if (match.submitted_at) {
          return new Date(match.submitted_at).getTime();
        }
        return 0;
      };
      
      const dateA = getSortTime(a);
      const dateB = getSortTime(b);
      return dateA - dateB; // ascending order (upcoming first, then past)
    });
  }, [results]);

  // Division hierarchy order (highest to lowest)
  const divisionHierarchy = [
    'Elite',
    'Advanced +',
    'Confirmed Advanced',
    'Advanced',
    'Intermediate +',
    'Intermediate',
    'Beginner +',
    'Beginner'
  ];

  // Extract unique divisions and cantons from actual results (only show filters for matches that exist)
  const availableDivisions = React.useMemo(() => {
    const uniqueDivisions = new Set();
    results.forEach(match => {
      if (match.division_name && match.division_name !== 'N/A') {
        uniqueDivisions.add(match.division_name);
      }
    });
    // Sort by custom hierarchy order
    return Array.from(uniqueDivisions).sort((a, b) => {
      const indexA = divisionHierarchy.indexOf(a);
      const indexB = divisionHierarchy.indexOf(b);
      
      // If both are in hierarchy, sort by hierarchy order
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }
      // If only one is in hierarchy, prioritize it
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      // If neither is in hierarchy, sort alphabetically
      return a.localeCompare(b);
    });
  }, [results]);

  const availableCantons = React.useMemo(() => {
    const uniqueCantons = new Set();
    results.forEach(match => {
      if (match.canton && match.canton !== 'N/A') {
        uniqueCantons.add(match.canton);
      }
    });
    return Array.from(uniqueCantons).sort();
  }, [results]);

  // Results search + pagination state
  const [resultsSearchTerm, setResultsSearchTerm] = useState('');
  const [resultsPage, setResultsPage] = useState(1);
  const [resultsCantonFilter, setResultsCantonFilter] = useState('');
  const [resultsDivisionFilter, setResultsDivisionFilter] = useState('');
  const [resultsTeamFilter, setResultsTeamFilter] = useState('');
  const prevActiveSectionRef = useRef(null);
  const prevFiltersRef = useRef({ searchTerm: '', cantonFilter: '', divisionFilter: '', teamFilter: '' });
  const [resultsPerPage, setResultsPerPage] = useState(DESKTOP_RESULTS_PER_PAGE);

  useEffect(() => {
    const updatePerPage = () => {
      setResultsPerPage(window.innerWidth <= 768 ? MOBILE_RESULTS_PER_PAGE : DESKTOP_RESULTS_PER_PAGE);
    };

    updatePerPage();
    window.addEventListener('resize', updatePerPage);
    return () => window.removeEventListener('resize', updatePerPage);
  }, []);

  // Filter results based on search term, division, canton, and selected team
  // Handle canton name variations (e.g., "Aargau" vs "AG", "Basel-Landschaft" vs "BL", etc.)
  const filteredResults = React.useMemo(() => {
    const searchLower = resultsSearchTerm.toLowerCase().trim();
    return orderedResults.filter(match => {
      const matchesSearch = !searchLower || (
        match.team1_name.toLowerCase().includes(searchLower) ||
        match.team2_name.toLowerCase().includes(searchLower) ||
        match.division_name?.toLowerCase().includes(searchLower) ||
        match.canton?.toLowerCase().includes(searchLower)
      );
      
      // Division filter: exact match with division name
      const matchesDivision = !resultsDivisionFilter || match.division_name === resultsDivisionFilter;
      
      // Canton filter: exact match (backend normalizes canton names)
      const matchesCanton = !resultsCantonFilter || (match.canton || '') === resultsCantonFilter;

      // Team filter: match either team name exactly
      const matchesTeam =
        !resultsTeamFilter ||
        match.team1_name === resultsTeamFilter ||
        match.team2_name === resultsTeamFilter;
      
      return matchesSearch && matchesCanton && matchesDivision && matchesTeam;
    });
  }, [orderedResults, resultsSearchTerm, resultsCantonFilter, resultsDivisionFilter, resultsTeamFilter]);

  const chunkedResults = React.useMemo(() => {
    if (filteredResults.length === 0) return [];

    const chunks = [];
    for (let i = 0; i < filteredResults.length; i += resultsPerPage) {
      chunks.push(filteredResults.slice(i, i + resultsPerPage));
    }
    return chunks;
  }, [filteredResults, resultsPerPage]);

  const totalPages = Math.max(1, chunkedResults.length);

  // Find the page with the latest score submission
  // This should be based on when the score was SUBMITTED/VERIFIED in the system, not the match date/time
  const findPageWithLatestScore = React.useMemo(() => {
    if (filteredResults.length === 0 || resultsPerPage === 0) return null;
    
    let latestScoreTime = 0;
    let latestScoreIndex = -1;
    
    // Iterate through filtered results to find the match with the latest score submission
    filteredResults.forEach((match, index) => {
      // Check if match has scores (using hasScores flag or checking if scores string exists)
      const hasScores = match.hasScores || (match.scores && match.scores.trim() !== '');
      
      if (hasScores) {
        // Use verified_at or submitted_at (when score was submitted/verified in system)
        // verified_at takes priority as it means the score was confirmed
        let scoreTime = 0;
        if (match.verified_at) {
          const verifiedTime = new Date(match.verified_at).getTime();
          if (!isNaN(verifiedTime)) {
            scoreTime = verifiedTime;
          }
        } else if (match.submitted_at) {
          const submittedTime = new Date(match.submitted_at).getTime();
          if (!isNaN(submittedTime)) {
            scoreTime = submittedTime;
          }
        }
        
        // Update if this is the most recent score submission (by submission/verification time)
        if (scoreTime > latestScoreTime && scoreTime > 0) {
          latestScoreTime = scoreTime;
          latestScoreIndex = index;
        }
      }
    });
    
    // Calculate page number from index (1-based)
    if (latestScoreIndex >= 0) {
      const pageNumber = Math.floor(latestScoreIndex / resultsPerPage) + 1;
      return pageNumber;
    }
    
    return null;
  }, [filteredResults, resultsPerPage]);

  // Track if we've already set the initial page for this results load
  const hasSetInitialPageRef = useRef(false);
  
  // Store the default page (page with latest score when no filters are active)
  const defaultPageRef = useRef(null);
  
  // Calculate default page from unfiltered results (when no filters are active)
  const findDefaultPageWithLatestScore = React.useMemo(() => {
    // Only calculate when no filters are active
    if (resultsSearchTerm || resultsCantonFilter || resultsDivisionFilter || resultsTeamFilter) {
      return null;
    }
    
    if (orderedResults.length === 0 || resultsPerPage === 0) return null;
    
    let latestScoreTime = 0;
    let latestScoreIndex = -1;
    
    // Iterate through unfiltered results to find the match with the latest score submission
    orderedResults.forEach((match, index) => {
      const hasScores = match.hasScores || (match.scores && match.scores.trim() !== '');
      
      if (hasScores) {
        let scoreTime = 0;
        if (match.verified_at) {
          const verifiedTime = new Date(match.verified_at).getTime();
          if (!isNaN(verifiedTime)) {
            scoreTime = verifiedTime;
          }
        } else if (match.submitted_at) {
          const submittedTime = new Date(match.submitted_at).getTime();
          if (!isNaN(submittedTime)) {
            scoreTime = submittedTime;
          }
        }
        
        if (scoreTime > latestScoreTime && scoreTime > 0) {
          latestScoreTime = scoreTime;
          latestScoreIndex = index;
        }
      }
    });
    
    if (latestScoreIndex >= 0) {
      const pageNumber = Math.floor(latestScoreIndex / resultsPerPage) + 1;
      return pageNumber;
    }
    
    return null;
  }, [orderedResults, resultsPerPage, resultsSearchTerm, resultsCantonFilter, resultsDivisionFilter, resultsTeamFilter]);
  
  // Store the default page when it's calculated
  useEffect(() => {
    if (findDefaultPageWithLatestScore !== null) {
      defaultPageRef.current = findDefaultPageWithLatestScore;
    }
  }, [findDefaultPageWithLatestScore]);
  
  // Reset to page with latest score when search or filters change, or when switching to results section
  useEffect(() => {
    // Only process if we're on the results section
    if (activeSection === 'results') {
      const isSwitchingToResults = prevActiveSectionRef.current !== 'results';
      const isResultsDataReady = !resultsLoading && filteredResults.length > 0 && totalPages > 0;
      
      // Check if we're clearing the team filter (going from having a team filter to no team filter)
      const hadTeamFilter = prevFiltersRef.current.teamFilter && prevFiltersRef.current.teamFilter !== '';
      const hasNoTeamFilter = !resultsTeamFilter || resultsTeamFilter === '';
      const isClearingTeamFilter = hadTeamFilter && hasNoTeamFilter;
      
      // Check if no other filters are active
      const hasNoOtherFilters = !resultsSearchTerm && !resultsCantonFilter && !resultsDivisionFilter;
      
      // When switching to results OR when filters/search change, reset the flag
      if (isSwitchingToResults || resultsSearchTerm || resultsCantonFilter || resultsDivisionFilter || resultsTeamFilter) {
        hasSetInitialPageRef.current = false;
      }
      
      // Set the page when data is ready and we haven't set it yet
      if (isResultsDataReady && !hasSetInitialPageRef.current) {
        let targetPage;
        
        // If switching to results OR clearing team filter with no other filters, use default page
        // This makes "Show all results" behave exactly like clicking "Results" in sidebar
        if ((isSwitchingToResults || (isClearingTeamFilter && hasNoOtherFilters)) && hasNoOtherFilters) {
          // Use findDefaultPageWithLatestScore if available (calculated from unfiltered results), otherwise use stored ref
          targetPage = findDefaultPageWithLatestScore || defaultPageRef.current || totalPages;
        } else {
          // Otherwise, use findPageWithLatestScore (calculated from filteredResults)
          // If no latest score found, go to the last page (most recent matches)
          targetPage = findPageWithLatestScore || totalPages;
        }
        
        setResultsPage(targetPage);
        hasSetInitialPageRef.current = true;
      }
    }
    
    // Update refs to track previous state
    prevActiveSectionRef.current = activeSection;
    prevFiltersRef.current = {
      searchTerm: resultsSearchTerm,
      cantonFilter: resultsCantonFilter,
      divisionFilter: resultsDivisionFilter,
      teamFilter: resultsTeamFilter
    };
  }, [activeSection, totalPages, findPageWithLatestScore, findDefaultPageWithLatestScore, resultsLoading, filteredResults.length, resultsSearchTerm, resultsCantonFilter, resultsDivisionFilter, resultsTeamFilter]);

  const paginatedResults = React.useMemo(() => {
    if (chunkedResults.length === 0) return [];
    // Simple sequential pagination: page 1 = chunk[0], page 2 = chunk[1], etc.
    // Since orderedResults is sorted by match date/time (upcoming first), chunk[0] = upcoming matches, chunk[last] = past matches
    const chunkIndex = resultsPage - 1;
    return chunkedResults[chunkIndex] || [];
  }, [chunkedResults, resultsPage]);

  const members = membersData?.data?.members || [];
  /** Adding player 1 or 2: always allow skill picker even if season skill edits are closed. */
  const canSelectSkillWhenAddingMember = members.length < 2;
  const hasMembers = members.length > 0;
  const dashboard = dashboardData?.data;

  const findDashboardMatchById = useCallback(
    (matchId) => {
      if (!matchId) return null;
      const id = String(matchId);
      const lists = [
        dashboard?.recentMatches,
        overviewMatchesOnlyData?.data?.recentMatches,
        scheduleDashboard?.recentMatches,
      ];
      for (const list of lists) {
        if (!Array.isArray(list)) continue;
        const hit = list.find((m) => String(m.id) === id);
        if (hit) return hit;
      }
      return null;
    },
    [dashboard?.recentMatches, overviewMatchesOnlyData?.data?.recentMatches, scheduleDashboard?.recentMatches]
  );

  useEffect(() => {
    const mid = searchParams.get('open_match_chat');
    if (!mid || !effectiveTeamId) return;
    const m = findDashboardMatchById(mid);
    if (!m) return;
    setChatTeamIdOverride(null);
    setSelectedChatMatch(m);
    setChatModalOpen(true);
    const p = new URLSearchParams(searchParams);
    p.delete('open_match_chat');
    const nextSearch = p.toString();
    navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true });
  }, [
    searchParams,
    effectiveTeamId,
    findDashboardMatchById,
    navigate,
    location.pathname,
  ]);

  useEffect(() => {
    const mid = searchParams.get('open_match_schedule');
    if (!mid || !effectiveTeamId) return;
    const m = findDashboardMatchById(mid);
    if (!m) return;
    setScheduleTeamIdOverride(null);
    setSelectedScheduleMatch(m);
    setScheduleModalOpen(true);
    const p = new URLSearchParams(searchParams);
    p.delete('open_match_schedule');
    const nextSearch = p.toString();
    navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true });
  }, [searchParams, effectiveTeamId, findDashboardMatchById, navigate, location.pathname]);

  useEffect(() => {
    const mid = searchParams.get('open_opponent_sheet');
    if (!mid || !effectiveTeamId) return;
    const m = findDashboardMatchById(mid);
    if (!m) return;
    if (embedded && onEmbeddedSectionNavigate) {
      onEmbeddedSectionNavigate('overview');
    } else if (!embedded) {
      setActiveSection('overview');
    }
    openOpponentTeamSheet(m);
    const p = new URLSearchParams(searchParams);
    p.delete('open_opponent_sheet');
    const nextSearch = p.toString();
    navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true });
  }, [
    searchParams,
    effectiveTeamId,
    findDashboardMatchById,
    navigate,
    location.pathname,
    embedded,
    onEmbeddedSectionNavigate,
  ]);

  useEffect(() => {
    const mid = searchParams.get('open_match_score');
    if (!mid || !effectiveTeamId) return;
    const m = findDashboardMatchById(mid);
    if (!m) return;
    void handleOpenScore(m);
    const p = new URLSearchParams(searchParams);
    p.delete('open_match_score');
    const nextSearch = p.toString();
    navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true });
  }, [searchParams, effectiveTeamId, findDashboardMatchById, navigate, location.pathname]);

  useEffect(() => {
    const mid = searchParams.get('open_match_details');
    if (!mid || !effectiveTeamId) return;
    const m = findDashboardMatchById(mid);
    if (!m) return;
    handleOpenMatchDetails(m);
    const p = new URLSearchParams(searchParams);
    p.delete('open_match_details');
    const nextSearch = p.toString();
    navigate({ pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' }, { replace: true });
  }, [searchParams, effectiveTeamId, findDashboardMatchById, navigate, location.pathname]);
  
  // Debug logging for matches
  useEffect(() => {
    if (dashboard) {
    }
  }, [dashboard, selectedStatsSeason]);

  // Helper function to check if season is completed (date-based check)
  const isSeasonCompleted = (season) => {
    if (!season || !season.end_date) return false;
    const now = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const endDate = String(season.end_date).split('T')[0]; // Extract date part
    return now > endDate;
  };

  // Check if current season is completed
  const seasonCompleted = dashboard?.season ? isSeasonCompleted(dashboard.season) : false;
  
  // Check if selected schedule season is completed
  const selectedScheduleSeasonData = selectedScheduleSeason 
    ? seasonsData?.data?.find(s => s.id === selectedScheduleSeason)
    : null;
  const scheduleSeasonCompleted = selectedScheduleSeasonData 
    ? isSeasonCompleted(selectedScheduleSeasonData) 
    : false;

  const selectedOverviewMatchesSeasonData = selectedOverviewMatchesSeason
    ? seasonsData?.data?.find((s) => s.id === selectedOverviewMatchesSeason)
    : null;
  const overviewMatchesSeasonCompleted = selectedOverviewMatchesSeasonData
    ? isSeasonCompleted(selectedOverviewMatchesSeasonData)
    : false;

  // Get next season start date if available
  const nextSeason = seasonsData?.data?.find(s => {
    if (!s.start_date) return false;
    const now = new Date().toISOString().split('T')[0];
    const startDate = String(s.start_date).split('T')[0];
    return startDate > now;
  });

  // Phone number modal state
  const phoneNumberStatus = usePhoneNumberStatus(dashboard?.team?.id);
  
  // Debug phone number status
  // Monitor dashboard state changes
  useEffect(() => {
    // Dashboard state monitoring
  }, [user, dashboard, phoneNumberStatus.showPhoneModal, phoneNumberStatus.hasPhoneNumber, phoneNumberStatus.isLoading]);

  // Sync settings phone input with saved value - use phone-status API or dashboard team data
  useEffect(() => {
    const value = phoneNumberStatus?.phoneNumber || dashboard?.team?.phone_number;
    if (value && typeof value === 'string' && value.trim()) {
      setPhoneNumber(value.trim());
    }
  }, [phoneNumberStatus?.phoneNumber, dashboard?.team?.phone_number]);

  // Team member info modal state
  const [showTeamMemberInfoModal, setShowTeamMemberInfoModal] = React.useState(false);

  // Check if all team members have required fields (phone, DOB, sex)
  useEffect(() => {
    if (!membersLoading && members.length > 0 && dashboard?.team?.id) {
      // Check if all team members have phone, DOB, and sex
      const allMembersComplete = members.every(member => {
        const hasPhone = !!(member.phone_number || member.phone);
        const hasDob = !!member.date_of_birth;
        const hasSex = !!member.sex;
        return hasPhone && hasDob && hasSex;
      });

      // If all members are complete, clear skip flag and don't show modal
      if (allMembersComplete) {
        const skipKey = `teamMemberInfoSkipped_${dashboard.team.id}`;
        localStorage.removeItem(skipKey);
        setShowTeamMemberInfoModal(false);
        return;
      }

      // Check if user has skipped this notification
      const skipKey = `teamMemberInfoSkipped_${dashboard.team.id}`;
      const hasSkipped = localStorage.getItem(skipKey) === 'true';

      if (hasSkipped) {
        setShowTeamMemberInfoModal(false);
        return;
      }

      // Only show modal if not all members are complete and not skipped
      setShowTeamMemberInfoModal(true);
    } else if (!membersLoading && members.length === 0) {
      // No members yet, don't show modal
      setShowTeamMemberInfoModal(false);
    }
  }, [members, membersLoading, dashboard?.team?.id]);

  // Handle skip notification
  const handleSkipTeamMemberInfo = () => {
    if (dashboard?.team?.id) {
      const skipKey = `teamMemberInfoSkipped_${dashboard.team.id}`;
      localStorage.setItem(skipKey, 'true');
    }
    setShowTeamMemberInfoModal(false);
  };

  // Handle go to team members section
  const handleGoToTeamMembers = () => {
    setActiveSection('team');
    // Scroll to team section
    setTimeout(() => {
      const teamSection = document.getElementById('team-section');
      if (teamSection) {
        teamSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Leaderboard state
  const [divisionLeaderboard, setDivisionLeaderboard] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardError, setLeaderboardError] = useState(null);

  // Leaderboard accordion state
  const [openCantons, setOpenCantons] = useState({});
  const [openDivisions, setOpenDivisions] = useState({});
  const [cantonDivisions, setCantonDivisions] = useState({});
  const [divisionLeaderboards, setDivisionLeaderboards] = useState({});
  const [loadingStates, setLoadingStates] = useState({
    divisions: {},
    leaderboards: {}
  });
  // Search-related state variables
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const searchInputRef = useRef(null);
  const logoFileInputRef = useRef(null);

  // Team logo upload state
  const [logoUploading, setLogoUploading] = useState(false);

  // Profile picture upload state
  const [profileUploading, setProfileUploading] = useState({});

  // Simple 3-button display state
  const [currentPage, setCurrentPage] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const buttonGroups = useMemo(() => {
    const groups = [
      [
        { id: 'overview', icon: '🎯', label: t('teamDashboardPage.mobileNavOverview') },
        { id: 'stats', icon: '📊', label: t('teamDashboardPage.mobileNavStats') },
      ],
      [
        { id: 'results', icon: '📋', label: t('teamDashboardPage.mobileNavResults') },
        { id: 'divisions', icon: '🏆', label: t('teamDashboardPage.mobileNavRankings') },
        { id: 'reserve-court', icon: '🏟️', label: t('teamDashboardPage.mobileNavCourt') },
        { id: 'team', icon: '👥', label: t('teamDashboardPage.mobileNavTeam') },
        { id: 'settings', icon: '⚙️', label: t('teamDashboardPage.mobileNavSettings') },
      ],
    ];
    if (!compactEmbedMain) return groups;
    return groups.map((g) => g.filter((b) => b.id !== 'settings'));
  }, [compactEmbedMain, t]);

  // Navigation functions
  const swipeDown = () => {
    if (currentPage < buttonGroups.length - 1 && !isTransitioning) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentPage(1);
        setIsTransitioning(false);
      }, 200);
    }
  };

  const swipeUp = () => {
    if (currentPage > 0 && !isTransitioning) {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentPage(0);
        setIsTransitioning(false);
      }, 200);
    }
  };

  // Auto-navigate to correct page when active section changes (only on initial load)
  useEffect(() => {
    const allSections = ['overview', 'stats', 'schedule', 'results', 'divisions', 'reserve-court', 'team', 'settings'];
    const activeIndex = allSections.indexOf(activeSection);
    if (activeIndex !== -1) {
      const targetPage = activeIndex >= 3 ? 1 : 0;
      // Only set page if it's different and we're not transitioning
      if (targetPage !== currentPage && !isTransitioning) {
        setCurrentPage(targetPage);
      }
    }
  }, [activeSection]); // Removed currentPage from dependencies

  // Fetch leaderboards when dashboard loads or season changes
  useEffect(() => {
    const fetchLeaderboards = async () => {
      // Use selectedStatsSeason if available, otherwise fall back to dashboard.season.id
      const seasonId = selectedStatsSeason || dashboard?.season?.id;
      
      // Always try to fetch leaderboards, even for new mid-season teams
      // They should still see the division leaderboard even if they haven't played matches yet
      
      if (!dashboard?.teamInfo?.divisionId || !seasonId) {
        // Set a helpful error message when team has no division
        setLeaderboardLoading(false);
        if (dashboard?.teamInfo?.division === 'Not Assigned') {
          setLeaderboardError(t('teamDashboardPage.leaderboardErrorNoDivision'));
        } else {
          setLeaderboardError(t('teamDashboardPage.leaderboardErrorGeneric'));
        }
        return;
      }
      setLeaderboardLoading(true);
      setLeaderboardError(null);
      try {
        const response = await leaderboardAPI.getLeaderboard(dashboard.teamInfo.divisionId, seasonId);
        const leaderboardData = response.leaderboard || [];
        setDivisionLeaderboard(leaderboardData);
      } catch (err) {
        setLeaderboardError(err?.message || t('teamDashboardPage.toastLeaderboardLoadFail'));
      } finally {
        setLeaderboardLoading(false);
      }
    };
    fetchLeaderboards();
  }, [dashboard?.teamInfo?.divisionId, dashboard?.season?.id, selectedStatsSeason, dashboard?.teamInfo?.division, t, i18n.language]);

  // Handle team logo upload
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type - support iPhone formats including HEIC/HEIF
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 
      'image/webp', 'image/heic', 'image/heif', 'image/tiff'
    ];
    
    // Check if file is HEIC by extension (since MIME type might be empty)
    const isHeicByExtension = file.name.toLowerCase().endsWith('.heic');
    const isHeicByMimeType = file.type === 'image/heic' || file.type === 'image/heif';
    
    if (!allowedTypes.includes(file.type.toLowerCase()) && !isHeicByExtension) {
      toast.error(t('teamDashboardPage.toastFileTypeUnsupported', { type: file.type || '' }));
      return;
    }

    // Validate file size (20MB for iPhone images)
    if (file.size > 20 * 1024 * 1024) {
      toast.error(t('teamDashboardPage.toastFileSize20Mb'));
      return;
    }

    setLogoUploading(true);
    
    try {
      let fileToUpload = file;
      
      // Handle HEIC files - just upload them as-is
      if (isHeicByExtension || isHeicByMimeType) {
        toast.success(t('teamDashboardPage.toastUploadingHeic'));
        // Keep the original HEIC file for upload
        fileToUpload = file;
      }
      
      const result = await teamsAPI.uploadLogo(fileToUpload, effectiveTeamId || undefined);

      if (result.success) {
        toast.success(t('teamDashboardPage.toastLogoUploaded'));
        await queryClient.invalidateQueries(['dashboardOverview']);
        await queryClient.invalidateQueries(['dashboardSchedule']);
      } else {
        toast.error(result.message || t('teamDashboardPage.toastLogoUploadFail'));
      }
    } catch (error) {
      const dataMsg = error?.response?.data?.message;
      const msg =
        dataMsg ||
        (error?.message && /too large|413|payload/i.test(error.message)
          ? t('teamDashboardPage.toastImageTooLarge')
          : t('teamDashboardPage.toastLogoUploadFail'));
      toast.error(msg);
    } finally {
      setLogoUploading(false);
    }
  };

  // Get team initials for fallback
  const getTeamInitials = (teamName) => {
    return teamName
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Handle profile picture upload
  const handleProfileUpload = async (memberId, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type - support iPhone formats including HEIC/HEIF
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 
      'image/webp', 'image/heic', 'image/heif', 'image/tiff'
    ];
    
    // Check if file is HEIC by extension (since MIME type might be empty)
    const isHeicByExtension = file.name.toLowerCase().endsWith('.heic');
    const isHeicByMimeType = file.type === 'image/heic' || file.type === 'image/heif';
    
    if (!allowedTypes.includes(file.type.toLowerCase()) && !isHeicByExtension) {
      toast.error(t('teamDashboardPage.toastFileTypeUnsupported', { type: file.type || '' }));
      return;
    }

    // Validate file size (20MB for iPhone images)
    if (file.size > 20 * 1024 * 1024) {
      toast.error(t('teamDashboardPage.toastFileSize20Mb'));
      return;
    }

    setProfileUploading(prev => ({ ...prev, [memberId]: true }));
    
    // Handle HEIC files
    if (isHeicByExtension || isHeicByMimeType) {
      toast.success(t('teamDashboardPage.toastUploadingHeic'));
    }
    
    try {
      // Use service method instead of direct fetch
      const result = await membersAPI.uploadProfilePicture(
        memberId,
        file,
        effectiveTeamId || undefined
      );
      
      if (result.success) {
        const successMessage =
          isHeicByExtension || isHeicByMimeType
            ? t('teamDashboardPage.toastProfileHeicConverted')
            : t('teamDashboardPage.toastProfileUploadedOk');
        toast.success(successMessage);
        // Refresh members data to get updated profile picture
        window.location.reload();
      } else {
        toast.error(result.message || t('teamDashboardPage.toastProfileUploadFail'));
      }
    } catch (error) {
      const msg =
        error?.message && /too large|413|payload/i.test(error.message)
          ? t('teamDashboardPage.toastImageTooLarge')
          : t('teamDashboardPage.toastProfileUploadFail');
      toast.error(msg);
    } finally {
      setProfileUploading(prev => ({ ...prev, [memberId]: false }));
    }
  };

  // LeaderboardTabs function
  const LeaderboardTabs = () => {
    // Only show division leaderboard, remove canton leaderboard
    const allRows = divisionLeaderboard;
    const teamId = dashboard?.team?.id;
    const tabRankingsSeason = seasonsData?.data?.find(
      (s) => s.id === (selectedRankingsSeason || dashboard?.season?.id)
    );
    // Show all teams in the division
    const rowsToShow = allRows;
    return (
      <div>
        {leaderboardLoading ? (
          <div style={{ padding: '0.5rem', color: '#6b7280' }}>
            {t('teamDashboardPage.leaderboardLoadingRankings')}
          </div>
        ) : leaderboardError ? (
          <div style={{ padding: '0.5rem', color: '#d52b1e' }}>{leaderboardError}</div>
        ) : rowsToShow.length === 0 ? (
          <div style={{ padding: '0.5rem', color: '#6b7280' }}>{t('teamDashboardPage.leaderboardNoData')}</div>
        ) : (
          <>
            <LeaderboardTableWrapper>
              <LeaderboardTable>
                <thead>
                  <tr>
                    <LeaderboardHeadCell>{t('teamDashboardPage.leaderboardColRank')}</LeaderboardHeadCell>
                    <LeaderboardHeadCell>{t('teamDashboardPage.leaderboardColTeam')}</LeaderboardHeadCell>
                    <LeaderboardHeadCell className="center">{t('teamDashboardPage.leaderboardColGames')}</LeaderboardHeadCell>
                    <LeaderboardHeadCell className="center">{t('teamDashboardPage.leaderboardColPts')}</LeaderboardHeadCell>
                    <LeaderboardHeadCell className="center">{t('teamDashboardPage.leaderboardColW')}</LeaderboardHeadCell>
                    <LeaderboardHeadCell className="center">{t('teamDashboardPage.leaderboardColL')}</LeaderboardHeadCell>
                    <LeaderboardHeadCell className="center">{t('teamDashboardPage.leaderboardColSetDiff')}</LeaderboardHeadCell>
                  </tr>
                </thead>
                <tbody>
                  {rowsToShow.map((r, idx) => {
                    const rank = r.position || idx + 1;
                    const rowPrIcons = getPromotionRelegationIconsForLeaderboardRow(
                      r.promotion_status,
                      r.promotion_completed_season_name,
                      tabRankingsSeason?.name
                    );
                    return (
                      <LeaderboardRow key={r.team_id} $highlight={r.team_id === teamId}>
                        <LeaderboardCell>{rank}</LeaderboardCell>
                        <TeamLogoCell>
                          <TeamLogoSmall 
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTeamLogo(r.team_logo_url);
                              setSelectedTeamName(r.team_name);
                              setSelectedTeamInitials(getTeamInitials(r.team_name));
                              setTeamLogoModalOpen(true);
                            }}
                          >
                            {r.team_logo_url ? (
                              <TeamLogoImg src={r.team_logo_url} alt={`${r.team_name} logo`} />
                            ) : (
                              <TeamLogoInitials>
                                {getTeamInitials(r.team_name)}
                              </TeamLogoInitials>
                            )}
                          </TeamLogoSmall>
                          <TeamNameCell
                            role="button"
                            tabIndex={0}
                            title={t('teamDashboardPage.titleViewTeamResults')}
                            onClick={(e) => {
                              e.stopPropagation();
                              openResultsForTeam(r.team_name, selectedStatsSeason || dashboard?.season?.id);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openResultsForTeam(r.team_name, selectedStatsSeason || dashboard?.season?.id);
                              }
                            }}
                            style={{ cursor: 'pointer', color: '#1d4ed8', textDecoration: 'underline' }}
                          >
                            {r.team_name}
                            {isForfeitTeam(r) ? (
                              <ForfeitBadge title={t('teamDashboardPage.titleForfeit')}>
                                {t('teamDashboardPage.leaderboardForfeitLabel')}
                              </ForfeitBadge>
                            ) : null}
                            {r.promotion_status === 'promote' && (
                              <span style={{ marginLeft: '6px', color: '#16a34a', fontSize: '1.1em' }} title={t('teamDashboardPage.titlePromoted')}>{rowPrIcons.promote}</span>
                            )}
                            {r.promotion_status === 'relegate' && (
                              <span style={{ marginLeft: '6px', color: '#dc2626', fontSize: '1.1em' }} title={t('teamDashboardPage.titleRelegated')}>{rowPrIcons.relegate}</span>
                            )}
                          </TeamNameCell>
                        </TeamLogoCell>
                        <LeaderboardCell className="center">{r.games_played !== undefined && r.games_played !== null ? r.games_played : (r.wins || 0) + (r.losses || 0)}</LeaderboardCell>
                        <LeaderboardCell className="center">{r.points}</LeaderboardCell>
                        <LeaderboardCell className="center">{r.wins}</LeaderboardCell>
                        <LeaderboardCell className="center">{r.losses}</LeaderboardCell>
                        <LeaderboardCell className="center">{r.set_difference >= 0 ? `+${r.set_difference}` : r.set_difference}</LeaderboardCell>
                      </LeaderboardRow>
                    );
                  })}
                </tbody>
              </LeaderboardTable>
            </LeaderboardTableWrapper>
          </>
        )}
      </div>
    );
  };


  // Search Results Component
  const SearchResults = ({ results, searchQuery, onDivisionClick }) => {
    if (results.length === 0) {
      return (
        <EmptyMessage>{t('teamDashboardPage.searchNoDivisions', { query: searchQuery })}</EmptyMessage>
      );
    }

    return (
      <>
        <div style={{ marginBottom: '1rem', fontWeight: '600', color: '#1f2937' }}>
          {t('teamDashboardPage.searchResultsHeading', { count: results.length })}
        </div>
        {results.map((result, index) => {
          const divisionKey = `${result.canton.code}-${result.division.id}`;
          const teamCount = result.division.team_count || 0;
          return (
            <SearchResultItem key={divisionKey} onClick={() => onDivisionClick(result.canton.code, result.division)}>
              <SearchResultTitle>🏆 {result.division.name}</SearchResultTitle>
              <SearchResultSubtitle>
                {t('teamDashboardPage.searchSubtitleTeams', {
                  canton: result.canton.name,
                  count: teamCount,
                })}
              </SearchResultSubtitle>
            </SearchResultItem>
          );
        })}
      </>
    );
  };


  useEffect(() => {
    // Show alert if no team members
    if (user && !membersLoading && !hasMembers) {
      setShowAlert(true);
    } else if (user && !membersLoading && hasMembers) {
      setShowAlert(false);
    }
  }, [user, membersLoading, hasMembers]);

  // Refetch members when user changes
  useEffect(() => {
    if (user) {
      refetchMembers();
    }
  }, [user, refetchMembers]);
  
  // Poll for unread message counts every 15 seconds
  useEffect(() => {
    if (!user) return;
    if (isPlayerView && !effectiveTeamId) return;
    fetchUnreadCounts();
    const interval = setInterval(fetchUnreadCounts, 15000);
    return () => clearInterval(interval);
  }, [user, isPlayerView, effectiveTeamId, fetchUnreadCounts]);

  const showSection = (sectionName) => {
    if (campaignViewOnly && sectionName === 'settings') return;
    // Prevent unnecessary updates if already on this section
    if (activeSection === sectionName) {
      return;
    }
    
    // Update state immediately for instant UI feedback
    setActiveSection(sectionName);
    
    // Reset leaderboard accordion when switching sections
    if (sectionName !== 'divisions') {
      setOpenCantons({});
      setOpenDivisions({});
      setCantonDivisions({});
      setDivisionLeaderboards({});
      setLoadingStates({ divisions: {}, leaderboards: {} });
      setSearchQuery('');
      setSearchResults([]);
      setShowSearchResults(false);
      if (searchTimeout) {
        clearTimeout(searchTimeout);
        setSearchTimeout(null);
      }
    }
    
    if (embedded && typeof onEmbeddedSectionNavigate === 'function') {
      onEmbeddedSectionNavigate(sectionName);
      return;
    }

    // Update URL to reflect the active section; preserve team_id and season_id when viewing as player
    const params = new URLSearchParams(searchParams);
    params.set('section', sectionName);
    navigate(`/dashboard?${params.toString()}`, { replace: true });
  };

  const openResultsForTeam = (teamName, seasonId) => {
    if (!teamName) return;
    if (seasonId) setSelectedResultsSeason(seasonId);
    setResultsTeamFilter(teamName);
    setResultsSearchTerm('');
    setResultsCantonFilter('');
    setResultsDivisionFilter('');
    showSection('results');
  };

  // Pre-load division counts for all cantons
  const preloadDivisionCounts = async (seasonId = null) => {
    const swissCantons = [
      { name: 'Aargau', code: 'AG' },
      { name: 'Appenzell', codes: ['AR', 'AI'] }, // Combined AR + AI
      { name: 'Basel', codes: ['BL', 'BS'] }, // Combined BL + BS
      { name: 'Bern', code: 'BE' },
      { name: 'Fribourg', code: 'FR' },
      { name: 'Geneva', code: 'GE' },
      { name: 'Glarus', code: 'GL' },
      { name: 'Grisons', code: 'GR' }, // Renamed from Graubünden
      { name: 'Jura', code: 'JU' },
      { name: 'Lucerne', code: 'LU' },
      { name: 'Neuchâtel', code: 'NE' },
      { name: 'Nidwalden & Obwalden & Uri', codes: ['NW', 'OW', 'UR'] }, // Combined NW + OW + UR
      { name: 'Schaffhausen', code: 'SH' },
      { name: 'Schwyz', code: 'SZ' },
      { name: 'Solothurn', code: 'SO' },
      { name: 'St. Gallen', code: 'SG' },
      { name: 'Thurgau', code: 'TG' },
      { name: 'Ticino', code: 'TI' },
      { name: 'Valais', code: 'VS' },
      { name: 'Vaud', code: 'VD' },
      { name: 'Zug', code: 'ZG' },
      { name: 'Zurich', code: 'ZH' }
    ];

    // Load divisions for all cantons in parallel
    const promises = swissCantons.map(async (canton) => {
      try {
        // Handle merged cantons (multiple codes)
        const codesToFetch = canton.codes || [canton.code];
        const allDivisions = [];
        const seenDivisionIds = new Set();
        
        for (const code of codesToFetch) {
          // Use service method instead of direct fetch
          const data = await divisionsAPI.getDivisionsByCanton(code, seasonId);
          
          if (data.success && data.divisions) {
            // Deduplicate divisions by ID
            data.divisions.forEach(div => {
              if (!seenDivisionIds.has(div.id)) {
                seenDivisionIds.add(div.id);
                allDivisions.push(div);
              }
            });
          }
        }
        
        if (allDivisions.length > 0 || canton.codes) {
          // Store under the primary code (first code in array, or single code)
          const primaryCode = canton.codes ? canton.codes[0] : canton.code;
          setCantonDivisions(prev => ({
            ...prev,
            [primaryCode]: allDivisions
          }));
        }
      } catch (error) {
        // Error loading divisions - silently handle
      }
    });

    await Promise.all(promises);
  };

  // Define functions before useMemo to avoid reference errors
  const handleCantonToggle = async (cantonCode) => {
    const newOpenCantons = { ...openCantons };
    newOpenCantons[cantonCode] = !newOpenCantons[cantonCode];
    setOpenCantons(newOpenCantons);

    // If opening and we don't have divisions for this canton, fetch them
    if (newOpenCantons[cantonCode] && !cantonDivisions[cantonCode]) {
      setLoadingStates(prev => ({
        ...prev,
        divisions: { ...prev.divisions, [cantonCode]: true }
      }));

      try {
        // Find if this is a merged canton
        const swissCantons = [
          { name: 'Aargau', code: 'AG' },
          { name: 'Appenzell', codes: ['AR', 'AI'] },
          { name: 'Basel', codes: ['BL', 'BS'] },
          { name: 'Bern', code: 'BE' },
          { name: 'Fribourg', code: 'FR' },
          { name: 'Geneva', code: 'GE' },
          { name: 'Glarus', code: 'GL' },
          { name: 'Grisons', code: 'GR' },
          { name: 'Jura', code: 'JU' },
          { name: 'Lucerne', code: 'LU' },
          { name: 'Neuchâtel', code: 'NE' },
          { name: 'Nidwalden & Obwalden & Uri', codes: ['NW', 'OW', 'UR'] },
          { name: 'Schaffhausen', code: 'SH' },
          { name: 'Schwyz', code: 'SZ' },
          { name: 'Solothurn', code: 'SO' },
          { name: 'St. Gallen', code: 'SG' },
          { name: 'Thurgau', code: 'TG' },
          { name: 'Ticino', code: 'TI' },
          { name: 'Valais', code: 'VS' },
          { name: 'Vaud', code: 'VD' },
          { name: 'Zug', code: 'ZG' },
          { name: 'Zurich', code: 'ZH' }
        ];
        
        const canton = swissCantons.find(c => c.code === cantonCode || (c.codes && c.codes.includes(cantonCode)));
        
        // Get codes to fetch (either single code or merged codes)
        const codesToFetch = canton?.codes || [cantonCode];
        const allDivisions = [];
        const seenDivisionIds = new Set();
        
        // Use selectedRankingsSeason if available, otherwise fall back to dashboard.season.id
        const seasonId = selectedRankingsSeason || dashboard?.season?.id || null;
        
        let upcomingSeasonMessage = null;
        let hasUpcomingSeasonResponse = false;
        
        for (const code of codesToFetch) {
          // Use api client so X-Tenant-ID and Authorization are sent (team's tenant from login)
          const data = await divisionsAPI.getDivisionsByCanton(code, seasonId);
          
          // Check if this is an upcoming season response
          if (data && data.success && data.isUpcomingSeason && data.message) {
            upcomingSeasonMessage = data.message;
            hasUpcomingSeasonResponse = true;
            // Don't break - continue checking other codes to ensure consistency
            // But don't add any divisions if we get an upcoming season message
            continue;
          }
          
          // Only add divisions if we haven't seen an upcoming season message
          if (!hasUpcomingSeasonResponse && data && data.success && data.divisions) {
            // Deduplicate divisions by ID
            data.divisions.forEach(div => {
              if (!seenDivisionIds.has(div.id)) {
                seenDivisionIds.add(div.id);
                allDivisions.push(div);
              }
            });
          }
        }
        
        // Store results: if we got an upcoming season message, store that; otherwise store divisions
        if (hasUpcomingSeasonResponse && upcomingSeasonMessage) {
          setCantonDivisions(prev => ({
            ...prev,
            [cantonCode]: [],
            [`${cantonCode}_message`]: upcomingSeasonMessage
          }));
        } else if (allDivisions.length > 0) {
          setCantonDivisions(prev => ({
            ...prev,
            [cantonCode]: allDivisions,
            [`${cantonCode}_message`]: null // Clear any previous message
          }));
        } else {
          // No divisions and no message - empty state
          setCantonDivisions(prev => ({
            ...prev,
            [cantonCode]: [],
            [`${cantonCode}_message`]: null
          }));
        }
      } catch (error) {
        // Error fetching divisions - silently handle
      } finally {
        setLoadingStates(prev => ({
          ...prev,
          divisions: { ...prev.divisions, [cantonCode]: false }
        }));
      }
    }
  };

  // handleDivisionToggle function
  const handleDivisionToggle = async (cantonCode, division) => {
    const divisionKey = `${cantonCode}-${division.id}`;
    
    const newOpenDivisions = { ...openDivisions };
    newOpenDivisions[divisionKey] = !newOpenDivisions[divisionKey];
    setOpenDivisions(newOpenDivisions);

    // If opening and we don't have leaderboard data, fetch it
    if (newOpenDivisions[divisionKey] && !divisionLeaderboards[divisionKey]) {
      setLoadingStates(prev => ({
        ...prev,
        leaderboards: { ...prev.leaderboards, [divisionKey]: true }
      }));

      try {
        // Use selectedRankingsSeason if available, otherwise fall back to dashboard.season.id
        const seasonId = selectedRankingsSeason || dashboard?.season?.id || null;
        const response = await leaderboardAPI.getLeaderboard(division.id, seasonId);
        setDivisionLeaderboards(prev => ({
          ...prev,
          [divisionKey]: response.leaderboard || response.data || []
        }));
      } catch (error) {
        // Error fetching leaderboard - silently handle
      } finally {
        setLoadingStates(prev => ({
          ...prev,
          leaderboards: { ...prev.leaderboards, [divisionKey]: false }
        }));
      }
    }
  };

  // Simple search functions without useCallback to prevent focus issues - COMMENTED OUT FOR COMING SOON
  // const performSearch = (query) => {
    
  //   const searchTerm = query.toLowerCase().trim();
  //   const results = [];
  //   const swissCantons = [
  //     { name: 'Aargau', code: 'AG' }, { name: 'Appenzell Ausserrhoden', code: 'AR' },
  //     { name: 'Appenzell Innerrhoden', code: 'AI' }, { name: 'Basel-Landschaft', code: 'BL' },
  //     { name: 'Basel-Stadt', code: 'BS' }, { name: 'Bern', code: 'BE' },
  //     { name: 'Fribourg', code: 'FR' }, { name: 'Geneva', code: 'GE' },
  //     { name: 'Glarus', code: 'GL' }, { name: 'Graubünden', code: 'GR' },
  //     { name: 'Jura', code: 'JU' }, { name: 'Lucerne', code: 'LU' },
  //     { name: 'Neuchâtel', code: 'NE' }, { name: 'Nidwalden', code: 'NW' },
  //     { name: 'Obwalden', code: 'OW' }, { name: 'Schaffhausen', code: 'SH' },
  //     { name: 'Schwyz', code: 'SZ' }, { name: 'Solothurn', code: 'SO' },
  //     { name: 'St. Gallen', code: 'SG' }, { name: 'Thurgau', code: 'TG' },
  //     { name: 'Ticino', code: 'TI' }, { name: 'Uri', code: 'UR' },
  //     { name: 'Valais', code: 'VS' }, { name: 'Vaud', code: 'VD' },
  //     { name: 'Zug', code: 'ZG' }, { name: 'Zurich', code: 'ZH' }
  //   ];

  //   // Search through all loaded canton divisions
  //   Object.entries(cantonDivisions).forEach(([cantonCode, divisions]) => {
  //     const canton = swissCantons.find(c => c.code === cantonCode);
  //     if (!canton) return;

  //     // Check if canton name matches
  //     if (canton.name.toLowerCase().includes(searchTerm)) {
  //       divisions.forEach(division => {
  //         results.push({
  //           canton: canton,
  //           division: division,
  //           matchType: 'canton'
  //         });
  //       });
  //     }

  //     // Check if division name matches
  //     divisions.forEach(division => {
  //       if (division.name.toLowerCase().includes(searchTerm)) {
  //         results.push({
  //           canton: canton,
  //           division: division,
  //           matchType: 'division'
  //         });
  //       }
  //     });
  //   });

  //   setSearchResults(results);
  //   setShowSearchResults(true);
  // };

  // Simple search input handler - COMMENTED OUT FOR COMING SOON
  // const handleSearchInput = (query) => {
  //   setSearchQuery(query);
    
  //   // If query is empty, clear results immediately
  //   if (query.trim() === '') {
  //     setShowSearchResults(false);
  //     setSearchResults([]);
  //     return;
  //   }
    
  //   // Only search if query has at least 2 characters
  //   if (query.trim().length < 2) {
  //     setShowSearchResults(false);
  //     setSearchResults([]);
  //     return;
  //   }
    
  //   // Perform search immediately
  //   performSearch(query);
  // };

  // Memoized Leaderboard accordion component to prevent recreation
  const LeaderboardSection = React.useMemo(() => {
    const swissCantons = [
      { name: 'Aargau', code: 'AG' },
      { name: 'Appenzell', codes: ['AR', 'AI'] }, // Combined AR + AI
      { name: 'Basel', codes: ['BL', 'BS'] }, // Combined BL + BS
      { name: 'Bern', code: 'BE' },
      { name: 'Fribourg', code: 'FR' },
      { name: 'Geneva', code: 'GE' },
      { name: 'Glarus', code: 'GL' },
      { name: 'Grisons', code: 'GR' }, // Renamed from Graubünden
      { name: 'Jura', code: 'JU' },
      { name: 'Lucerne', code: 'LU' },
      { name: 'Neuchâtel', code: 'NE' },
      { name: 'Nidwalden & Obwalden & Uri', codes: ['NW', 'OW', 'UR'] }, // Combined NW + OW + UR
      { name: 'Schaffhausen', code: 'SH' },
      { name: 'Schwyz', code: 'SZ' },
      { name: 'Solothurn', code: 'SO' },
      { name: 'St. Gallen', code: 'SG' },
      { name: 'Thurgau', code: 'TG' },
      { name: 'Ticino', code: 'TI' },
      { name: 'Valais', code: 'VS' },
      { name: 'Vaud', code: 'VD' },
      { name: 'Zug', code: 'ZG' },
      { name: 'Zurich', code: 'ZH' }
    ];

    // Get all cantons - show ALL cantons even if no teams
    const cantonEntries = swissCantons.map((canton) => {
      // Get the primary code (first code if multiple, or single code)
      const primaryCode = canton.codes ? canton.codes[0] : canton.code;
      const divisions = cantonDivisions[primaryCode] || [];
      // Filter divisions to only show those with teams (teamCount > 0 or teams array has items)
      const filteredDivisions = divisions.filter(div => {
        const hasTeams = (div.teamCount && div.teamCount > 0) || (div.team_count && div.team_count > 0) || (div.teams && div.teams.length > 0);
        return hasTeams;
      });
      
      // Sort divisions: first by skill level, then by group letter (A, B, C)
      const sortedDivisions = [...filteredDivisions].sort((a, b) => {
        // Extract skill level from division name (e.g., "Intermediate", "Beginner", "Advanced")
        const getSkillLevel = (name) => {
          const nameLower = name.toLowerCase();
          // Order: Elite > Advanced + > Advanced > Intermediate + > Intermediate > Beginner + > Beginner
          if (nameLower.includes('elite')) return 0;
          if (nameLower.includes('advanced +') || nameLower.includes('confirmed advanced')) return 1;
          if (nameLower.includes('advanced')) return 2;
          if (nameLower.includes('intermediate +') || nameLower.includes('confirmed intermediate')) return 3;
          if (nameLower.includes('intermediate')) return 4;
          if (nameLower.includes('beginner +') || nameLower.includes('confirmed beginner')) return 5;
          if (nameLower.includes('beginner')) return 6;
          return 99; // Unknown skill level goes last
        };
        
        // Extract group letter from division name (e.g., "Group A" -> "A")
        const getGroupLetter = (name) => {
          const match = name.match(/\bGroup\s+([A-Z])\b/i);
          return match ? match[1].toUpperCase() : null;
        };
        
        const skillLevelA = getSkillLevel(a.name);
        const skillLevelB = getSkillLevel(b.name);
        
        // First, sort by skill level (lower number = higher skill)
        if (skillLevelA !== skillLevelB) {
          return skillLevelA - skillLevelB;
        }
        
        // If same skill level, sort by group letter (A, B, C)
        const groupA = getGroupLetter(a.name);
        const groupB = getGroupLetter(b.name);
        
        if (groupA && groupB) {
          return groupA.localeCompare(groupB);
        }
        
        // If only one has a group letter, put it first
        if (groupA && !groupB) return -1;
        if (!groupA && groupB) return 1;
        
        // Otherwise, sort alphabetically by division name
        return a.name.localeCompare(b.name);
      });
      
      return { canton, divisions: sortedDivisions };
    });
    
    // Sort alphabetically by canton name
    cantonEntries.sort((a, b) => {
      const nameA = a.canton.name.toLowerCase();
      const nameB = b.canton.name.toLowerCase();
      return nameA.localeCompare(nameB);
    });

    const selectedSeason =
      seasonsData?.data?.find((s) => String(s.id) === String(selectedRankingsSeason)) ||
      (dashboard?.season &&
      selectedRankingsSeason &&
      String(dashboard.season.id) === String(selectedRankingsSeason)
        ? dashboard.season
        : null);
    const now = new Date();
    const shouldShowLeaderboard = seasonAllowsStandingsUI(selectedSeason, now);
    const isUpcomingSeason =
      selectedSeason &&
      ['upcoming', 'registration'].includes(selectedSeason.status) &&
      !shouldShowLeaderboard;
    const upcomingSeasonMessage =
      selectedSeason && isUpcomingSeason
        ? t('teamDashboardPage.rankingsStartsOnGeneratedBody', {
            name: selectedSeason.name,
            date: new Date(selectedSeason.start_date).toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            }),
          })
        : null;

    return (
      <DivisionsContainer>
        <SectionHeader>
          <SectionTitle>{t('teamDashboardPage.divisionsRankingsHeading')}</SectionTitle>
          <StatsControls>
            <SeasonDropdown>
              <SeasonSelect
                value={selectedRankingsSeason}
                onChange={(e) => setSelectedRankingsSeason(e.target.value)}
              >
                {seasonsData?.data?.length > 0 ? (
                  seasonsData.data.map((season) => (
                    <option key={season.id} value={season.id}>
                      {season.name}
                    </option>
                  ))
                ) : (
                  <option>{t('teamDashboardPage.loadingSeasonsOption')}</option>
                )}
              </SeasonSelect>
              <DropdownIcon>▼</DropdownIcon>
            </SeasonDropdown>
          </StatsControls>
        </SectionHeader>

        {/* Show message if season is not active or completed */}
        {!shouldShowLeaderboard && selectedSeason && (
          <SeasonInfoBanner>
            {upcomingSeasonMessage ||
              t('teamDashboardPage.rankingsStartsOnBody', {
                name: selectedSeason.name,
                date: new Date(selectedSeason.start_date).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                }),
              })}
          </SeasonInfoBanner>
        )}

        {/* Show leaderboard if season is active or completed */}
        {shouldShowLeaderboard && cantonEntries.map(({ canton, divisions, upcomingMessage }) => {
            const primaryCode = canton.codes ? canton.codes[0] : canton.code;
            const isCantonOpen = openCantons[primaryCode];
            // Get message from state if not in entry
            const messageForCanton = upcomingMessage || cantonDivisions[`${primaryCode}_message`] || null;
            
            return (
              <CantonRow key={primaryCode}>
                <CantonHeader 
                  onClick={() => handleCantonToggle(primaryCode)}
                  $isOpen={isCantonOpen}
                >
                  <CantonName>
                    {canton.name}
                  </CantonName>
                  <ExpandIcon $isOpen={isCantonOpen}>▼</ExpandIcon>
                </CantonHeader>
                
                <CantonContent $isOpen={isCantonOpen}>
                  {loadingStates.divisions[primaryCode] ? (
                    <LoadingSpinner>{t('teamDashboardPage.loadingDivisions')}</LoadingSpinner>
                  ) : divisions.length === 0 ? (
                    <EmptyMessage>
                      {isUpcomingSeason
                        ? t('teamDashboardPage.rankingsWhenSeasonBegins')
                        : messageForCanton || t('teamDashboardPage.noTeamsInCanton')}
                    </EmptyMessage>
                  ) : (
                    divisions
                      .map((division) => {
                        const divisionKey = `${primaryCode}-${division.id}`;
                        const isDivisionOpen = openDivisions[divisionKey];
                        const leaderboard = divisionLeaderboards[divisionKey] || [];
                        
                        return (
                        <DivisionRow key={division.id}>
                          <DivisionHeader
                            onClick={() => handleDivisionToggle(primaryCode, division)}
                            $isOpen={isDivisionOpen}
                          >
                            <DivisionName>
                              {division.name}
                            </DivisionName>
                            <ExpandIcon $isOpen={isDivisionOpen}>▼</ExpandIcon>
                          </DivisionHeader>
                          
                          <DivisionContent $isOpen={isDivisionOpen}>
                            {loadingStates.leaderboards[divisionKey] ? (
                              <LoadingSpinner>{t('teamDashboardPage.loadingRankingsPane')}</LoadingSpinner>
                            ) : leaderboard.length === 0 ? (
                              <EmptyMessage>{t('teamDashboardPage.noTeamsInDivision')}</EmptyMessage>
                            ) : (
                              <LeaderboardTableWrapper>
                                <LeaderboardTable>
                                  <thead>
                                    <tr>
                                      <LeaderboardHeadCell>{t('teamDashboardPage.leaderboardColRank')}</LeaderboardHeadCell>
                                      <LeaderboardHeadCell>{t('teamDashboardPage.leaderboardColTeam')}</LeaderboardHeadCell>
                                      <LeaderboardHeadCell className="center">{t('teamDashboardPage.leaderboardColGames')}</LeaderboardHeadCell>
                                      <LeaderboardHeadCell className="center">{t('teamDashboardPage.leaderboardColPts')}</LeaderboardHeadCell>
                                      <LeaderboardHeadCell className="center">{t('teamDashboardPage.leaderboardColW')}</LeaderboardHeadCell>
                                      <LeaderboardHeadCell className="center">{t('teamDashboardPage.leaderboardColL')}</LeaderboardHeadCell>
                                      <LeaderboardHeadCell className="center">{t('teamDashboardPage.leaderboardColSetDiff')}</LeaderboardHeadCell>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {leaderboard.map((r, idx) => {
                                      const rank = r.position || idx + 1;
                                      const rowPrIcons = getPromotionRelegationIconsForLeaderboardRow(
                                        r.promotion_status,
                                        r.promotion_completed_season_name,
                                        selectedSeason?.name
                                      );
                                      return (
                                        <LeaderboardRow key={r.team_id} $highlight={r.team_id === dashboard?.team?.id}>
                                          <LeaderboardCell>{rank}</LeaderboardCell>
                                          <TeamLogoCell>
                                            <TeamLogoSmall
                                              style={{ cursor: 'pointer' }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedTeamLogo(r.team_logo_url);
                                                setSelectedTeamName(r.team_name);
                                                setSelectedTeamInitials(getTeamInitials(r.team_name));
                                                setTeamLogoModalOpen(true);
                                              }}
                                            >
                                              {r.team_logo_url ? (
                                                <TeamLogoImg src={r.team_logo_url} alt={`${r.team_name} logo`} />
                                              ) : (
                                                <TeamLogoInitials>{getTeamInitials(r.team_name)}</TeamLogoInitials>
                                              )}
                                            </TeamLogoSmall>
                                            <TeamNameCell
                                              role="button"
                                              tabIndex={0}
                                              title={t('teamDashboardPage.titleViewTeamResults')}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openResultsForTeam(r.team_name, selectedRankingsSeason || dashboard?.season?.id);
                                              }}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                  e.preventDefault();
                                                  openResultsForTeam(r.team_name, selectedRankingsSeason || dashboard?.season?.id);
                                                }
                                              }}
                                              style={{ cursor: 'pointer', color: '#1d4ed8', textDecoration: 'underline' }}
                                            >
                                              {r.team_name}
                                              {isForfeitTeam(r) ? (
                                                <ForfeitBadge title={t('teamDashboardPage.titleForfeit')}>
                                                  {t('teamDashboardPage.leaderboardForfeitLabel')}
                                                </ForfeitBadge>
                                              ) : null}
                                              {r.promotion_status === 'promote' && (
                                                <span style={{ marginLeft: '6px', color: '#16a34a', fontSize: '1.1em' }} title={t('teamDashboardPage.titlePromoted')}>{rowPrIcons.promote}</span>
                                              )}
                                              {r.promotion_status === 'relegate' && (
                                                <span style={{ marginLeft: '6px', color: '#dc2626', fontSize: '1.1em' }} title={t('teamDashboardPage.titleRelegated')}>{rowPrIcons.relegate}</span>
                                              )}
                                            </TeamNameCell>
                                          </TeamLogoCell>
                                          <LeaderboardCell className="center">{r.games_played !== undefined && r.games_played !== null ? r.games_played : (r.wins || 0) + (r.losses || 0)}</LeaderboardCell>
                                          <LeaderboardCell className="center">{r.points}</LeaderboardCell>
                                          <LeaderboardCell className="center">{r.wins}</LeaderboardCell>
                                          <LeaderboardCell className="center">{r.losses}</LeaderboardCell>
                                          <LeaderboardCell className="center">{r.set_difference >= 0 ? `+${r.set_difference}` : r.set_difference}</LeaderboardCell>
                                        </LeaderboardRow>
                                      );
                                    })}
                                  </tbody>
                                </LeaderboardTable>
                              </LeaderboardTableWrapper>
                            )}
                          </DivisionContent>
                        </DivisionRow>
                      );
                    })
                  )}
                </CantonContent>
              </CantonRow>
            );
          })}
        {!shouldShowLeaderboard && selectedSeason && (
          <div style={{ 
            textAlign: 'center', 
            padding: '3rem 2rem',
            color: '#6b7280',
            fontSize: '1.1rem'
          }}>
            <div style={{ marginBottom: '1rem', fontSize: '1.5rem' }}>🏆</div>
            <div>{t('teamDashboardPage.rankingsWhenBeginsShort')}</div>
          </div>
        )}
      </DivisionsContainer>
    );
  }, [cantonDivisions, openCantons, openDivisions, divisionLeaderboards, loadingStates, dashboard?.team?.id, dashboard?.season, selectedRankingsSeason, seasonsData, t, i18n.language]);


  // Load division counts when component mounts or when season changes
  useEffect(() => {
    if (activeSection === 'divisions') {
      // Use selectedRankingsSeason if available, otherwise fall back to dashboard.season.id
      const seasonId = selectedRankingsSeason || dashboard?.season?.id || null;
      preloadDivisionCounts(seasonId);
    }
  }, [activeSection, selectedRankingsSeason, dashboard?.season?.id]);

  // Clear cached leaderboards when season changes in rankings section
  useEffect(() => {
    if (activeSection === 'divisions' && selectedRankingsSeason) {
      setDivisionLeaderboards({});
      setOpenDivisions({});
      setOpenCantons({});
      // Clear cached divisions so they reload with the new season filter
      setCantonDivisions({});
    }
  }, [selectedRankingsSeason, activeSection]);

  // Cleanup search timeout on unmount - COMMENTED OUT FOR COMING SOON
  // useEffect(() => {
  //   return () => {
  //     if (searchTimeout) {
  //       clearTimeout(searchTimeout);
  //     }
  //   };
  // }, [searchTimeout]);


  return (
    <ScrollMemoryWrapper enableScrollMemory={true}>
      <DashboardContainer $embedded={embedded}>
      {showTeamSidebar && (
      <Sidebar>
        <SidebarHeader>
          <TeamLogoSection>
            <TeamLogoContainer 
              title={
                dashboard?.teamInfo?.teamLogoUrl
                  ? t('teamDashboardPage.titleClickViewLogo')
                  : t('teamDashboardPage.titleClickUploadLogo')
              }
              onClick={() => {
                if (!campaignViewOnly) setLogoUploadModalOpen(true);
              }}
              style={{ cursor: campaignViewOnly ? 'default' : 'pointer' }}
            >
              {dashboard?.teamInfo?.teamLogoUrl ? (
                <TeamLogo src={dashboard.teamInfo.teamLogoUrl} alt={t('teamDashboardPage.altTeamLogo')} />
              ) : (
                <TeamLogoPlaceholder>
                  {getTeamInitials(
                    isPlayerView && dashboard?.teamInfo?.name
                      ? dashboard.teamInfo.name
                      : user?.teamName || t('teamDashboardPage.teamFallback')
                  )}
                  <PlusIcon>+</PlusIcon>
                </TeamLogoPlaceholder>
              )}
            </TeamLogoContainer>
            <AppTitle>
              {isPlayerView && dashboard?.teamInfo?.name
                ? dashboard.teamInfo.name
                : user?.teamName || t('teamDashboardPage.teamDashboardFallbackTitle')}
            </AppTitle>
          </TeamLogoSection>
        </SidebarHeader>
        {embedded && typeof onEmbeddedBack === 'function' && (
          <EmbeddedBackSidebar type="button" onClick={onEmbeddedBack}>
            {t('teamDashboardPage.backToTournaments')}
          </EmbeddedBackSidebar>
        )}
        <Navigation>
          <NavItem $active={activeSection === 'overview'} onClick={() => showSection('overview')}>
            <NavIcon>🎯</NavIcon>
            {t('teamDashboardPage.navOverview')}
          </NavItem>
          <NavItem $active={activeSection === 'stats'} onClick={() => showSection('stats')}>
            <NavIcon>📊</NavIcon>
            {t('teamDashboardPage.navStatistics')}
          </NavItem>
          <NavItem $active={activeSection === 'results'} onClick={() => showSection('results')}>
            <NavIcon>📋</NavIcon>
            {t('teamDashboardPage.navResults')}
          </NavItem>
          <NavItem $active={activeSection === 'divisions'} onClick={() => showSection('divisions')}>
            <NavIcon>🏆</NavIcon>
            {t('teamDashboardPage.navRankings')}
          </NavItem>
          <NavItem $active={activeSection === 'reserve-court'} onClick={() => showSection('reserve-court')}>
            <NavIcon>🏟️</NavIcon>
            {t('teamDashboardPage.navReserveCourt')}
          </NavItem>
          <NavItem $active={activeSection === 'team'} onClick={() => showSection('team')}>
            <NavIcon>👥</NavIcon>
            {t('teamDashboardPage.navTeamMembers')}
          </NavItem>
          {!campaignViewOnly && (
            <NavItem $active={activeSection === 'settings'} onClick={() => showSection('settings')}>
              <NavIcon>⚙️</NavIcon>
              {t('teamDashboardPage.navSettings')}
            </NavItem>
          )}
        </Navigation>
        <SidebarFooter>
          {versionText ? (
            <VersionInfo>{t('teamDashboardPage.versionLabel', { version: versionText })}</VersionInfo>
          ) : null}
          {hasPendingUpdate && (
            <UpdateButton onClick={() => versionService.forceRefresh()}>
              {t('teamDashboardPage.newVersionRefresh')}
            </UpdateButton>
          )}
          <LogoutButton onClick={handleLogout}>
            <NavIcon style={{ color: '#dc2626' }}>●</NavIcon>
            {t('teamDashboardPage.logout')}
          </LogoutButton>
        </SidebarFooter>
      </Sidebar>
      )}

      <MainContent $compactEmbedMain={compactEmbedMain}>
        <VisuallyHiddenFileInput
          ref={logoFileInputRef}
          accept="image/*,.heic,.heif"
          onChange={handleLogoUpload}
          disabled={logoUploading}
          aria-label={t('teamDashboardPage.uploadTeamLogoAria')}
        />
        {user && (
          <MobileTeamHeader>
            <TeamLogoContainer
              title={
                dashboard?.teamInfo?.teamLogoUrl
                  ? t('teamDashboardPage.titleTapViewLogo')
                  : t('teamDashboardPage.titleTapUploadLogo')
              }
              onClick={() => {
                if (!campaignViewOnly) setLogoUploadModalOpen(true);
              }}
              style={{ cursor: campaignViewOnly ? 'default' : 'pointer' }}
            >
              {dashboard?.teamInfo?.teamLogoUrl ? (
                <TeamLogo src={dashboard.teamInfo.teamLogoUrl} alt={t('teamDashboardPage.altTeamLogo')} />
              ) : (
                <TeamLogoPlaceholder>
                  {getTeamInitials(
                    isPlayerView && dashboard?.teamInfo?.name
                      ? dashboard.teamInfo.name
                      : user?.teamName || t('teamDashboardPage.teamFallback')
                  )}
                  <PlusIcon>+</PlusIcon>
                </TeamLogoPlaceholder>
              )}
            </TeamLogoContainer>
            <MobileTeamName>
              {isPlayerView && dashboard?.teamInfo?.name
                ? dashboard.teamInfo.name
                : user?.teamName || t('teamDashboardPage.teamDashboardFallbackTitle')}
            </MobileTeamName>
          </MobileTeamHeader>
        )}
        <TopBar>
          {embedded && typeof onEmbeddedBack === 'function' && (
            <EmbeddedBackMobile type="button" onClick={onEmbeddedBack}>
              {t('teamDashboardPage.backToTournaments')}
            </EmbeddedBackMobile>
          )}
          <TopBarActions>
          </TopBarActions>
        </TopBar>

          {/* Team Disabled Message: only when the viewed team is disabled (team login: user.is_active; player view: dashboard.teamInfo.is_active) */}
          {user && (user.type === 'team' ? !user.is_active : dashboard?.teamInfo?.is_active === false) && (
            <AlertBanner style={{ background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' }}>
              <AlertIcon>🚫</AlertIcon>
              <AlertContent>
                <AlertTitle>{t('teamDashboardPage.teamDisabledTitle')}</AlertTitle>
                <AlertText>{t('teamDashboardPage.teamDisabledBody')}</AlertText>
              </AlertContent>
            </AlertBanner>
          )}

          {showAlert && (
            <AlertBanner>
              <AlertIcon>⚠️</AlertIcon>
              <AlertContent>
                <AlertTitle>{t('teamDashboardPage.completeSetupTitle')}</AlertTitle>
                <AlertText>{t('teamDashboardPage.completeSetupBody')}</AlertText>
              </AlertContent>
              <AlertButton to={`/dashboard?${new URLSearchParams({ ...Object.fromEntries(searchParams.entries()), section: 'team' }).toString()}`}>{t('teamDashboardPage.addMember')}</AlertButton>
            </AlertBanner>
          )}

        {activeSection === 'overview' && (
          <>
            {isPlayerView ? (
              <PlayerHomeBannerStack
                availabilityReminder={availabilityReminder}
                fetchEnabled={!!viewerPlayerIdForChat}
              />
            ) : null}
            {/* Season Ended Banner */}
            {seasonCompleted && (
              <SeasonEndedBanner>
                <SeasonEndedIcon>🏁</SeasonEndedIcon>
                <SeasonEndedContent>
                  <SeasonEndedTitle>{t('teamDashboardPage.seasonEndedTitle')}</SeasonEndedTitle>
                  <SeasonEndedMessage>
                    {t('teamDashboardPage.seasonEndedBody')}
                    {nextSeason ? t('teamDashboardPage.seasonEndedNextHint') : null}
                  </SeasonEndedMessage>
                </SeasonEndedContent>
              </SeasonEndedBanner>
            )}
            {dashboardLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '1.2rem', color: '#6b7280' }}>{t('teamDashboardPage.loadingDashboard')}</div>
              </div>
            ) : dashboardError ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '1.2rem', color: '#d52b1e' }}>{t('teamDashboardPage.errorLoadingDashboard')}</div>
                <div style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem' }}>
                  {t('teamDashboardPage.tryRefresh')}
                </div>
              </div>
            ) : !dashboard ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '1.2rem', color: '#6b7280' }}>{t('teamDashboardPage.noDashboardData')}</div>
                <div style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem' }}>
                  {t('teamDashboardPage.contactSupportPersist')}
                </div>
              </div>
            ) : (
              <CurrentStandingCard>
                <StandingHeader>
                  <StandingTitle>{t('teamDashboardPage.currentStandingTitle')}</StandingTitle>
                  {dashboard?.teamInfo?.canton && (
                    <div style={{ 
                      fontSize: '0.8rem', 
                      color: 'rgba(255, 255, 255, 0.8)', 
                      marginBottom: '0.25rem',
                      fontWeight: '500'
                    }}>
                      {t('teamDashboardPage.cantonLine', { canton: dashboard.teamInfo.canton })}
                    </div>
                  )}
                  {dashboard?.teamInfo?.averageLevel && (
                    <div style={{ 
                      fontSize: '0.8rem', 
                      color: 'rgba(255, 255, 255, 0.8)', 
                      marginBottom: '0.25rem',
                      fontWeight: '500'
                    }}>
                      {t('teamDashboardPage.teamLevelLine', { level: dashboard.teamInfo.averageLevel })}
                    </div>
                  )}
                  <StandingRank>
                    <RankNumber>
                      {dashboard?.currentStanding?.position
                        ? t('teamDashboardPage.placeStanding', {
                            pos: dashboard.currentStanding.position,
                            ordinal: getOrdinalSuffix(dashboard.currentStanding.position),
                          })
                        : t('teamDashboardPage.noRankingYet')}
                    </RankNumber>
                    <Division>{dashboard?.currentStanding?.division || t('teamDashboardPage.noDivision')}</Division>
                  </StandingRank>
                </StandingHeader>
                <StandingStats>
                  <StatCard>
                    <StatValue>{dashboard?.teamStats?.games ?? 0}</StatValue>
                    <StatLabel>{t('teamDashboardPage.labelGames')}</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>
                      {(dashboard?.teamStats?.wins ?? 0)}-{(dashboard?.teamStats?.losses ?? 0)}
                    </StatValue>
                    <StatLabel>{t('teamDashboardPage.labelRecord')}</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>{dashboard?.currentStanding?.setDiff || '+0'}</StatValue>
                    <StatLabel>{t('teamDashboardPage.labelSetDiff')}</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>{dashboard?.currentStanding?.points || 0}</StatValue>
                    <StatLabel>{t('teamDashboardPage.labelPoints')}</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>
                      {dashboard?.teamStats?.memberCount ?? 0}/2
                    </StatValue>
                    <StatLabel>{t('teamDashboardPage.labelRoster')}</StatLabel>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        opacity: 0.85,
                        marginTop: '0.2rem',
                        lineHeight: 1.3,
                      }}
                    >
                      {(dashboard?.teamStats?.memberCount ?? 0) >= 2
                        ? t('teamDashboardPage.teamCompleteShort')
                        : t('teamDashboardPage.activePlayersShort')}
                    </div>
                  </StatCard>
                </StandingStats>
              </CurrentStandingCard>
            )}

            <MatchesSection>
              <SectionHeader style={{ flexWrap: 'wrap', gap: '0.5rem', rowGap: '0.75rem' }}>
                <MatchesHeading>{t('teamDashboardPage.myMatchesTitle')}</MatchesHeading>
                <SectionAction>
                  <SeasonDropdown>
                    <SeasonSelect
                      value={selectedOverviewMatchesSeason}
                      onChange={(e) => setSelectedOverviewMatchesSeason(e.target.value)}
                    >
                      <option value="">{t('teamDashboardPage.selectSeasonPlaceholder')}</option>
                      {seasonsData?.data?.map((season) => (
                        <option key={season.id} value={season.id}>
                          {season.name}
                        </option>
                      ))}
                    </SeasonSelect>
                    <DropdownIcon>▼</DropdownIcon>
                  </SeasonDropdown>
                </SectionAction>
              </SectionHeader>
              {isPlayerView && schedulingInboxData?.success && Array.isArray(schedulingInboxData.matches) && schedulingInboxData.matches.length > 0 && (
                <SchedulingInboxSection>
                  <SchedulingInboxHeading>{t('teamDashboardPage.fixturesNeedingDatetime')}</SchedulingInboxHeading>
                  {schedulingInboxData.matches.map((row) => (
                    <SchedulingInboxRow key={`${row.league_match_id}-${row.my_team_id}`}>
                      <SchedulingInboxNames>
                        {String(row.my_team_id) !== String(effectiveTeamId) && (
                          <SchedulingInboxBadge>{t('teamDashboardPage.otherTeamBadge')}</SchedulingInboxBadge>
                        )}
                        {row.team1_name} vs {row.team2_name}
                      </SchedulingInboxNames>
                      <SchedulingInboxBtn type="button" onClick={() => handleOpenSchedulingInbox(row)}>
                        {t('teamDashboardPage.openSchedule')}
                      </SchedulingInboxBtn>
                    </SchedulingInboxRow>
                  ))}
                </SchedulingInboxSection>
              )}
              <MatchesList>
                {overviewMatchesListLoading ? (
                  <div style={{ textAlign: 'center', padding: '1rem', color: '#6b7280' }}>
                    {t('teamDashboardPage.loadingMatches')}
                  </div>
                ) : overviewMatchesDashboard?.recentMatches?.length > 0 ? (() => {
                  const lockingEnabled =
                    overviewMatchesDashboard?.season?.score_submission_locking_enabled !== false;
                  const isWindowUnlocked = (windowNumber, seasonStartDate) => {
                    if (!lockingEnabled) return true;
                    if (!seasonStartDate) {
                      return true;
                    }

                    const now = new Date();
                    let startDateStr;
                    if (typeof seasonStartDate === 'string') {
                      startDateStr = seasonStartDate.split('T')[0];
                    } else if (seasonStartDate instanceof Date) {
                      const year = seasonStartDate.getFullYear();
                      const month = String(seasonStartDate.getMonth() + 1).padStart(2, '0');
                      const day = String(seasonStartDate.getDate()).padStart(2, '0');
                      startDateStr = `${year}-${month}-${day}`;
                    } else {
                      startDateStr = seasonStartDate.toISOString().split('T')[0];
                    }

                    const [startYear, startMonth, startDay] = startDateStr.split('-').map(Number);
                    const startDate = new Date(startYear, startMonth - 1, startDay);

                    const nowYear = now.getFullYear();
                    const nowMonth = now.getMonth() + 1;
                    const nowDay = now.getDate();
                    const currentDate = new Date(nowYear, nowMonth - 1, nowDay);

                    const daysToAdd = (windowNumber - 1) * 28;
                    const windowStartDate = new Date(startYear, startMonth - 1, startDay);
                    windowStartDate.setDate(windowStartDate.getDate() + daysToAdd);

                    return currentDate >= windowStartDate;
                  };

                  const seasonStartDate = overviewMatchesDashboard?.season?.start_date || null;
                  const matchTeamId =
                    overviewMatchesDashboard?.team?.id != null
                      ? String(overviewMatchesDashboard.team.id)
                      : dashboard?.team?.id != null
                        ? String(dashboard.team.id)
                        : '';

                  return overviewMatchesDashboard.recentMatches.map((match, index) => {
                    const windowNumber = Math.floor(index / 2) + 1;
                    const isUnlocked = isWindowUnlocked(windowNumber, seasonStartDate);
                    const currentTeamId = matchTeamId;
                    const submittedById = match?.submitted_by_team_id
                      ? String(match.submitted_by_team_id)
                      : '';
                    const isOriginalSubmitter =
                      match.status === 'disputed' &&
                      currentTeamId &&
                      submittedById &&
                      currentTeamId === submittedById;
                    const isHomeSet = match.is_home != null;
                    const isHome = match.is_home === true;

                    return (
                      <MatchCard key={match.id || index}>
                        <MatchInfo>
                          <MatchDate>
                            {schedulingNeedsMap.has(String(match.id)) && (
                              <SchedulingMatchBadge title={t('teamDashboardPage.schedulingDateTimeNotSetTitle')}>
                                {t('teamDashboardPage.schedulingTimeTbdBadge')}
                              </SchedulingMatchBadge>
                            )}
                            <MatchHomeAwayRow>
                              <HomeAwayButton
                                type="button"
                                $isHome={isHome}
                                $isUnset={!isHomeSet}
                                onClick={() => {
                                  setHomeAwayModalType(isHome ? 'home' : 'away');
                                  setHomeAwayModalOpen(true);
                                }}
                                title={
                                  isHomeSet
                                    ? isHome
                                      ? t('teamDashboardPage.homeAwayTitleHome')
                                      : t('teamDashboardPage.homeAwayTitleAway')
                                    : t('teamDashboardPage.homeAwayTitleUnset')
                                }
                              >
                                {isHomeSet
                                  ? isHome
                                    ? t('teamDashboardPage.homeLabel')
                                    : t('teamDashboardPage.awayLabel')
                                  : t('teamDashboardPage.homeAwayUnsetDash')}
                              </HomeAwayButton>
                            </MatchHomeAwayRow>
                            {(match.courtDetails?.court_name || (match.venue && match.venue !== 'TBD')) && (
                              <div
                                style={{
                                  marginTop: '0.35rem',
                                  fontWeight: 500,
                                  fontSize: '0.8125rem',
                                  lineHeight: 1.4,
                                }}
                              >
                                {match.courtDetails?.court_name || match.venue}
                                {(match.courtDetails?.match_date || match.courtDetails?.match_time) && (
                                  <>
                                    {' • '}
                                    {match.courtDetails?.match_date &&
                                      (() => {
                                        const date = new Date(match.courtDetails.match_date);
                                        return date.toLocaleDateString('en-GB', {
                                          day: 'numeric',
                                          month: 'short',
                                          year: 'numeric',
                                        });
                                      })()}
                                    {match.courtDetails?.match_time &&
                                      (() => {
                                        const timeStr = String(match.courtDetails.match_time);
                                        const timeMatch = timeStr.match(/^(\d{2}):(\d{2})/);
                                        if (timeMatch) {
                                          return ` ${timeMatch[1]}:${timeMatch[2]}`;
                                        }
                                        return ` ${timeStr}`;
                                      })()}
                                  </>
                                )}
                                {!match.courtDetails?.match_date &&
                                  !match.courtDetails?.match_time &&
                                  match.matchDateTime &&
                                  ` (${match.matchDateTime})`}
                              </div>
                            )}
                          </MatchDate>
                          <OpponentMatchTrigger
                            type="button"
                            onClick={() => openOpponentTeamSheet(match)}
                            disabled={match.opponentId == null}
                            title={t('teamDashboardPage.opponentSheetOpenTitle')}
                          >
                            <span className="opp-line opp-vs">{t('teamDashboardPage.vsLabel')}</span>
                            {match.opponentTeamLogoUrl ? (
                              <OpponentMiniLogo
                                src={publicAssetUrl(match.opponentTeamLogoUrl)}
                                alt=""
                              />
                            ) : (
                              <OpponentMiniLogoPh>
                                {(match.opponent || '?').trim().slice(0, 1).toUpperCase()}
                              </OpponentMiniLogoPh>
                            )}
                            <span className="opp-line opp-name">{match.opponent}</span>
                          </OpponentMatchTrigger>
                          {match.score && (
                            <MatchScore
                              $isWin={match.score.includes('Won')}
                              $isLoss={match.score.includes('Lost')}
                            >
                              {match.score}
                            </MatchScore>
                          )}
                          {match.status === 'disputed' && (
                            <DisputedBanner>
                              <strong>{t('teamDashboardPage.disputedTitle')}</strong>
                              <span>
                                {isOriginalSubmitter
                                  ? t('teamDashboardPage.disputedSubOriginal')
                                  : t('teamDashboardPage.disputedSubRaised')}
                              </span>
                              {isOriginalSubmitter ? (
                                <DisputedBannerAction onClick={() => handleOpenScore(match)}>
                                  {t('teamDashboardPage.resubmitScore')}
                                </DisputedBannerAction>
                              ) : (
                                <span style={{ fontSize: '0.7rem', color: '#b45309' }}>
                                  {t('teamDashboardPage.disputeTipViewer')}
                                </span>
                              )}
                            </DisputedBanner>
                          )}
                        </MatchInfo>
                        <MatchStatus>
                          <MatchActions>
                            <MatchScheduleButton
                              onClick={() => handleOpenSchedule(match)}
                              disabled={overviewMatchesSeasonCompleted}
                            />
                            <MatchChatButton
                              onClick={() => handleOpenChat(match)}
                              unreadCount={unreadCounts[match.id]?.count || 0}
                              disabled={overviewMatchesSeasonCompleted}
                            />
                            {(() => {
                              const hasDetails = !!(
                                match.courtDetails?.court_name ||
                                match.courtDetails?.match_date ||
                                match.courtDetails?.match_time ||
                                match.match_notes
                              );

                              return hasDetails ? (
                                <MatchDetailsButton
                                  onClick={() => handleOpenMatchDetails(match)}
                                  title={t('teamDashboardPage.matchDetailsTitleViewEdit')}
                                  style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' }}
                                >
                                  {t('teamDashboardPage.matchDetailsBtnView')}
                                </MatchDetailsButton>
                              ) : (
                                <MatchDetailsButton
                                  onClick={() => handleOpenMatchDetails(match)}
                                  title={t('teamDashboardPage.matchDetailsTitleSubmit')}
                                >
                                  {t('teamDashboardPage.matchDetailsBtnShort')}
                                </MatchDetailsButton>
                              );
                            })()}
                            {(user?.type === 'player' || user?.is_active) && (() => {
                              const hasScore = match.status !== 'upcoming';
                              return (
                                <MatchScoreButton
                                  onClick={() => handleOpenScore(match)}
                                  hasScore={hasScore}
                                  seasonCompleted={overviewMatchesSeasonCompleted}
                                  isWindowLocked={!isUnlocked && !hasScore}
                                  scoreStatus={(() => {
                                    const pendingVerification =
                                      overviewMatchesDashboard?.pendingScoreVerifications?.find(
                                        (v) => v.matchId === match.id
                                      );

                                    if (pendingVerification) {
                                      const currentTeam = String(overviewMatchesDashboard?.team?.id || '');
                                      const submitter = String(pendingVerification.submittedBy);
                                      const isSubmitter = submitter === currentTeam;
                                      return isSubmitter ? 'pending_confirmation' : 'pending_verification';
                                    } else if (match.status === 'completed') {
                                      return 'verified';
                                    } else if (match.status === 'score_submitted') {
                                      return 'pending_confirmation';
                                    } else if (match.status === 'disputed') {
                                      return 'disputed';
                                    }
                                    return null;
                                  })()}
                                />
                              );
                            })()}
                          </MatchActions>
                          {match.status !== 'upcoming' && (
                            <StatusBadge
                              $status={match.status === 'completed' ? 'completed' : 'pending-score'}
                            >
                              {match.status === 'completed'
                                ? t('teamDashboardPage.statusCompleted')
                                : t('teamDashboardPage.statusScorePending')}
                            </StatusBadge>
                          )}
                          <StatusArrow>›</StatusArrow>
                        </MatchStatus>
                      </MatchCard>
                    );
                  });
                })() : (
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '2rem 1rem',
                      color: '#6b7280',
                      background: 'rgba(255, 255, 255, 0.7)',
                      borderRadius: '16px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      backdropFilter: 'blur(8px)',
                    }}
                  >
                    <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                      {t('teamDashboardPage.noMatchesYetTitle')}
                    </div>
                    <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                      {t('teamDashboardPage.noMatchesYetBody')}
                    </div>
                  </div>
                )}
              </MatchesList>
            </MatchesSection>
          </>
        )}

        {activeSection === 'stats' && (
          <div>
            <StatsHeader>
              <StatsTitle>{t('teamDashboardPage.statisticsTitle')}</StatsTitle>
              <StatsControls>
                <SeasonDropdown>
                  <SeasonSelect
                    value={selectedStatsSeason}
                    onChange={(e) => setSelectedStatsSeason(e.target.value)}
                  >
                    {seasonsData?.data?.length > 0 ? (
                      seasonsData.data.map((season) => (
                        <option key={season.id} value={season.id}>
                          {season.name}
                        </option>
                      ))
                    ) : (
                      <option>{t('teamDashboardPage.loadingSeasonsOption')}</option>
                    )}
                  </SeasonSelect>
                  <DropdownIcon>▼</DropdownIcon>
                </SeasonDropdown>
              </StatsControls>
            </StatsHeader>

            {dashboardLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '1.2rem', color: '#6b7280' }}>{t('teamDashboardPage.loadingStatistics')}</div>
              </div>
            ) : dashboardError ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '1.2rem', color: '#d52b1e' }}>{t('teamDashboardPage.errorLoadingStatistics')}</div>
              </div>
            ) : (
              <>
                {(() => {
                  const now = new Date();
                  const currentSeason = dashboard?.season;

                  if (
                    currentSeason &&
                    isSeasonPlayWindowSwiss(currentSeason, now) &&
                    currentSeason.status !== 'completed'
                  ) {
                    return null;
                  }
                  
                  // Check for upcoming season (either from currentSeason or from seasonsData)
                  let upcomingSeason = null;
                  
                  // First check if currentSeason is upcoming
                  if (currentSeason) {
                    const startDate = new Date(currentSeason.start_date);
                    const isUpcoming = startDate > now && 
                      (currentSeason.status === 'upcoming' || currentSeason.status === 'registration' || !currentSeason.status);
                    if (isUpcoming) {
                      upcomingSeason = currentSeason;
                    }
                  }
                  
                  // If no upcoming season from currentSeason, check seasonsData for upcoming seasons
                  if (!upcomingSeason && seasonsData?.data) {
                    upcomingSeason = seasonsData.data.find(s => {
                      const startDate = new Date(s.start_date);
                      return startDate > now && 
                        (s.status === 'upcoming' || s.status === 'registration' || !s.status);
                    });
                  }
                  
                  // Only show message if there's an upcoming season and team is new to that season
                  if (upcomingSeason && dashboard?.isNewMidSeasonTeam) {
                    const startDate = new Date(upcomingSeason.start_date);
                    const registrationStart = upcomingSeason.registration_start ? new Date(upcomingSeason.registration_start) : null;
                    const registrationEnd = upcomingSeason.registration_end ? new Date(upcomingSeason.registration_end) : null;
                    
                    // Check if registration period is active
                    const isRegistrationActive = registrationStart && registrationEnd && 
                      now >= registrationStart && now <= registrationEnd;
                    
                    // Format start date
                    const formattedStartDate = startDate.toLocaleDateString(localeTag, { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    });
                    
                    const title = t('teamDashboardPage.midSeasonStartingSoonTitle', { name: upcomingSeason.name });
                    let message = t('teamDashboardPage.midSeasonStatsBody', {
                      seasonName: upcomingSeason.name.toLowerCase(),
                      date: formattedStartDate,
                    });
                    
                    if (isRegistrationActive) {
                      const formattedRegEnd = registrationEnd.toLocaleDateString(localeTag, { 
                        day: 'numeric', 
                        month: 'long', 
                        year: 'numeric' 
                      });
                      message = t('teamDashboardPage.midSeasonStatsBodyRegistration', {
                        regEnd: formattedRegEnd,
                        seasonName: upcomingSeason.name.toLowerCase(),
                        date: formattedStartDate,
                      });
                    } else if (registrationEnd && now > registrationEnd) {
                      message = t('teamDashboardPage.midSeasonStatsBody', {
                        seasonName: upcomingSeason.name.toLowerCase(),
                        date: formattedStartDate,
                      });
                    }
                    
                    return (
                      <MidSeasonMessage>
                        <MidSeasonIcon>📅</MidSeasonIcon>
                        <MidSeasonContent>
                          <MidSeasonTitle>{title}</MidSeasonTitle>
                          <MidSeasonText>{message}</MidSeasonText>
                        </MidSeasonContent>
                      </MidSeasonMessage>
                    );
                  }
                  
                  // No message to show
                  return null;
                })()}
                <StatsCardsGrid>
                <StatsCard>
                  <StatsCardIcon>🏆</StatsCardIcon>
                  <StatsCardContent>
                    <StatsCardLabel>{t('teamDashboardPage.statsWinPct')}</StatsCardLabel>
                    <StatsCardValue>
                      {dashboard?.teamStats?.games > 0 
                        ? Math.round((dashboard.teamStats.wins / dashboard.teamStats.games) * 100) + '%'
                        : '0%'
                      }
                    </StatsCardValue>
                    <StatsCardSubtext>
                      {t('teamDashboardPage.statsGamesFraction', {
                        wins: dashboard?.teamStats?.wins || 0,
                        games: dashboard?.teamStats?.games || 0,
                      })}
                    </StatsCardSubtext>
                  </StatsCardContent>
                </StatsCard>
                <StatsCard>
                  <StatsCardIcon>📊</StatsCardIcon>
                  <StatsCardContent>
                    <StatsCardLabel>{t('teamDashboardPage.statsTotalPoints')}</StatsCardLabel>
                    <StatsCardValue>
                      {dashboard?.currentStanding?.points || 0}
                    </StatsCardValue>
                    <StatsCardSubtext>{t('teamDashboardPage.statsSubThisSeason')}</StatsCardSubtext>
                  </StatsCardContent>
                </StatsCard>
                <StatsCard>
                  <StatsCardIcon>📅</StatsCardIcon>
                  <StatsCardContent>
                    <StatsCardLabel>{t('teamDashboardPage.statsWinStreak')}</StatsCardLabel>
                    <StatsCardValue>
                      {dashboard?.teamStats?.winStreak || 0}
                    </StatsCardValue>
                    <StatsCardSubtext>{t('teamDashboardPage.statsSubCurrentStreak')}</StatsCardSubtext>
                  </StatsCardContent>
                </StatsCard>
                <StatsCard>
                  <StatsCardIcon>🎯</StatsCardIcon>
                  <StatsCardContent>
                    <StatsCardLabel>{t('teamDashboardPage.statsSetDifference')}</StatsCardLabel>
                    <StatsCardValue>
                      {(() => {
                        const setDiff = parseInt(dashboard?.currentStanding?.setDiff) || 0;
                        return setDiff >= 0 ? `+${setDiff}` : `${setDiff}`;
                      })()}
                    </StatsCardValue>
                    <StatsCardSubtext>{t('teamDashboardPage.statsSubOverallPerformance')}</StatsCardSubtext>
                  </StatsCardContent>
                </StatsCard>
              </StatsCardsGrid>
              </>
            )}

            {!dashboardLoading && !dashboardError && (
              <ChartsGrid>
                <ChartCard>
                  <ChartTitle>{t('teamDashboardPage.chartPerformanceTrend')}</ChartTitle>
                  <ChartContainer>
                    {dashboard?.recentMatches?.length > 0 ? (
                      <div>
                        <PerformanceChartContainer>
                          {dashboard.recentMatches.slice(0, 6).map((match, index) => {
                            const isWin = match.score && match.score.includes('Won');
                            const isLoss = match.score && match.score.includes('Lost');
                            const isCompleted = match.status === 'completed';
                            const isUpcoming = match.status === 'upcoming';
                            
                            // Calculate bar height and color based on performance
                            let barHeight = 40; // Default height for upcoming/pending
                            let barColor = '#9ca3af'; // Gray for upcoming/pending
                            
                            if (isWin) {
                              barHeight = 100; // High for wins
                              barColor = '#10b981'; // Green
                            } else if (isLoss) {
                              barHeight = 60; // Lower for losses
                              barColor = '#ef4444'; // Red
                            } else if (isUpcoming) {
                              barHeight = 40; // Low for upcoming
                              barColor = '#9ca3af'; // Gray
                            }
                            
                            return (
                              <div key={match.id || index} style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                flex: 1,
                                margin: '0 0.125rem',
                                minWidth: '0'
                              }}>
                                <div style={{
                                  width: '100%',
                                  height: `${barHeight}px`,
                                  borderRadius: '4px 4px 0 0',
                                  display: 'flex',
                                  alignItems: 'end',
                                  justifyContent: 'center',
                                  color: '#ffffff',
                                  fontSize: window.innerWidth <= 768 ? '0.6rem' : '0.75rem',
                                  fontWeight: '600',
                                  padding: window.innerWidth <= 768 ? '0.125rem' : '0.25rem',
                                  minHeight: '20px',
                                  backgroundColor: barColor,
                                  border: '1px solid #e5e7eb'
                                }}>
                                  {isWin ? t('teamDashboardPage.chartPerfLetterWin') : isLoss ? t('teamDashboardPage.chartPerfLetterLoss') : t('teamDashboardPage.chartPerfLetterMid')}
                                </div>
                                <div style={{
                                  fontSize: window.innerWidth <= 768 ? '0.6rem' : '0.7rem',
                                  color: '#6b7280',
                                  marginTop: '0.25rem',
                                  textAlign: 'center',
                                  fontWeight: '500'
                                }}>
                                  {t('teamDashboardPage.chartPerfLetterMid')}{index + 1}
                                </div>
                                <ChartOpponentHit
                                  type="button"
                                  onClick={() => openOpponentTeamSheet(match)}
                                  disabled={match.opponentId == null}
                                  title={t('teamDashboardPage.opponentSheetOpenTitle')}
                                >
                                  {match.opponentTeamLogoUrl ? (
                                    <ChartOppLogo
                                      src={publicAssetUrl(match.opponentTeamLogoUrl)}
                                      alt=""
                                    />
                                  ) : null}
                                  <span
                                    className="chart-opp-line"
                                    style={{
                                      fontSize: window.innerWidth <= 768 ? '0.5rem' : '0.6rem',
                                      color: '#9ca3af',
                                      textAlign: 'center',
                                      maxWidth: window.innerWidth <= 768 ? '40px' : '60px',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      fontWeight: 700,
                                    }}
                                  >
                                    {match.opponent?.split(' ').slice(0, 2).join(' ') ||
                                      t('teamDashboardPage.venueTbd')}
                                  </span>
                                </ChartOpponentHit>
                              </div>
                            );
                          })}
                        </PerformanceChartContainer>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'center',
                          marginTop: '1rem',
                          fontSize: '0.75rem',
                          color: '#6b7280'
                        }}>
                          <div style={{ marginRight: '1rem' }}>
                            <span style={{ color: '#10b981', fontWeight: '600' }}>■</span> {t('teamDashboardPage.chartLegendWin')}
                          </div>
                          <div style={{ marginRight: '1rem' }}>
                            <span style={{ color: '#ef4444', fontWeight: '600' }}>■</span> {t('teamDashboardPage.chartLegendLoss')}
                          </div>
                          <div>
                            <span style={{ color: '#9ca3af', fontWeight: '600' }}>■</span> {t('teamDashboardPage.chartLegendUpcoming')}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '2rem', 
                        color: '#6b7280',
                        fontSize: '0.9rem'
                      }}>
                        {t('teamDashboardPage.chartNoMatchData')}
                      </div>
                    )}
                  </ChartContainer>
                </ChartCard>
                <ChartCard>
                  <ChartTitle>{t('teamDashboardPage.chartWinsVsLosses')}</ChartTitle>
                  <ChartContainer>
                    {dashboard?.teamStats?.games > 0 ? (
                      <WinLossChartContainer>
                        <WinLossStatsContainer>
                          <WinLossStatItem>
                            <div style={{ 
                              fontSize: window.innerWidth <= 768 ? '1.5rem' : '2rem', 
                              fontWeight: 'bold', 
                              color: '#10b981',
                              marginBottom: '0.25rem'
                            }}>
                              {dashboard.teamStats.wins}
                            </div>
                            <div style={{ fontSize: window.innerWidth <= 768 ? '0.75rem' : '0.875rem', color: '#6b7280' }}>{t('teamDashboardPage.chartWinsLabel')}</div>
                            <div style={{ fontSize: window.innerWidth <= 768 ? '0.65rem' : '0.75rem', color: '#10b981', fontWeight: '600' }}>
                              {Math.round((dashboard.teamStats.wins / dashboard.teamStats.games) * 100)}%
                            </div>
                          </WinLossStatItem>
                          <WinLossStatItem>
                            <div style={{ 
                              fontSize: window.innerWidth <= 768 ? '1.5rem' : '2rem', 
                              fontWeight: 'bold', 
                              color: '#d52b1e',
                              marginBottom: '0.25rem'
                            }}>
                              {dashboard.teamStats.losses}
                            </div>
                            <div style={{ fontSize: window.innerWidth <= 768 ? '0.75rem' : '0.875rem', color: '#6b7280' }}>{t('teamDashboardPage.chartLossesLabel')}</div>
                            <div style={{ fontSize: window.innerWidth <= 768 ? '0.65rem' : '0.75rem', color: '#d52b1e', fontWeight: '600' }}>
                              {Math.round((dashboard.teamStats.losses / dashboard.teamStats.games) * 100)}%
                            </div>
                          </WinLossStatItem>
                          <WinLossStatItem>
                            <div style={{ 
                              fontSize: window.innerWidth <= 768 ? '1.5rem' : '2rem', 
                              fontWeight: 'bold', 
                              color: '#6b7280',
                              marginBottom: '0.25rem'
                            }}>
                              {dashboard.teamStats.games}
                            </div>
                            <div style={{ fontSize: window.innerWidth <= 768 ? '0.75rem' : '0.875rem', color: '#6b7280' }}>{t('teamDashboardPage.chartTotalLabel')}</div>
                            <div style={{ fontSize: window.innerWidth <= 768 ? '0.65rem' : '0.75rem', color: '#6b7280', fontWeight: '600' }}>
                              {t('teamDashboardPage.chartGamesLabel')}
                            </div>
                          </WinLossStatItem>
                        </WinLossStatsContainer>
                        <div style={{ 
                          display: 'flex', 
                          height: '60px',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border: '1px solid #e5e7eb'
                        }}>
                          <div style={{
                            flex: dashboard.teamStats.wins,
                            background: '#10b981',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: '600',
                            fontSize: '0.875rem'
                          }}>
                            {dashboard.teamStats.wins > 0 && t('teamDashboardPage.chartBarWinCount', { count: dashboard.teamStats.wins })}
                          </div>
                          <div style={{
                            flex: dashboard.teamStats.losses,
                            background: '#d52b1e',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: '600',
                            fontSize: '0.875rem'
                          }}>
                            {dashboard.teamStats.losses > 0 && t('teamDashboardPage.chartBarLossCount', { count: dashboard.teamStats.losses })}
                          </div>
                        </div>
                      </WinLossChartContainer>
                    ) : (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '2rem', 
                        color: '#6b7280',
                        fontSize: '0.9rem'
                      }}>
                        {t('teamDashboardPage.chartNoMatchData')}
                      </div>
                    )}
                  </ChartContainer>
                </ChartCard>
                <ChartCard>
                  <ChartTitle>{t('teamDashboardPage.chartRankingsTitle')}</ChartTitle>
                  <ChartContainer>
                    {(() => {
                      const selectedSeason = seasonsData?.data?.find(s => s.id === selectedStatsSeason);
                      const now = new Date();
                      const shouldShowRankings = seasonAllowsStandingsUI(selectedSeason, now);
                      const isUpcomingSeason =
                        selectedSeason &&
                        ['upcoming', 'registration'].includes(selectedSeason.status) &&
                        !shouldShowRankings;

                      if (!shouldShowRankings && selectedSeason) {
                        const startDate = new Date(selectedSeason.start_date).toLocaleDateString(localeTag, { day: 'numeric', month: 'long', year: 'numeric' });
                        return (
                          <div style={{ 
                            textAlign: 'center', 
                            padding: '2rem', 
                            color: '#6b7280',
                            fontSize: '0.9rem'
                          }}>
                            {isUpcomingSeason 
                              ? t('teamDashboardPage.rankingsStartsOnBody', { name: selectedSeason.name, date: startDate })
                              : t('teamDashboardPage.rankingsWhenBeginsShort')
                            }
                          </div>
                        );
                      }
                      
                      // Show rankings for active or completed seasons
                      return <LeaderboardTabs />;
                    })()}
                  </ChartContainer>
                </ChartCard>
              </ChartsGrid>
            )}

                          </div>
        )}
                        
        {activeSection === 'schedule' && (
                          <div>
            <SectionHeader>
              <SectionTitle>{t('teamDashboardPage.scheduleTitle')}</SectionTitle>
              <SectionAction>
                <SeasonDropdown>
                  <SeasonSelect
                    value={selectedScheduleSeason}
                    onChange={(e) => setSelectedScheduleSeason(e.target.value)}
                  >
                    <option value="">{t('teamDashboardPage.selectSeasonPlaceholder')}</option>
                    {seasonsData?.data?.map((season) => (
                      <option key={season.id} value={season.id}>
                        {season.name}
                      </option>
                    ))}
                  </SeasonSelect>
                  <DropdownIcon>▼</DropdownIcon>
                </SeasonDropdown>
              </SectionAction>
            </SectionHeader>
            <ScheduleList>
              {scheduleDashboardLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div style={{ fontSize: '1.2rem', color: '#6b7280' }}>{t('teamDashboardPage.loadingSchedule')}</div>
                </div>
              ) : scheduleDashboard?.recentMatches?.length > 0 ? (
                (() => {
                  // Helper function to calculate which window a match belongs to (1-2, 3-4, 5-6)
                  const getMatchWindow = (matchIndex) => {
                    // Window 1: matches 0-1 (index 0, 1)
                    // Window 2: matches 2-3 (index 2, 3)
                    // Window 3: matches 4-5 (index 4, 5)
                    return Math.floor(matchIndex / 2) + 1;
                  };
                  
                  // When score_submission_locking_enabled is false, all matches are unlocked from season start
                  const lockingEnabledSchedule = scheduleDashboard?.season?.score_submission_locking_enabled !== false;
                  const isWindowUnlocked = (windowNumber, seasonStartDate) => {
                    if (!lockingEnabledSchedule) return true; // Season has locking disabled: all matches unlocked
                    if (!seasonStartDate) {
                      return true; // If no season date, allow all (fallback)
                    }
                    
                    const now = new Date();
                    // Parse the season start date - handle both string and Date object
                    // Extract date string (YYYY-MM-DD) to avoid timezone issues
                    let startDateStr;
                    if (typeof seasonStartDate === 'string') {
                      startDateStr = seasonStartDate.split('T')[0];
                    } else if (seasonStartDate instanceof Date) {
                      const year = seasonStartDate.getFullYear();
                      const month = String(seasonStartDate.getMonth() + 1).padStart(2, '0');
                      const day = String(seasonStartDate.getDate()).padStart(2, '0');
                      startDateStr = `${year}-${month}-${day}`;
                    } else {
                      startDateStr = seasonStartDate.toISOString().split('T')[0];
                    }
                    
                    // Parse as local date to avoid timezone conversion issues
                    const [startYear, startMonth, startDay] = startDateStr.split('-').map(Number);
                    const startDate = new Date(startYear, startMonth - 1, startDay);
                    
                    // Get current date as local date (YYYY-MM-DD)
                    const nowYear = now.getFullYear();
                    const nowMonth = now.getMonth() + 1;
                    const nowDay = now.getDate();
                    const currentDate = new Date(nowYear, nowMonth - 1, nowDay);
                    
                    // Window 1 starts immediately (day 0)
                    // Window 2 starts after 28 days (day 28)
                    // Window 3 starts after 56 days (day 56)
                    const daysToAdd = (windowNumber - 1) * 28;
                    const windowStartDate = new Date(startYear, startMonth - 1, startDay);
                    windowStartDate.setDate(windowStartDate.getDate() + daysToAdd);
                    
                    const isUnlocked = currentDate >= windowStartDate;
                    
                    return isUnlocked;
                  };
                  
                  // Get season start date from schedule dashboard
                  const seasonStartDate = scheduleDashboard?.season?.start_date || null;
                  
                  // Group matches by window (pairs of 2) - same as overview
                  const groupedMatches = [];
                  let currentWindow = null;
                  
                  scheduleDashboard.recentMatches.forEach((match, index) => {
                    const windowNumber = getMatchWindow(index);
                    const isUnlocked = isWindowUnlocked(windowNumber, seasonStartDate);
                    
                    if (!currentWindow || currentWindow.windowNumber !== windowNumber) {
                      // Start a new window group
                      currentWindow = {
                        windowNumber,
                        isUnlocked,
                        matches: []
                      };
                      groupedMatches.push(currentWindow);
                    }
                    
                    currentWindow.matches.push({ match, index, isUnlocked });
                  });
                  
                  return groupedMatches.map((windowGroup, groupIndex) => {
                    const rangeStart = (windowGroup.windowNumber - 1) * 2 + 1;
                    const rangeEnd = windowGroup.windowNumber * 2;
                    
                    return (
                      <MatchWindowGroup key={`schedule-window-${windowGroup.windowNumber}`}>
                        <MatchWindowLabel>
                          {t('teamDashboardPage.matchWindowRange', { rangeStart, rangeEnd })}{' '}
                          {windowGroup.isUnlocked ? t('teamDashboardPage.matchWindowUnlocked') : t('teamDashboardPage.matchWindowLocked')}
                        </MatchWindowLabel>
                        {windowGroup.matches.map(({ match, index, isUnlocked }) => {
                          const hasScore = match.status !== 'upcoming';
                          const isHomeSet = match.is_home != null;
                          const isHome = match.is_home === true;
                          return (
                            <ScheduleCard key={match.id || index} $isLocked={!isUnlocked && !hasScore}>
                              <ScheduleDate>
                                <HomeAwayButton
                                  type="button"
                                  style={{ width: '100%', justifyContent: 'center' }}
                                  $isHome={isHome}
                                  $isUnset={!isHomeSet}
                                  onClick={() => {
                                    setHomeAwayModalType(isHome ? 'home' : 'away');
                                    setHomeAwayModalOpen(true);
                                  }}
                                  title={
                                    isHomeSet
                                      ? isHome
                                        ? t('teamDashboardPage.homeAwayTitleHome')
                                        : t('teamDashboardPage.homeAwayTitleAway')
                                      : t('teamDashboardPage.homeAwayTitleUnset')
                                  }
                                >
                                  {isHomeSet
                                    ? isHome
                                      ? t('teamDashboardPage.homeLabel')
                                      : t('teamDashboardPage.awayLabel')
                                    : t('teamDashboardPage.homeAwayUnsetDash')}
                                </HomeAwayButton>
                                {!isUnlocked && !hasScore && (
                                  <div
                                    style={{
                                      fontSize: '0.7rem',
                                      color: '#f59e0b',
                                      fontWeight: '600',
                                      marginTop: '0.25rem',
                                      textAlign: 'center',
                                    }}
                                  >
                                    {t('teamDashboardPage.scheduleLockedBadge')}
                                  </div>
                                )}
                              </ScheduleDate>
                              <ScheduleInfo>
                                <ScheduleOpponent>
                                  <OpponentMatchTrigger
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openOpponentTeamSheet(match);
                                    }}
                                    disabled={match.opponentId == null}
                                    style={{ marginTop: 0 }}
                                    title={t('teamDashboardPage.opponentSheetOpenTitle')}
                                  >
                                    <span className="opp-line opp-vs">{t('teamDashboardPage.vsLabel')}</span>
                                    {match.opponentTeamLogoUrl ? (
                                      <OpponentMiniLogo
                                        src={publicAssetUrl(match.opponentTeamLogoUrl)}
                                        alt=""
                                      />
                                    ) : (
                                      <OpponentMiniLogoPh>
                                        {(match.opponent || '?').trim().slice(0, 1).toUpperCase()}
                                      </OpponentMiniLogoPh>
                                    )}
                                    <span className="opp-line opp-name">{match.opponent}</span>
                                  </OpponentMatchTrigger>
                                </ScheduleOpponent>
                                {(match.courtDetails?.court_name ||
                                  (match.venue && match.venue !== 'TBD') ||
                                  match.courtDetails?.match_date ||
                                  match.courtDetails?.match_time ||
                                  match.matchDateTime) && (
                                  <ScheduleVenue style={{ fontWeight: '600', color: '#1f2937', marginTop: '0.25rem' }}>
                                    📍 {(match.courtDetails?.court_name || (match.venue && match.venue !== 'TBD')) && (
                                      <>
                                        {match.courtDetails?.court_name || match.venue}
                                        {(match.courtDetails?.match_date || match.courtDetails?.match_time) && (
                                          <>
                                            {' • '}
                                            {match.courtDetails?.match_date &&
                                              (() => {
                                                const date = new Date(match.courtDetails.match_date);
                                                return date.toLocaleDateString('en-GB', {
                                                  day: 'numeric',
                                                  month: 'short',
                                                  year: 'numeric',
                                                });
                                              })()}
                                            {match.courtDetails?.match_time &&
                                              (() => {
                                                const timeStr = String(match.courtDetails.match_time);
                                                const timeMatch = timeStr.match(/^(\d{2}):(\d{2})/);
                                                if (timeMatch) {
                                                  return ` ${timeMatch[1]}:${timeMatch[2]}`;
                                                }
                                                return ` ${timeStr}`;
                                              })()}
                                          </>
                                        )}
                                        {!match.courtDetails?.match_date &&
                                          !match.courtDetails?.match_time &&
                                          match.matchDateTime &&
                                          ` • ${match.matchDateTime}`}
                                      </>
                                    )}
                                    {!match.courtDetails?.court_name && (!match.venue || match.venue === 'TBD') && (
                                      <>
                                        {(match.courtDetails?.match_date || match.courtDetails?.match_time) && (
                                          <>
                                            {match.courtDetails?.match_date &&
                                              (() => {
                                                const date = new Date(match.courtDetails.match_date);
                                                return date.toLocaleDateString('en-GB', {
                                                  day: 'numeric',
                                                  month: 'short',
                                                  year: 'numeric',
                                                });
                                              })()}
                                            {match.courtDetails?.match_time &&
                                              (() => {
                                                const timeStr = String(match.courtDetails.match_time);
                                                const timeMatch = timeStr.match(/^(\d{2}):(\d{2})/);
                                                if (timeMatch) {
                                                  return ` ${timeMatch[1]}:${timeMatch[2]}`;
                                                }
                                                return ` ${timeStr}`;
                                              })()}
                                          </>
                                        )}
                                        {!match.courtDetails?.match_date &&
                                          !match.courtDetails?.match_time &&
                                          match.matchDateTime &&
                                          match.matchDateTime}
                                      </>
                                    )}
                                  </ScheduleVenue>
                                )}
                                {match.score && (
                                  <MatchScore 
                                    $isWin={match.score.includes('Won')}
                                    $isLoss={match.score.includes('Lost')}
                                  >
                                    {match.score}
                                  </MatchScore>
                                )}
                              </ScheduleInfo>
                              <ScheduleActions>
                                <MatchActions>
                                  <MatchScheduleButton
                                    onClick={() => handleOpenSchedule(match)}
                                    disabled={scheduleSeasonCompleted}
                                  />
                                  <MatchChatButton
                                    onClick={() => handleOpenChat(match)}
                                    unreadCount={unreadCounts[match.id]?.count || 0}
                                    disabled={scheduleSeasonCompleted}
                                  />
                                  {(() => {
                                    // Check if there are any details (court_name, match_date, match_time, or match_notes)
                                    const hasDetails = !!(
                                      match.courtDetails?.court_name ||
                                      match.courtDetails?.match_date ||
                                      match.courtDetails?.match_time ||
                                      match.match_notes
                                    );

                                    return hasDetails ? (
                                      <MatchDetailsButton
                                        onClick={() => handleOpenMatchDetails(match)}
                                        title={t('teamDashboardPage.matchDetailsTitleViewEdit')}
                                        style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)' }}
                                      >
                                        {t('teamDashboardPage.matchDetailsBtnView')}
                                      </MatchDetailsButton>
                                    ) : (
                                      <MatchDetailsButton
                                        onClick={() => handleOpenMatchDetails(match)}
                                        title={t('teamDashboardPage.matchDetailsTitleSubmit')}
                                      >
                                        {t('teamDashboardPage.matchDetailsBtnShort')}
                                      </MatchDetailsButton>
                                    );
                                  })()}
                                  {(user?.type === 'player' || user?.is_active) && (
                                    <MatchScoreButton
                                      onClick={() => handleOpenScore(match)}
                                      hasScore={hasScore}
                                      seasonCompleted={scheduleSeasonCompleted}
                                      isWindowLocked={!isUnlocked && !hasScore}
                                      scoreStatus={(() => {
                                      // Check if this match has a pending verification
                                      const pendingVerification = scheduleDashboard?.pendingScoreVerifications?.find(
                                        v => v.matchId === match.id
                                      );

                                      if (pendingVerification) {
                                        // Check if current team is the one who submitted the score
                                        const currentTeamId = String(scheduleDashboard?.team?.id);
                                        const submittedById = String(pendingVerification.submittedBy);
                                        const isSubmitter = submittedById === currentTeamId;
                                        return isSubmitter ? 'pending_confirmation' : 'pending_verification';
                                      } else if (match.status === 'completed') {
                                        return 'verified';
                                      } else if (match.status === 'score_submitted') {
                                        // For score_submitted status, determine if current team submitted it
                                        return 'pending_confirmation';
                                      } else if (match.status === 'disputed') {
                                        return 'disputed';
                                      }
                                      return null;
                                    })()}
                                  />
                                  )}
                                </MatchActions>
                                {match.status !== 'upcoming' && (
                                  <StatusBadge $status={match.status === 'completed' ? 'completed' : 'pending-score'}>
                                    {match.status === 'completed' ? t('teamDashboardPage.statusCompletedSchedule') : t('teamDashboardPage.statusScorePendingSchedule')}
                                  </StatusBadge>
                                )}
                              </ScheduleActions>
                            </ScheduleCard>
                          );
                        })}
                      </MatchWindowGroup>
                    );
                  });
                })()
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '2rem', 
                  color: '#6b7280',
                  background: 'rgba(255, 255, 255, 0.7)',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(8px)'
                }}>
                  <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>{t('teamDashboardPage.noMatchesScheduledTitle')}</div>
                  <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                    {t('teamDashboardPage.noMatchesScheduledBody')}
                  </div>
                </div>
              )}
            </ScheduleList>
                      </div>
        )}

        {activeSection === 'team' && (
          <div id="team-section">
            <SectionHeader $teamRoster>
              <SectionTitle>
                {t('teamDashboardPage.teamMembersTitle')}
                <span style={{ 
                  fontSize: '0.9rem', 
                  fontWeight: 'normal', 
                  color: '#6b7280',
                  marginLeft: '0.5rem'
                }}>
                  ({members.length}/2)
                </span>
              </SectionTitle>
              <SectionAction>
                {isPlayerView && viewerIsCaptain && !campaignViewOnly ? (
                  <button
                    type="button"
                    onClick={() => setInvitePlayerModalOpen(true)}
                    disabled={members.length >= 2}
                    title={
                      members.length >= 2
                        ? t('teamDashboardPage.invitePlayerDisabledFull')
                        : undefined
                    }
                    style={{
                      marginRight: '0.75rem',
                      padding: '0.5rem 1rem',
                      borderRadius: '8px',
                      border: 'none',
                      background: members.length >= 2 ? '#9ca3af' : '#043663',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '0.85rem',
                      cursor: members.length >= 2 ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t('teamDashboardPage.addMember')}
                  </button>
                ) : null}
                <TeamRosterHint>
                  {isPlayerView ? (
                    <>
                      {t('teamDashboardPage.teamRosterHintPlayerBeforeLink')}
                      <Link to="/player/teams" style={{ color: '#043663', fontWeight: 600 }}>
                        {t('teamDashboardPage.teamsLinkInline')}
                      </Link>
                      {t('teamDashboardPage.teamRosterHintPlayerAfterLink')}
                    </>
                  ) : (
                    t('teamDashboardPage.teamRosterHintTeamLogin')
                  )}
                </TeamRosterHint>
              </SectionAction>
            </SectionHeader>
            
            <PlayerGrid>
              {membersLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem', gridColumn: '1 / -1' }}>
                  <div style={{ fontSize: '1.2rem', color: '#6b7280' }}>{t('teamDashboardPage.loadingTeamMembers')}</div>
                    </div>
              ) : members.length > 0 ? (
                members.map((member, index) => {
                  const initials = `${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase();
                  const shortName = `${member.first_name?.[0] || ''}. ${member.last_name || ''}`;
                  const phoneNumber = member.phone_number || member.phone || '';
                  const formattedSex = member.sex
                    ? member.sex
                        .replace(/_/g, ' ')
                        .split(' ')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ')
                    : '';
                  let formattedDob = '';
                  if (member.date_of_birth) {
                    const dobDate = new Date(member.date_of_birth);
                    if (!Number.isNaN(dobDate.getTime())) {
                      formattedDob = dobDate.toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      });
                    } else {
                      formattedDob = member.date_of_birth;
                    }
                  }
                  
                  const openSelfRoster = isPlayerView && member.is_self && !campaignViewOnly;
                  const canClickCard = !isPlayerView || openSelfRoster;
                  return (
                    <PlayerCard 
                      key={member.id || index}
                      onClick={() => {
                        if (!canClickCard) return;
                        handleEditMember(member);
                      }}
                      style={{ cursor: canClickCard ? 'pointer' : 'default' }}
                      title={
                        openSelfRoster
                          ? t('teamDashboardPage.cardTitleEditRosterSkill')
                          : isPlayerView
                            ? t('teamDashboardPage.cardTitleCaptainRemoveHint')
                            : t('teamDashboardPage.cardTitleEditMember')
                      }
                    >
                      {!isPlayerView ? (
                        <PlayerAvatar title={t('teamDashboardPage.avatarUploadTitle')}>
                          {member.profile_picture_url ? (
                            <PlayerAvatarImg src={member.profile_picture_url} alt={`${member.first_name} ${member.last_name}`} />
                          ) : (
                            <PlayerAvatarInitials>
                              {initials}
                              <PlayerPlusIcon>+</PlayerPlusIcon>
                            </PlayerAvatarInitials>
                          )}
                          <ProfileUploadInput
                            type="file"
                            accept="image/*,.heic,.heif"
                            onChange={(e) => handleProfileUpload(member.id, e)}
                            disabled={
                              campaignViewOnly ||
                              profileUploading[member.id] ||
                              (isPlayerView && !member.is_self)
                            }
                            onClick={(e) => e.stopPropagation()}
                          />
                        </PlayerAvatar>
                      ) : null}
                      <PlayerInfo>
                        <PlayerName>
                          {shortName}
                          <PlayerPill $type={member.position === 'captain' ? 'captain' : 'member'}>
                            {member.position === 'captain'
                              ? t('teamDashboardPage.roleCaptain')
                              : t('teamDashboardPage.roleMember')}
                          </PlayerPill>
                          {members.length >= 2 && (
                            <PlayerPill $type="complete">Team complete</PlayerPill>
                          )}
                        </PlayerName>
                        <PlayerDetails>
                          <PlayerMetaItem>
                            {member.profile_skill_level != null && member.profile_skill_level !== '' ? (
                              <>
                                Profile {member.profile_skill_level} • This team {member.skill_level ?? '—'}
                              </>
                            ) : (
                              <>Player • Level {member.skill_level ?? 'N/A'}</>
                            )}
                          </PlayerMetaItem>
                          {phoneNumber && (
                            <PlayerMetaItem>
                              📞 {phoneNumber}
                            </PlayerMetaItem>
                          )}
                          {formattedSex && (
                            <PlayerMetaItem>
                              ⚧ {formattedSex}
                            </PlayerMetaItem>
                          )}
                          {formattedDob && (
                            <PlayerMetaItem>
                              🎂 {formattedDob}
                            </PlayerMetaItem>
                          )}
                        </PlayerDetails>
                      </PlayerInfo>
                      <PlayerActions
                        onClick={(e) => e.stopPropagation()}
                        style={{ alignItems: 'center', gap: '0.5rem' }}
                      >
                        {(member.phone || member.phone_number) && <PlayerIcon title="Phone number on file">📞</PlayerIcon>}
                        {member.email && <PlayerIcon>✉️</PlayerIcon>}
                        {isPlayerView && viewerIsCaptain && !member.is_self && !campaignViewOnly && (
                          <button
                            type="button"
                            onClick={() => {
                              setRemoveTarget(member);
                              setRemovePassword('');
                              setRemoveReason('');
                              setRemoveModalOpen(true);
                            }}
                            style={{
                              marginLeft: '0.25rem',
                              padding: '0.35rem 0.65rem',
                              fontSize: '0.78rem',
                              fontWeight: 600,
                              borderRadius: '8px',
                              border: '1px solid rgba(213, 43, 30, 0.45)',
                              background: 'rgba(213, 43, 30, 0.08)',
                              color: '#b91c1c',
                              cursor: 'pointer',
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </PlayerActions>
                    </PlayerCard>
                  );
                })
              ) : members.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '2rem', 
                  gridColumn: '1 / -1',
                  color: '#6b7280',
                  background: 'rgba(255, 255, 255, 0.7)',
                  borderRadius: '16px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(8px)'
                }}>
                  <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>No team members yet</div>
                  <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                    Players join via invitations or join requests (player app). Up to 2 members per team.
                </div>
                </div>
              ) : members.length === 1 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '1rem', 
                  gridColumn: '1 / -1',
                  color: '#d52b1e',
                  background: 'rgba(213, 43, 30, 0.1)',
                  borderRadius: '12px',
                  border: '1px solid rgba(213, 43, 30, 0.2)',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  ⚠️ Team needs 1 more member. Invite a player or use join requests to complete your team.
                  {members[0]?.skill_level && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', opacity: 0.8 }}>
                      Current player level: {members[0].skill_level}
                    </div>
                  )}
                </div>
              ) : members.length >= 2 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '1rem', 
                  gridColumn: '1 / -1',
                  color: '#059669',
                  background: 'rgba(16, 185, 129, 0.1)',
                  borderRadius: '12px',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  ✅ Team is complete! Maximum of 2 members reached.
                  {dashboard?.teamInfo?.averageLevel && (
                    <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', opacity: 0.8 }}>
                      Team average level: {dashboard.teamInfo.averageLevel}
                    </div>
                  )}
                </div>
              ) : null}
            </PlayerGrid>
            
            {/* Add Member Form */}
            {showAddForm && (
              <div style={{ 
                marginTop: '2rem', 
                padding: '2rem', 
                background: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(8px)'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: '1.5rem' 
                }}>
                  <h3 style={{ margin: 0, color: '#1f2937' }}>{t('teamDashboardPage.addTeamMemberTitle')}</h3>
                  <button
                    onClick={() => {
                      setShowAddForm(false);
                      resetForm();
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '1.5rem',
                      cursor: 'pointer',
                      color: '#6b7280'
                    }}
                  >
                    ×
                  </button>
                </div>
                
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                    gap: '1rem',
                    marginBottom: '1.5rem'
                  }}>
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        {t('teamDashboardPage.labelFirstName')}
                      </label>
                      <input
                        type="text"
                        {...register('firstName', { required: t('teamDashboardPage.valFirstNameRequired') })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '1rem'
                        }}
                      />
                      {errors.firstName && (
                        <div style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {errors.firstName.message}
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        {t('teamDashboardPage.labelLastName')}
                      </label>
                      <input
                        type="text"
                        {...register('lastName', { required: t('teamDashboardPage.valLastNameRequired') })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '1rem'
                        }}
                      />
                      {errors.lastName && (
                        <div style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {errors.lastName.message}
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        {t('teamDashboardPage.labelEmail')}
                      </label>
                      <input
                        type="email"
                        {...register('email', { 
                          required: t('teamDashboardPage.valEmailRequired'),
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: t('teamDashboardPage.valEmailInvalid')
                          }
                        })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '1rem'
                        }}
                      />
                      {errors.email && (
                        <div style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {errors.email.message}
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        {t('teamDashboardPage.labelPhoneNumber')}
                      </label>
                      <input
                        type="tel"
                        {...register('phoneNumber', {
                          required: t('teamDashboardPage.valPhoneRequired'),
                          maxLength: {
                            value: 25,
                            message: t('teamDashboardPage.valPhoneMax')
                          }
                        })}
                        placeholder={t('teamDashboardPage.placeholderPhoneCh')}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '1rem'
                        }}
                      />
                      {errors.phoneNumber && (
                        <div style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {errors.phoneNumber.message}
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        {t('teamDashboardPage.labelSex')}
                      </label>
                      <select
                        {...register('sex', { required: t('teamDashboardPage.valSexRequired') })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '1rem'
                        }}
                      >
                        <option value="">{t('teamDashboardPage.selectSex')}</option>
                        <option value="male">{t('teamDashboardPage.sexMale')}</option>
                        <option value="female">{t('teamDashboardPage.sexFemale')}</option>
                        <option value="non_binary">{t('teamDashboardPage.sexNonBinary')}</option>
                        <option value="other">{t('teamDashboardPage.sexOther')}</option>
                        <option value="prefer_not_to_say">{t('teamDashboardPage.sexPreferNot')}</option>
                      </select>
                      {errors.sex && (
                        <div style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {errors.sex.message}
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        {t('teamDashboardPage.labelDateOfBirth')}
                      </label>
                      <input
                        type="date"
                        max={maxDob}
                        {...register('dateOfBirth', {
                          required: t('teamDashboardPage.valDobRequired'),
                          validate: value => !value || value <= maxDob || t('teamDashboardPage.valDobFuture')
                        })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '1rem'
                        }}
                      />
                      {errors.dateOfBirth && (
                        <div style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {errors.dateOfBirth.message}
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        Home Club
                      </label>
                      <input
                        type="text"
                        {...register('homeClub')}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        Instagram
                      </label>
                      <input
                        type="text"
                        {...register('instagramHandle')}
                        placeholder="@username"
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        {t('teamDashboardPage.labelSkillLevel')}{' '}
                        {!canSelectSkillWhenAddingMember && !canEditSkillLevel() && (
                          <span style={{ color: '#dc2626', fontSize: '0.875rem', marginLeft: '0.5rem' }}>
                            {t('teamDashboardPage.skillLockedSuffix')}
                          </span>
                        )}
                      </label>
                      <select
                        value={selectedSkillRange}
                        onChange={(e) => {
                          setSelectedSkillRange(e.target.value);
                          setValue('exactSkillLevel', '');
                        }}
                        disabled={!canSelectSkillWhenAddingMember && !canEditSkillLevel()}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          backgroundColor: (!canSelectSkillWhenAddingMember && !canEditSkillLevel()) ? '#f3f4f6' : 'white',
                          cursor: (!canSelectSkillWhenAddingMember && !canEditSkillLevel()) ? 'not-allowed' : 'pointer',
                          opacity: (!canSelectSkillWhenAddingMember && !canEditSkillLevel()) ? 0.6 : 1
                        }}
                      >
                        <option value="">{t('teamDashboardPage.selectSkillRange')}</option>
                        <option value="1.0-2.5">1.0 – 2.5 : Beginner</option>
                        <option value="3.0-3.5">3.0 – 3.5 : Beginner +</option>
                        <option value="4.0-4.5">4.0 – 4.5 : Intermediate</option>
                        <option value="5.0-5.5">5.0 – 5.5 : Intermediate +</option>
                        <option value="6.0-6.5">6.0 – 6.5 : Advanced</option>
                        <option value="7.0-7.5">7.0 – 7.5 : Advanced +</option>
                        <option value="8.0-10.0">8.0 – 10.0 : Elite</option>
                      </select>
                      {!canSelectSkillWhenAddingMember && !canEditSkillLevel() && (
                        <div style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {skillLevelEdit?.reason || t('teamDashboardPage.toastSkillClosed')}
                        </div>
                      )}
                      {!selectedSkillRange && (canSelectSkillWhenAddingMember || canEditSkillLevel()) && (
                        <div style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {t('teamDashboardPage.toastSelectSkillRange')}
                        </div>
                      )}
                    </div>
                    
                    {selectedSkillRange && (
                      <div>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '0.5rem', 
                          fontWeight: '500',
                          color: '#374151'
                        }}>
                          {t('teamDashboardPage.exactSkillOptionalLabel')}{' '}
                          {!canSelectSkillWhenAddingMember && !canEditSkillLevel() && (
                            <span style={{ color: '#dc2626', fontSize: '0.875rem', marginLeft: '0.5rem' }}>
                              {t('teamDashboardPage.skillLockedSuffix')}
                            </span>
                          )}
                        </label>
                        <select
                          {...register('exactSkillLevel')}
                          disabled={!canSelectSkillWhenAddingMember && !canEditSkillLevel()}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            backgroundColor: (!canSelectSkillWhenAddingMember && !canEditSkillLevel()) ? '#f3f4f6' : 'white',
                            cursor: (!canSelectSkillWhenAddingMember && !canEditSkillLevel()) ? 'not-allowed' : 'pointer',
                            opacity: (!canSelectSkillWhenAddingMember && !canEditSkillLevel()) ? 0.6 : 1
                          }}
                        >
                          <option value="">{t('teamDashboardPage.selectExactSkillLevel')}</option>
                          {(() => {
                            const options = [];
                            for (let i = 1.0; i <= 10.0; i += 0.5) {
                              const value = i.toFixed(1);
                              const min = parseFloat(selectedSkillRange.split('-')[0]);
                              const max = parseFloat(selectedSkillRange.split('-')[1]);
                              if (i >= min && i <= max) {
                                options.push(
                                  <option key={value} value={value}>{value}</option>
                                );
                              }
                            }
                            return options;
                          })()}
                        </select>
                        <div style={{ 
                          fontSize: '0.875rem', 
                          color: '#6b7280', 
                          marginTop: '0.25rem' 
                        }}>
                          {t('teamDashboardPage.optionalExactSkillHint', {
                            min: selectedSkillRange.split('-')[0],
                            max: selectedSkillRange.split('-')[1],
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    gap: '1rem', 
                    justifyContent: 'flex-end' 
                  }}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddForm(false);
                        resetForm();
                      }}
                      style={{
                        background: '#6b7280',
                        color: 'white',
                        border: 'none',
                        padding: '0.75rem 2rem',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '1rem'
                      }}
                    >
                      {t('teamDashboardPage.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={addMemberMutation.isLoading}
                      style={{
                        background: addMemberMutation.isLoading ? '#9ca3af' : '#d52b1e',
                        color: 'white',
                        border: 'none',
                        padding: '0.75rem 2rem',
                        borderRadius: '8px',
                        cursor: addMemberMutation.isLoading ? 'not-allowed' : 'pointer',
                        fontSize: '1rem'
                      }}
                    >
                      {addMemberMutation.isLoading ? t('teamDashboardPage.addingMember') : t('teamDashboardPage.addMember')}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Edit Member Form */}
            {showEditForm && editingMember && (
              <div style={{ 
                marginTop: '2rem', 
                padding: '2rem', 
                background: 'rgba(255, 255, 255, 0.9)',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(8px)'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  marginBottom: '1.5rem' 
                }}>
                  <div>
                    <h3 style={{ margin: 0, color: '#1f2937' }}>
                      {isPlayerView && editingMember?.is_self
                        ? t('teamDashboardPage.editMemberTitleSelf')
                        : t('teamDashboardPage.editMemberTitleOther')}
                    </h3>
                    {isPlayerView && editingMember?.is_self && (
                      <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem', color: '#6b7280', maxWidth: '36rem', lineHeight: 1.4 }}>
                        <Trans
                          i18nKey="teamDashboardPage.editMemberSelfCaption"
                          components={{ strong: <strong /> }}
                        />
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setShowEditForm(false);
                      setEditingMember(null);
                      resetEditForm();
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      fontSize: '1.5rem',
                      cursor: 'pointer',
                      color: '#6b7280'
                    }}
                  >
                    ×
                  </button>
                </div>
                
                <form
                  onSubmit={
                    isPlayerView && editingMember?.is_self
                      ? (e) => {
                          e.preventDefault();
                          onEditSubmit(getValues());
                        }
                      : handleSubmit(onEditSubmit)
                  }
                >
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                    gap: '1rem',
                    marginBottom: '1.5rem'
                  }}>
                    {isPlayerView && editingMember?.is_self && editingMember?.profile_skill_level != null && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151' }}>
                          {t('teamDashboardPage.profileSkillAccountLabel')}
                        </span>
                        <div style={{ marginTop: '0.35rem', fontSize: '0.95rem', color: '#111827' }}>
                          {typeof editingMember.profile_skill_level === 'number'
                            ? editingMember.profile_skill_level.toFixed(1)
                            : editingMember.profile_skill_level}{' '}
                          <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>
                            {t('teamDashboardPage.profileSkillEditHint')}
                          </span>
                        </div>
                      </div>
                    )}
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        {t('teamDashboardPage.labelFirstName')}
                      </label>
                      <input
                        type="text"
                        defaultValue={editingMember.first_name}
                        readOnly={isPlayerView && editingMember?.is_self}
                        {...register('firstName', { required: t('teamDashboardPage.valFirstNameRequired') })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          backgroundColor: isPlayerView && editingMember?.is_self ? '#f3f4f6' : 'white',
                        }}
                      />
                      {errors.firstName && (
                        <div style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {errors.firstName.message}
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        {t('teamDashboardPage.labelLastName')}
                      </label>
                      <input
                        type="text"
                        defaultValue={editingMember.last_name}
                        readOnly={isPlayerView && editingMember?.is_self}
                        {...register('lastName', { required: t('teamDashboardPage.valLastNameRequired') })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          backgroundColor: isPlayerView && editingMember?.is_self ? '#f3f4f6' : 'white',
                        }}
                      />
                      {errors.lastName && (
                        <div style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {errors.lastName.message}
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        {t('teamDashboardPage.labelEmail')}
                      </label>
                      <input
                        type="email"
                        defaultValue={editingMember.email}
                        readOnly={isPlayerView && editingMember?.is_self}
                        {...register('email', { 
                          required: t('teamDashboardPage.valEmailRequired'),
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: t('teamDashboardPage.valEmailInvalid')
                          }
                        })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          backgroundColor: isPlayerView && editingMember?.is_self ? '#f3f4f6' : 'white',
                        }}
                      />
                      {errors.email && (
                        <div style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {errors.email.message}
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        {t('teamDashboardPage.labelPhoneOptional')}
                      </label>
                      <input
                        type="tel"
                        defaultValue={editingMember.phone_number || ''}
                        readOnly={isPlayerView && editingMember?.is_self}
                        {...register('phoneNumber', {
                          maxLength: {
                            value: 25,
                            message: t('teamDashboardPage.valPhoneMax')
                          }
                        })}
                        placeholder={t('teamDashboardPage.placeholderPhoneCh')}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          backgroundColor: isPlayerView && editingMember?.is_self ? '#f3f4f6' : 'white',
                        }}
                      />
                      {errors.phoneNumber && (
                        <div style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {errors.phoneNumber.message}
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        {t('teamDashboardPage.labelSexOptional')}
                      </label>
                      <select
                        defaultValue={editingMember.sex || ''}
                        disabled={isPlayerView && editingMember?.is_self}
                        {...register('sex')}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          backgroundColor: isPlayerView && editingMember?.is_self ? '#f3f4f6' : 'white',
                        }}
                      >
                        <option value="">{t('teamDashboardPage.sexPreferNot')}</option>
                        <option value="male">{t('teamDashboardPage.sexMale')}</option>
                        <option value="female">{t('teamDashboardPage.sexFemale')}</option>
                        <option value="non_binary">{t('teamDashboardPage.sexNonBinary')}</option>
                        <option value="other">{t('teamDashboardPage.sexOther')}</option>
                      </select>
                    </div>
                    
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        {t('teamDashboardPage.labelDateOfBirth').replace(/\s*\*$/, '')}
                      </label>
                      <input
                        type="date"
                        defaultValue={editingMember.date_of_birth ? editingMember.date_of_birth.slice(0, 10) : ''}
                        max={maxDob}
                        readOnly={isPlayerView && editingMember?.is_self}
                        {...register('dateOfBirth', {
                          validate: value => !value || value <= maxDob || t('teamDashboardPage.valDobFuture')
                        })}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          backgroundColor: isPlayerView && editingMember?.is_self ? '#f3f4f6' : 'white',
                        }}
                      />
                      {errors.dateOfBirth && (
                        <div style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {errors.dateOfBirth.message}
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        Home Club
                      </label>
                      <input
                        type="text"
                        defaultValue={editingMember.home_club || ''}
                        readOnly={isPlayerView && editingMember?.is_self}
                        {...register('homeClub')}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          backgroundColor: isPlayerView && editingMember?.is_self ? '#f3f4f6' : 'white',
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        Instagram
                      </label>
                      <input
                        type="text"
                        defaultValue={editingMember.instagram_handle || ''}
                        placeholder="@username"
                        readOnly={isPlayerView && editingMember?.is_self}
                        {...register('instagramHandle')}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          backgroundColor: isPlayerView && editingMember?.is_self ? '#f3f4f6' : 'white',
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '0.5rem', 
                        fontWeight: '500',
                        color: '#374151'
                      }}>
                        {(isPlayerView && editingMember?.is_self)
                          ? (
                            <>
                              {t('teamDashboardPage.teamRosterSkillLabel')} *
                            </>
                          )
                          : t('teamDashboardPage.labelSkillLevel')}{' '}
                        {!canEditSkillLevel() && (
                          <span style={{ color: '#dc2626', fontSize: '0.875rem', marginLeft: '0.5rem' }}>
                            {t('teamDashboardPage.skillLockedSuffix')}
                          </span>
                        )}
                      </label>
                      <select
                        value={editSkillRange}
                        onChange={(e) => {
                          setEditSkillRange(e.target.value);
                          setValue('exactSkillLevel', '');
                        }}
                        disabled={!canEditSkillLevel()}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '1rem',
                          backgroundColor: !canEditSkillLevel() ? '#f3f4f6' : 'white',
                          cursor: !canEditSkillLevel() ? 'not-allowed' : 'pointer',
                          opacity: !canEditSkillLevel() ? 0.6 : 1
                        }}
                      >
                        <option value="">{t('teamDashboardPage.selectSkillRange')}</option>
                        <option value="1.0-2.5">1.0 – 2.5 : Beginner</option>
                        <option value="3.0-3.5">3.0 – 3.5 : Beginner +</option>
                        <option value="4.0-4.5">4.0 – 4.5 : Intermediate</option>
                        <option value="5.0-5.5">5.0 – 5.5 : Intermediate +</option>
                        <option value="6.0-6.5">6.0 – 6.5 : Advanced</option>
                        <option value="7.0-7.5">7.0 – 7.5 : Advanced +</option>
                        <option value="8.0-10.0">8.0 – 10.0 : Elite</option>
                      </select>
                      {!canEditSkillLevel() && (
                        <div style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {skillLevelEdit?.reason || t('teamDashboardPage.toastSkillClosed')}
                        </div>
                      )}
                      {!editSkillRange && canEditSkillLevel() && (
                        <div style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                          {t('teamDashboardPage.toastSelectSkillRange')}
                        </div>
                      )}
                    </div>
                    
                    {editSkillRange && (
                      <div>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: '0.5rem', 
                          fontWeight: '500',
                          color: '#374151'
                        }}>
                          {t('teamDashboardPage.exactSkillOptionalLabel')}{' '}
                          {!canEditSkillLevel() && (
                            <span style={{ color: '#dc2626', fontSize: '0.875rem', marginLeft: '0.5rem' }}>
                              {t('teamDashboardPage.skillLockedSuffix')}
                            </span>
                          )}
                        </label>
                        <select
                          {...register('exactSkillLevel')}
                          defaultValue={(() => {
                            const r =
                              editingMember.roster_skill_level != null &&
                              editingMember.roster_skill_level !== ''
                                ? editingMember.roster_skill_level
                                : editingMember.skill_level;
                            return r != null && r !== '' && typeof r === 'number'
                              ? r.toFixed(1)
                              : r || '';
                          })()}
                          disabled={!canEditSkillLevel()}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            backgroundColor: !canEditSkillLevel() ? '#f3f4f6' : 'white',
                            cursor: !canEditSkillLevel() ? 'not-allowed' : 'pointer',
                            opacity: !canEditSkillLevel() ? 0.6 : 1
                          }}
                        >
                          <option value="">{t('teamDashboardPage.selectExactSkillLevel')}</option>
                          {(() => {
                            const options = [];
                            for (let i = 1.0; i <= 10.0; i += 0.5) {
                              const value = i.toFixed(1);
                              const min = parseFloat(editSkillRange.split('-')[0]);
                              const max = parseFloat(editSkillRange.split('-')[1]);
                              if (i >= min && i <= max) {
                                options.push(
                                  <option key={value} value={value}>{value}</option>
                                );
                              }
                            }
                            return options;
                          })()}
                        </select>
                        <div style={{ 
                          fontSize: '0.875rem', 
                          color: '#6b7280', 
                          marginTop: '0.25rem' 
                        }}>
                          {t('teamDashboardPage.optionalExactSkillHint', {
                            min: editSkillRange.split('-')[0],
                            max: editSkillRange.split('-')[1],
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    gap: '1rem', 
                    justifyContent: 'flex-end' 
                  }}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowEditForm(false);
                        setEditingMember(null);
                        resetEditForm();
                      }}
                      style={{
                        background: '#6b7280',
                        color: 'white',
                        border: 'none',
                        padding: '0.75rem 2rem',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '1rem'
                      }}
                    >
                      {t('teamDashboardPage.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={updateMemberMutation.isLoading}
                      style={{
                        background: updateMemberMutation.isLoading ? '#9ca3af' : '#d52b1e',
                        color: 'white',
                        border: 'none',
                        padding: '0.75rem 2rem',
                        borderRadius: '8px',
                        cursor: updateMemberMutation.isLoading ? 'not-allowed' : 'pointer',
                        fontSize: '1rem'
                      }}
                    >
                      {updateMemberMutation.isLoading
                        ? t('teamDashboardPage.updating')
                        : isPlayerView && editingMember?.is_self
                          ? t('teamDashboardPage.saveTeamRosterSkill')
                          : t('teamDashboardPage.updateMember')}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {removeModalOpen && removeTarget && (
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 2000,
                  background: 'rgba(15, 23, 42, 0.45)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '1rem',
                }}
                onClick={() => !removeMemberMutation.isLoading && setRemoveModalOpen(false)}
                role="presentation"
              >
                <div
                  style={{
                    background: 'white',
                    borderRadius: '16px',
                    maxWidth: '440px',
                    width: '100%',
                    padding: '1.75rem',
                    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                  }}
                  onClick={(e) => e.stopPropagation()}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="remove-member-title"
                >
                  <h3 id="remove-member-title" style={{ margin: '0 0 0.75rem', color: '#111827' }}>
                    {t('teamDashboardPage.removeTeammateTitle')}
                  </h3>
                  <p style={{ margin: '0 0 1rem', fontSize: '0.9rem', color: '#4b5563', lineHeight: 1.5 }}>
                    {t('teamDashboardPage.removeTeammateBody', {
                      name: `${removeTarget.first_name} ${removeTarget.last_name}`,
                    })}
                  </p>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
                    {t('teamDashboardPage.removeLoginPasswordLabel')}
                  </label>
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={removePassword}
                    onChange={(e) => setRemovePassword(e.target.value)}
                    style={{
                      width: '100%',
                      marginTop: '0.35rem',
                      marginBottom: '0.85rem',
                      padding: '0.65rem 0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      fontSize: '1rem',
                    }}
                  />
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>
                    {t('teamDashboardPage.removeReasonLabel')}
                  </label>
                  <textarea
                    value={removeReason}
                    onChange={(e) => setRemoveReason(e.target.value)}
                    rows={3}
                    placeholder={t('teamDashboardPage.placeholderRemoveReason')}
                    style={{
                      width: '100%',
                      marginTop: '0.35rem',
                      marginBottom: '1rem',
                      padding: '0.65rem 0.75rem',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      fontSize: '0.95rem',
                      resize: 'vertical',
                    }}
                  />
                  <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      disabled={removeMemberMutation.isLoading}
                      onClick={() => {
                        setRemoveModalOpen(false);
                        setRemoveTarget(null);
                      }}
                      style={{
                        padding: '0.6rem 1.1rem',
                        borderRadius: '8px',
                        border: '1px solid #d1d5db',
                        background: '#f9fafb',
                        cursor: removeMemberMutation.isLoading ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {t('teamDashboardPage.cancel')}
                    </button>
                    <button
                      type="button"
                      disabled={removeMemberMutation.isLoading || removePassword.length < 1 || removeReason.trim().length < 5}
                      onClick={() => {
                        removeMemberMutation.mutate({
                          memberId: removeTarget.id,
                          body: { password: removePassword, reason: removeReason.trim() },
                          teamId: effectiveTeamId || undefined,
                        });
                      }}
                      style={{
                        padding: '0.6rem 1.1rem',
                        borderRadius: '8px',
                        border: 'none',
                        background:
                          removeMemberMutation.isLoading || removePassword.length < 1 || removeReason.trim().length < 5
                            ? '#fca5a5'
                            : '#b91c1c',
                        color: 'white',
                        fontWeight: 600,
                        cursor:
                          removeMemberMutation.isLoading || removePassword.length < 1 || removeReason.trim().length < 5
                            ? 'not-allowed'
                            : 'pointer',
                      }}
                    >
                      {removeMemberMutation.isLoading ? t('teamDashboardPage.removing') : t('teamDashboardPage.removeMember')}
                    </button>
                  </div>
                </div>
              </div>
            )}
                </div>
              )}

        {activeSection === 'results' && (
          <div id="results-section">
            <SectionHeader>
              <SectionTitle>{t('teamDashboardPage.resultsTitle')}</SectionTitle>
            </SectionHeader>
            
            {resultsLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '1.2rem', color: '#6b7280' }}>{t('teamDashboardPage.loadingResults')}</div>
              </div>
            ) : resultsError ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#dc2626' }}>
                <div style={{ fontSize: '1rem' }}>{t('teamDashboardPage.errorLoadingResults')}</div>
              </div>
            ) : (
              <>
                <ResultsSearchContainer>
                  <ResultsSearchInput
                    type="text"
                    placeholder={t('teamDashboardPage.placeholderResultsSearch')}
                    value={resultsSearchTerm}
                    onChange={(e) => setResultsSearchTerm(e.target.value)}
                  />
                  <ResultsFilterSelect
                    value={resultsDivisionFilter}
                    onChange={(e) => setResultsDivisionFilter(e.target.value)}
                  >
                    <option value="">{t('teamDashboardPage.filterAllDivisions')}</option>
                    {availableDivisions.map((division) => (
                      <option key={division} value={division}>
                        {division}
                      </option>
                    ))}
                  </ResultsFilterSelect>
                  <ResultsFilterSelect
                    value={resultsCantonFilter}
                    onChange={(e) => setResultsCantonFilter(e.target.value)}
                  >
                    <option value="">{t('teamDashboardPage.filterAllCantons')}</option>
                    {availableCantons.map((canton) => (
                      <option key={canton} value={canton}>
                        {canton}
                      </option>
                    ))}
                  </ResultsFilterSelect>
                  <ResultsFilterSelect
                    value={selectedResultsSeason}
                    onChange={(e) => setSelectedResultsSeason(e.target.value)}
                  >
                    <option value="">{t('teamDashboardPage.filterAllSeasons')}</option>
                    {seasonsData?.data?.map((season) => (
                      <option key={season.id} value={season.id}>
                        {season.name}
                      </option>
                    ))}
                  </ResultsFilterSelect>
                  <ResultsCount>
                    {t('teamDashboardPage.resultsCount', { count: filteredResults.length })}
                    {resultsSearchTerm && t('teamDashboardPage.resultsCountOfTotal', { total: results.length })}
                    {resultsTeamFilter && t('teamDashboardPage.resultsFilteredByTeam', { team: resultsTeamFilter })}
                  </ResultsCount>
                  </ResultsSearchContainer>

                {results.length === 0 ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '2rem', 
                    color: '#6b7280',
                    background: 'rgba(255, 255, 255, 0.7)',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(8px)'
                  }}>
                    <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>{t('teamDashboardPage.noResultsYetTitle')}</div>
                    <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                      {t('teamDashboardPage.noResultsYetBody')}
                    </div>
                  </div>
                ) : (
                  <>
                    {resultsTeamFilter && (
                  <div
                    style={{
                      marginBottom: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '999px',
                      background: 'rgba(37, 99, 235, 0.06)',
                      border: '1px solid rgba(37, 99, 235, 0.2)',
                      color: '#1d4ed8',
                      fontSize: '0.85rem',
                      flexWrap: 'wrap'
                    }}
                  >
                    <span>
                      {t('teamDashboardPage.showingResultsFor')}{' '}
                      <strong>{resultsTeamFilter}</strong>
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        // Clear the team filter
                        setResultsTeamFilter('');
                        // Reset the initial page flag so it recalculates to default page
                        hasSetInitialPageRef.current = false;
                        // Reset prevActiveSectionRef to trigger "switching to results" behavior
                        // This makes it behave exactly like clicking "Results" in the sidebar
                        prevActiveSectionRef.current = null;
                      }}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: '#1d4ed8',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        padding: '0.1rem 0.4rem',
                        borderRadius: '999px',
                        textDecoration: 'underline'
                      }}
                    >
                      {t('teamDashboardPage.showAllResults')}
                    </button>
                  </div>
                    )}
                
                    {filteredResults.length === 0 ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '2rem', 
                    color: '#6b7280',
                    background: 'rgba(255, 255, 255, 0.7)',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(8px)'
                  }}>
                    <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>{t('teamDashboardPage.noResultsFoundTitle')}</div>
                    <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                      {t('teamDashboardPage.noResultsFoundBody')}
                    </div>
                  </div>
                ) : (
                  <>
                    <ResultsTableWrapper>
                      <ResultsTable>
                        <thead>
                          <tr>
                            <ResultsHeadCell className="date-col">{t('teamDashboardPage.resultsColDate')}</ResultsHeadCell>
                            <ResultsHeadCell className="time-col center">{t('teamDashboardPage.resultsColTime')}</ResultsHeadCell>
                            <ResultsHeadCell className="location-col">{t('teamDashboardPage.resultsColLocation')}</ResultsHeadCell>
                            <ResultsHeadCell className="team-a-col">{t('teamDashboardPage.resultsColTeamA')}</ResultsHeadCell>
                            <ResultsHeadCell className="team-b-col">{t('teamDashboardPage.resultsColTeamB')}</ResultsHeadCell>
                            <ResultsHeadCell className="scores-col center">{t('teamDashboardPage.resultsColScores')}</ResultsHeadCell>
                            <ResultsHeadCell className="result-col center">{t('teamDashboardPage.resultsColResult')}</ResultsHeadCell>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedResults.map((match) => (
                            <ResultsRow key={match.id}>
                              <ResultsCell className="date-cell">{match.date}</ResultsCell>
                              <ResultsCell className="time-cell center">{match.time || '-'}</ResultsCell>
                              <ResultsCell className="location-cell">
                                <ResultsLocationTrigger
                                  type="button"
                                  title={t('teamDashboardPage.resultsLocationViewFull')}
                                  onClick={() => {
                                    setResultsLocationText(match.venue || t('teamDashboardPage.venueTbd'));
                                    setResultsLocationModalOpen(true);
                                  }}
                                >
                                  {match.venue || t('teamDashboardPage.venueTbd')}
                                </ResultsLocationTrigger>
                              </ResultsCell>
                              <ResultsTeamCell className="team-a-cell">
                                <ResultsTeamCellContent>
                                  <ResultsTeamLogo
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                      setSelectedTeamLogo(match.team1_logo_url);
                                      setSelectedTeamName(match.team1_name);
                                      setSelectedTeamInitials(getTeamInitials(match.team1_name));
                                      setTeamLogoModalOpen(true);
                                    }}
                                  >
                                    {match.team1_logo_url ? (
                                      <ResultsTeamLogoImg src={match.team1_logo_url} alt={`${match.team1_name} logo`} />
                                    ) : (
                                      <ResultsTeamLogoInitials>{getTeamInitials(match.team1_name)}</ResultsTeamLogoInitials>
                                    )}
                                  </ResultsTeamLogo>
                                  <ResultsTeamName
                                    onClick={() => setResultsTeamFilter(match.team1_name)}
                                    style={{
                                      cursor: 'pointer',
                                      color:
                                        resultsTeamFilter === match.team1_name ? '#1d4ed8' : 'inherit',
                                      textDecoration:
                                        resultsTeamFilter === match.team1_name ? 'underline' : 'none'
                                    }}
                                  >
                                    {match.team1_name}
                                  </ResultsTeamName>
                                </ResultsTeamCellContent>
                              </ResultsTeamCell>
                              <ResultsTeamCell className="team-b-cell">
                                <ResultsTeamCellContent>
                                  <ResultsTeamLogo
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                      setSelectedTeamLogo(match.team2_logo_url);
                                      setSelectedTeamName(match.team2_name);
                                      setSelectedTeamInitials(getTeamInitials(match.team2_name));
                                      setTeamLogoModalOpen(true);
                                    }}
                                  >
                                    {match.team2_logo_url ? (
                                      <ResultsTeamLogoImg src={match.team2_logo_url} alt={`${match.team2_name} logo`} />
                                    ) : (
                                      <ResultsTeamLogoInitials>{getTeamInitials(match.team2_name)}</ResultsTeamLogoInitials>
                                    )}
                                  </ResultsTeamLogo>
                                  <ResultsTeamName
                                    onClick={() => setResultsTeamFilter(match.team2_name)}
                                    style={{
                                      cursor: 'pointer',
                                      color:
                                        resultsTeamFilter === match.team2_name ? '#1d4ed8' : 'inherit',
                                      textDecoration:
                                        resultsTeamFilter === match.team2_name ? 'underline' : 'none'
                                    }}
                                  >
                                    {match.team2_name}
                                  </ResultsTeamName>
                                </ResultsTeamCellContent>
                              </ResultsTeamCell>
                              <ResultsCell className="scores-cell center">
                                {formatScoresWithBold(match.scores) || '-'}
                              </ResultsCell>
                              <ResultsCell className="result-cell center">
                                {match.result ? <ResultScore>{match.result}</ResultScore> : '-'}
                              </ResultsCell>
                            </ResultsRow>
                          ))}
                        </tbody>
                      </ResultsTable>
                    </ResultsTableWrapper>
                    <PaginationContainer>
                      <PaginationInfo>
                        {t('teamDashboardPage.paginationPageOf', { page: resultsPage, total: totalPages })}
                      </PaginationInfo>
                      <PaginationButtonGroup>
                        <PaginationButton
                          onClick={() => setResultsPage(Math.max(1, resultsPage - 1))}
                          disabled={resultsPage <= 1}
                          aria-label={t('teamDashboardPage.ariaPrevResults')}
                        >
                          {t('teamDashboardPage.paginationPrev')}
                        </PaginationButton>
                        <PaginationButton
                          onClick={() => setResultsPage(Math.min(totalPages, resultsPage + 1))}
                          disabled={resultsPage >= totalPages}
                          aria-label={t('teamDashboardPage.ariaNextResults')}
                        >
                          {t('teamDashboardPage.paginationNext')}
                        </PaginationButton>
                      </PaginationButtonGroup>
                    </PaginationContainer>
                  </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {activeSection === 'divisions' && (
          <div>
            {(() => {
              const now = new Date();
              const seasonCtx =
                seasonsData?.data?.find((s) => String(s.id) === String(selectedRankingsSeason)) ||
                dashboard?.season;

              if (seasonCtx && seasonAllowsStandingsUI(seasonCtx, now)) {
                return null;
              }

              let upcomingSeason = null;

              if (seasonCtx) {
                const startDate = new Date(seasonCtx.start_date);
                const isUpcoming =
                  startDate > now &&
                  (seasonCtx.status === 'upcoming' ||
                    seasonCtx.status === 'registration' ||
                    !seasonCtx.status);
                if (isUpcoming) {
                  upcomingSeason =
                    dashboard?.season?.id === seasonCtx.id ? dashboard.season : seasonCtx;
                }
              }
              
              // If no upcoming season from currentSeason, check seasonsData for upcoming seasons
              if (!upcomingSeason && seasonsData?.data) {
                upcomingSeason = seasonsData.data.find(s => {
                  const startDate = new Date(s.start_date);
                  return startDate > now && 
                    (s.status === 'upcoming' || s.status === 'registration' || !s.status);
                });
              }
              
              // Only show message if there's an upcoming season and team is new to that season
              if (upcomingSeason && dashboard?.isNewMidSeasonTeam) {
                const startDate = new Date(upcomingSeason.start_date);
                const registrationStart = upcomingSeason.registration_start ? new Date(upcomingSeason.registration_start) : null;
                const registrationEnd = upcomingSeason.registration_end ? new Date(upcomingSeason.registration_end) : null;
                
                // Check if registration period is active
                const isRegistrationActive = registrationStart && registrationEnd && 
                  now >= registrationStart && now <= registrationEnd;
                
                // Format start date
                const formattedStartDate = startDate.toLocaleDateString(localeTag, { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                });
                
                const title = t('teamDashboardPage.midSeasonRankingsStartingSoonTitle', { name: upcomingSeason.name });
                let message = t('teamDashboardPage.midSeasonRankingsBody', {
                  seasonName: upcomingSeason.name.toLowerCase(),
                  date: formattedStartDate,
                });
                
                if (isRegistrationActive) {
                  const formattedRegEnd = registrationEnd.toLocaleDateString(localeTag, { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  });
                  message = t('teamDashboardPage.midSeasonRankingsBodyRegistration', {
                    regEnd: formattedRegEnd,
                    seasonName: upcomingSeason.name.toLowerCase(),
                    date: formattedStartDate,
                  });
                } else if (registrationEnd && now > registrationEnd) {
                  message = t('teamDashboardPage.midSeasonRankingsBody', {
                    seasonName: upcomingSeason.name.toLowerCase(),
                    date: formattedStartDate,
                  });
                }
                
                return (
                  <MidSeasonMessage>
                    <MidSeasonIcon>📅</MidSeasonIcon>
                    <MidSeasonContent>
                      <MidSeasonTitle>{title}</MidSeasonTitle>
                      <MidSeasonText>{message}</MidSeasonText>
                    </MidSeasonContent>
                  </MidSeasonMessage>
                );
              }
              
              // No message to show
              return null;
            })()}
            {!dashboard?.isNewMidSeasonTeam && LeaderboardSection}
          </div>
        )}

        {activeSection === 'reserve-court' && (
          <div>
            <SectionHeader>
              <SectionTitle>
                {t('teamDashboardPage.reserveCourtTitle')}
                <span style={{ 
                  fontSize: '0.9rem', 
                  fontWeight: 'normal', 
                  color: '#6b7280',
                  marginLeft: '0.5rem'
                }}>
                  {t('teamDashboardPage.reserveCourtSubtitle')}
                </span>
              </SectionTitle>
            </SectionHeader>
            
            {(isPlayerView || user?.is_active) && <CourtBooking />}
          </div>
        )}

        {activeSection === 'settings' && (
          <SettingsContainer>
            {/* Captain contact phone (used for match chat revelation) */}
            <SettingsSection>
              <SettingsSectionTitle>
                {t('teamDashboardPage.settingsPhoneTitle')}
              </SettingsSectionTitle>
              {!phoneNumberStatus.viewerIsCaptain ? (
                <HelpText>{t('teamDashboardPage.phoneCaptainOnlyHint')}</HelpText>
              ) : isPlayerView ? (
                <>
                  {phoneNumberStatus?.hasPhoneNumber ? (
                    <HelpText>
                      {t('teamDashboardPage.phoneHelpHasCaptain', {
                        phone: phoneNumberStatus.phoneNumber || '—',
                      })}
                    </HelpText>
                  ) : (
                    <HelpText>{t('teamDashboardPage.phoneHelpAddCaptain')}</HelpText>
                  )}
                  <FormButton
                    type="button"
                    onClick={() => navigate('/player/settings/profile')}
                    style={{ marginTop: '0.75rem' }}
                  >
                    {t('teamDashboardPage.phoneModalGoProfile')}
                  </FormButton>
                </>
              ) : (
                <SettingsForm
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setPhoneNumberError('');

                    if (!phoneNumber.trim()) {
                      setPhoneNumberError(t('teamDashboardPage.phoneEnterError'));
                      return;
                    }

                    const phoneRegex = /^[+]?[0-9\s\-()]{8,20}$/;
                    if (!phoneRegex.test(phoneNumber.trim())) {
                      setPhoneNumberError(t('teamDashboardPage.phoneInvalidError'));
                      return;
                    }

                    setPhoneNumberLoading(true);
                    try {
                      const response = await api.put(`/teams/${dashboard?.team?.id}/phone-number`, {
                        phoneNumber: phoneNumber.trim(),
                      });

                      if (response.success) {
                        toast.success(t('teamDashboardPage.toastPhoneSaved'));
                        phoneNumberStatus.refetch();
                        phoneNumberStatus.onSuccess(response.data);
                        setPhoneNumberError('');
                      } else {
                        setPhoneNumberError(response.message || t('teamDashboardPage.phoneSaveFail'));
                      }
                    } catch (error) {
                      const errorMessage =
                        error.response?.data?.message ||
                        error.response?.message ||
                        error.message ||
                        t('teamDashboardPage.phoneSaveFail');
                      setPhoneNumberError(errorMessage);
                    } finally {
                      setPhoneNumberLoading(false);
                    }
                  }}
                >
                  <FormGroup>
                    <FormLabel htmlFor="phoneNumber">{t('teamDashboardPage.phoneModalLabel')}</FormLabel>
                    <FormInput
                      id="phoneNumber"
                      type="tel"
                      placeholder={t('teamDashboardPage.placeholderPhoneCh')}
                      value={phoneNumber}
                      onChange={(e) => {
                        setPhoneNumber(e.target.value);
                        setPhoneNumberError('');
                      }}
                      disabled={phoneNumberLoading}
                    />
                    {phoneNumberError && (
                      <div style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                        {phoneNumberError}
                      </div>
                    )}
                    {phoneNumberStatus?.hasPhoneNumber ? (
                      <HelpText>
                        {t('teamDashboardPage.phoneHelpHasCaptain', {
                          phone: phoneNumberStatus.phoneNumber || '—',
                        })}
                      </HelpText>
                    ) : (
                      <HelpText>{t('teamDashboardPage.phoneHelpAddCaptain')}</HelpText>
                    )}
                  </FormGroup>
                  <FormButton type="submit" disabled={phoneNumberLoading}>
                    {phoneNumberLoading
                      ? t('teamDashboardPage.saving')
                      : phoneNumberStatus?.hasPhoneNumber
                        ? t('teamDashboardPage.changePhoneNumber')
                        : t('teamDashboardPage.savePhoneNumber')}
                  </FormButton>
                </SettingsForm>
              )}
            </SettingsSection>

            {/* Change Password Section */}
            <SettingsSection>
              <SettingsSectionTitle>
                {t('teamDashboardPage.settingsPasswordTitle')}
              </SettingsSectionTitle>
              <SettingsForm onSubmit={async (e) => {
                e.preventDefault();
                setPasswordError('');
                
                if (!currentPassword || !newPassword || !confirmPassword) {
                  setPasswordError(t('teamDashboardPage.passwordAllRequired'));
                  return;
                }

                if (newPassword !== confirmPassword) {
                  setPasswordError(t('teamDashboardPage.passwordMismatch'));
                  return;
                }

                if (newPassword.length < 6) {
                  setPasswordError(t('teamDashboardPage.passwordMinLength'));
                  return;
                }

                setPasswordLoading(true);
                try {
                  const response = await api.put('/auth/change-password', {
                    currentPassword,
                    newPassword
                  });

                  if (response.success) {
                    toast.success(t('teamDashboardPage.toastPasswordChanged'));
                    // Clear form
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                    setPasswordError('');
                  } else {
                    setPasswordError(response.message || t('teamDashboardPage.passwordChangeFail'));
                  }
                } catch (error) {
                  const errorMessage = error.response?.data?.message || 
                                     error.response?.message || 
                                     error.message || 
                                     t('teamDashboardPage.passwordChangeFail');
                  setPasswordError(errorMessage);
                } finally {
                  setPasswordLoading(false);
                }
              }}>
                <FormGroup>
                  <FormLabel htmlFor="currentPassword">{t('teamDashboardPage.labelCurrentPassword')}</FormLabel>
                  <PasswordInput
                    id="currentPassword"
                    placeholder={t('teamDashboardPage.placeholderCurrentPassword')}
                    value={currentPassword}
                    onChange={(e) => {
                      setCurrentPassword(e.target.value);
                      setPasswordError('');
                    }}
                    disabled={passwordLoading}
                    padding="0.75rem"
                    paddingRight="2.5rem"
                    border="1px solid #d1d5db"
                    borderRadius="8px"
                    fontSize="0.95rem"
                    focusBorderColor="#043663"
                    focusBoxShadow="0 0 0 3px rgba(4, 54, 99, 0.1)"
                    autoComplete="current-password"
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel htmlFor="newPassword">{t('teamDashboardPage.labelNewPassword')}</FormLabel>
                  <PasswordInput
                    id="newPassword"
                    placeholder={t('teamDashboardPage.placeholderNewPassword')}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setPasswordError('');
                    }}
                    disabled={passwordLoading}
                    padding="0.75rem"
                    paddingRight="2.5rem"
                    border="1px solid #d1d5db"
                    borderRadius="8px"
                    fontSize="0.95rem"
                    focusBorderColor="#043663"
                    focusBoxShadow="0 0 0 3px rgba(4, 54, 99, 0.1)"
                    autoComplete="new-password"
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel htmlFor="confirmPassword">{t('teamDashboardPage.labelConfirmNewPassword')}</FormLabel>
                  <PasswordInput
                    id="confirmPassword"
                    placeholder={t('teamDashboardPage.placeholderConfirmPassword')}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setPasswordError('');
                    }}
                    disabled={passwordLoading}
                    padding="0.75rem"
                    paddingRight="2.5rem"
                    border="1px solid #d1d5db"
                    borderRadius="8px"
                    fontSize="0.95rem"
                    focusBorderColor="#043663"
                    focusBoxShadow="0 0 0 3px rgba(4, 54, 99, 0.1)"
                    autoComplete="new-password"
                  />
                  {passwordError && (
                    <div style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                      {passwordError}
                    </div>
                  )}
                  <HelpText>
                    {t('teamDashboardPage.passwordMinLength')}.
                  </HelpText>
                </FormGroup>
                <FormButton 
                  type="submit"
                  disabled={!currentPassword || !newPassword || !confirmPassword || passwordLoading}
                >
                  {passwordLoading ? t('teamDashboardPage.changingPassword') : t('teamDashboardPage.changePassword')}
                </FormButton>
              </SettingsForm>
            </SettingsSection>

            {/* Change Email Section */}
            <SettingsSection>
              <SettingsSectionTitle>
                {t('teamDashboardPage.settingsEmailTitle')}
              </SettingsSectionTitle>
              <SettingsForm onSubmit={async (e) => {
                e.preventDefault();
                setEmailError('');
                
                if (!newEmail || !newEmail.trim()) {
                  setEmailError(t('teamDashboardPage.valEmailRequired'));
                  return;
                }

                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(newEmail.trim())) {
                  setEmailError(t('teamDashboardPage.valEmailInvalid'));
                  return;
                }

                setEmailLoading(true);
                try {
                  const response = await api.put('/auth/change-email', {
                    newEmail: newEmail.trim()
                  });

                  if (response.success) {
                    toast.success(t('teamDashboardPage.toastEmailChanged'));
                    // Update the new email field to show the saved email
                    setNewEmail(response.data?.email || newEmail.trim());
                    setEmailError('');
                    // Refresh dashboard to get updated email
                    queryClient.invalidateQueries('dashboardOverview');
                  } else {
                    setEmailError(response.message || t('teamDashboardPage.emailChangeFail'));
                  }
                } catch (error) {
                  const errorMessage = error.response?.data?.message || 
                                     error.response?.message || 
                                     error.message || 
                                     t('teamDashboardPage.emailChangeFail');
                  setEmailError(errorMessage);
                } finally {
                  setEmailLoading(false);
                }
              }}>
                <FormGroup>
                  <FormLabel htmlFor="currentEmail">{t('teamDashboardPage.labelCurrentEmail')}</FormLabel>
                  <FormInput
                    id="currentEmail"
                    type="email"
                    value={user?.email || dashboard?.data?.team?.email || '—'}
                    disabled
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel htmlFor="newEmail">{t('teamDashboardPage.labelNewEmail')}</FormLabel>
                  <FormInput
                    id="newEmail"
                    type="email"
                    placeholder={t('teamDashboardPage.placeholderNewEmailExample')}
                    value={newEmail}
                    onChange={(e) => {
                      setNewEmail(e.target.value);
                      setEmailError('');
                    }}
                    disabled={emailLoading}
                  />
                  {emailError && (
                    <div style={{ color: '#dc2626', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                      {emailError}
                    </div>
                  )}
                  <HelpText>
                    {t('teamDashboardPage.emailHelpNewLogin')}
                  </HelpText>
                </FormGroup>
                <FormButton 
                  type="submit"
                  disabled={!newEmail || emailLoading}
                >
                  {emailLoading ? t('teamDashboardPage.changingEmail') : t('teamDashboardPage.changeEmail')}
                </FormButton>
              </SettingsForm>
            </SettingsSection>
          </SettingsContainer>
        )}
      </MainContent>

      <MobileActionBar>
        <MobileActionContainer style={{ flexDirection: 'column', alignItems: 'center' }}>
          {/* Navigation buttons */}
          <div style={{ 
            display: 'flex', 
            gap: '4px', 
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50px',
            opacity: isTransitioning ? 0 : 1,
            transition: 'opacity 0.2s ease-in-out'
          }}>
            {buttonGroups[currentPage]
              ?.filter((button) => !campaignViewOnly || button.id !== 'settings')
              .map((button) => (
              <MobileActionBtn 
                key={button.id}
                className={activeSection === button.id ? 'active' : ''}
                onClick={() => showSection(button.id)}
              >
                <MobileActionIcon>{button.icon}</MobileActionIcon>
                <MobileActionLabel>{button.label}</MobileActionLabel>
              </MobileActionBtn>
            ))}
          </div>
          
          {/* Simple arrow icon below the buttons */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginTop: '2px',
            width: '100%'
          }}>
            <button
              onClick={currentPage === 0 ? swipeDown : swipeUp}
              disabled={isTransitioning}
              style={{
                background: 'rgba(255, 255, 255, 0.9)',
                border: '1px solid rgba(229, 231, 235, 0.8)',
                cursor: isTransitioning ? 'not-allowed' : 'pointer',
                opacity: isTransitioning ? 0.4 : 1,
                padding: '6px',
                borderRadius: '50%',
                transition: 'all 0.2s ease',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title={currentPage === 0 ? "Show more items" : "Show previous items"}
            >
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="currentColor"
                style={{
                  transform: currentPage === 0 ? 'rotate(90deg)' : 'rotate(-90deg)',
                  color: '#1f2937'
                }}
              >
                <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
              </svg>
            </button>
          </div>
        </MobileActionContainer>
        
        {/* Pill-shaped Logout Button - Bottom Right */}
        <div style={{
          position: 'absolute',
          bottom: '100%',
          right: '8px',
          marginBottom: '8px'
        }}>
          <MobileActionBtn 
            onClick={handleLogout}
            style={{ 
              background: '#dc2626',
              color: 'white',
              border: 'none',
              width: 'auto',
              padding: '8px 12px',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600',
              boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)'
            }}
          >
            <MobileActionLabel>Logout</MobileActionLabel>
          </MobileActionBtn>
        </div>
      </MobileActionBar>


      {/* Team Logo Modal */}
      <TeamLogoModal
        isOpen={teamLogoModalOpen}
        onClose={() => setTeamLogoModalOpen(false)}
        logoUrl={selectedTeamLogo}
        teamName={selectedTeamName}
        teamInitials={selectedTeamInitials}
      />

      {/* Logo Upload Options Modal */}
      {logoUploadModalOpen && (
        <LogoUploadModal onClick={(e) => e.target === e.currentTarget && setLogoUploadModalOpen(false)}>
          <LogoUploadModalContent>
            <LogoUploadModalClose onClick={() => setLogoUploadModalOpen(false)}>×</LogoUploadModalClose>
            <LogoUploadModalTitle>Team Logo</LogoUploadModalTitle>
            {dashboard?.teamInfo?.teamLogoUrl ? (
              <>
                <LogoUploadModalButton
                  variant="primary"
                  onClick={() => {
                    setSelectedTeamLogo(dashboard.teamInfo.teamLogoUrl);
                    setSelectedTeamName(
                      isPlayerView && dashboard?.teamInfo?.name
                        ? dashboard.teamInfo.name
                        : user?.teamName || 'Team'
                    );
                    setSelectedTeamInitials(
                      getTeamInitials(
                        isPlayerView && dashboard?.teamInfo?.name
                          ? dashboard.teamInfo.name
                          : user?.teamName || 'Team'
                      )
                    );
                    setLogoUploadModalOpen(false);
                    setTeamLogoModalOpen(true);
                  }}
                >
                  View Current Logo
                </LogoUploadModalButton>
                <LogoUploadModalButton
                  onClick={() => {
                    setLogoUploadModalOpen(false);
                    logoFileInputRef.current?.click();
                  }}
                >
                  Upload New Logo
                </LogoUploadModalButton>
              </>
            ) : (
              <LogoUploadModalButton
                variant="primary"
                onClick={() => {
                  setLogoUploadModalOpen(false);
                  logoFileInputRef.current?.click();
                }}
              >
                Upload Logo
              </LogoUploadModalButton>
            )}
          </LogoUploadModalContent>
        </LogoUploadModal>
      )}

      {/* Match Chat Modal */}
      {selectedChatMatch && (
        <MatchChatModal 
          isOpen={chatModalOpen}
          onClose={handleCloseChat}
          matchId={selectedChatMatch.id}
          opponentName={selectedChatMatch.opponent}
          opponentTeamId={selectedChatMatch.opponentId}
          teamId={chatTeamIdOverride || effectiveTeamId || undefined}
          viewerPlayerId={viewerPlayerIdForChat}
        />
      )}

      {selectedScheduleMatch && (
        <MatchScheduleModal
          isOpen={scheduleModalOpen}
          onClose={handleCloseSchedule}
          matchId={selectedScheduleMatch.id}
          opponentName={selectedScheduleMatch.opponent}
          teamId={scheduleTeamIdOverride || effectiveTeamId || undefined}
          viewerPlayerId={viewerPlayerIdForChat}
          onSchedulingUpdated={() => queryClient.invalidateQueries(['matchSchedulingInbox'])}
        />
      )}

      {/* Home / Away info modal */}
      <HomeAwayInfoModal
        isOpen={homeAwayModalOpen}
        onClose={() => setHomeAwayModalOpen(false)}
        type={homeAwayModalType}
      />

      <ResultsLocationModal
        isOpen={resultsLocationModalOpen}
        onClose={() => setResultsLocationModalOpen(false)}
        locationText={resultsLocationText}
        title={t('teamDashboardPage.resultsColLocation')}
      />

      {/* Match Score Modal */}
      {selectedScoreMatch && (
        <MatchScoreModal 
          isOpen={scoreModalOpen}
          onClose={handleCloseScore}
          matchId={selectedScoreMatch.id}
          opponentName={selectedScoreMatch.opponent}
          matchDateRange={selectedScoreMatch.dateDisplay || selectedScoreMatch.date}
          onSubmitScore={handleScoreSubmission}
          teamMembers={members}
          opponentMembers={opponentMembers}
          loadingOpponentMembers={loadingOpponentMembers}
          modalState={selectedScoreMatch.modalState || 'submit'}
          existingScores={selectedScoreMatch.existingScores}
          onVerifyScore={selectedScoreMatch.onVerifyScore}
          onDisputeScore={selectedScoreMatch.onDisputeScore}
          onResubmitScore={selectedScoreMatch.onResubmitScore}
          isOpponentView={selectedScoreMatch.isOpponentView || false}
        />
      )}

      {/* Match Details Modal */}
      {selectedDetailsMatch && (
        <MatchDetailsModal 
          key={`${selectedDetailsMatch.id}-${selectedDetailsMatch.courtDetails?.details_submitted_at || 'new'}`}
          isOpen={matchDetailsModalOpen}
          onClose={handleCloseMatchDetails}
          match={selectedDetailsMatch}
          onSubmit={handleMatchDetailsSubmission}
          loading={matchDetailsLoading}
        />
      )}

      {/* Dispute Score Modal — player JWT has no is_active (that flag is team-only) */}
      {selectedDisputeMatch && (user?.type === 'player' || user?.is_active) && (
        <DisputeScoreModal
          isOpen={disputeModalOpen}
          onClose={handleCloseDisputeModal}
          match={selectedDisputeMatch}
          onSubmit={handleDisputeSubmission}
          loading={disputeLoading}
        />
      )}

      {/* Resubmit Score Modal */}
      {selectedResubmitMatch && (
        <MatchScoreModal 
          isOpen={resubmitModalOpen}
          onClose={handleCloseResubmitModal}
          matchId={selectedResubmitMatch.id}
          opponentName={selectedResubmitMatch.opponent}
          matchDateRange={selectedResubmitMatch.dateDisplay || selectedResubmitMatch.date}
          onSubmitScore={handleScoreSubmission}
          teamMembers={members}
          opponentMembers={opponentMembers}
          loadingOpponentMembers={loadingOpponentMembers}
          modalState="submit"
          existingScores={null}
          onVerifyScore={null}
          onDisputeScore={null}
          onResubmitScore={null}
          isOpponentView={false}
        />
      )}
      
      {opponentSheetOpen && opponentSheetMatch && effectiveTeamId && (
        <OpponentTeamSheetModal
          isOpen={opponentSheetOpen}
          onClose={closeOpponentTeamSheet}
          matchId={opponentSheetMatch.id}
          teamId={effectiveTeamId}
          matchForChat={opponentSheetMatch}
          onOpenChat={handleOpenChat}
        />
      )}

      {/* Phone Number Modal */}
      <PhoneNumberModal
        isOpen={phoneNumberStatus.showPhoneModal && phoneNumberStatus.viewerIsCaptain}
        onClose={phoneNumberStatus.onClose}
        onSuccess={phoneNumberStatus.onSuccess}
        teamId={dashboard?.team?.id}
      />

      {/* Team Member Info Modal */}
      <TeamMemberInfoModal
        isOpen={showTeamMemberInfoModal}
        onClose={handleSkipTeamMemberInfo}
        onGoToTeamMembers={handleGoToTeamMembers}
      />
      {invitePlayerModalOpen && effectiveTeamId ? (
        <InvitePlayerModal
          teamId={effectiveTeamId}
          teamName={dashboard?.teamInfo?.name || dashboard?.team?.name}
          onClose={() => setInvitePlayerModalOpen(false)}
          onSuccess={() => {
            queryClient.invalidateQueries(['members', effectiveTeamId]);
            queryClient.invalidateQueries(['dashboard']);
          }}
        />
      ) : null}
      </DashboardContainer>
    </ScrollMemoryWrapper>
  );
};

export default DashboardPage;

